import { Component, inject, OnInit, OnDestroy, HostListener, ViewChild, ElementRef, NgZone, ChangeDetectorRef } from '@angular/core';
import moment, { Moment } from 'moment';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { RAWBequestType, RAWBequestSubType, RAWBequest } from '../Models/RAWBequest';
import { RAWProbate } from '../Models/RAWProbate';
import { firstValueFrom, forkJoin, lastValueFrom, of, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, catchError } from 'rxjs/operators';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { MatAccordion } from '@angular/material/expansion';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { TabDisplay } from '../Models/TabDisplay';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { PdfService } from '../services/pdf.service';
import { Note } from '../Models/Note';
import { Beneficiary } from '../Models/Beneficiary';
import { Probate } from '../Models/Probate';
import { Bequest, BequestType } from '../Models/Bequest';
import { BequestTypeOption } from '../Models/BequestTypeOption';
import { PreconditionNode } from '../Models/Precondition';
import { CharityCategory } from '../Models/CharityCategory';
import { BequestsNotesTemplate } from '../Models/BequestsNotesTemplate';
import { NgForm } from '@angular/forms';
import { NavigationGuardService } from '../services/navigation-guard.service';
import { FeedbackTrackingService } from '../services/feedback-tracking.service';
import { FeedbackReviewDialogComponent } from '../feedback-review-dialog/feedback-review-dialog.component';
import { FeedbackItem } from '../Models/FeedbackItem';

// Interface for selected discretionary category (stores ID and display info)
interface SelectedDiscretionaryCategory {
  id: number;
  category: string;    // Parent category
  activity: string;    // Child activity
}

@Component({
  selector: 'app-reporting',
  templateUrl: './reporting.component.html',
  styleUrls: ['./reporting.component.css'],
  standalone: false
})


export class ReportingComponent implements OnInit, OnDestroy {
  route = inject(ActivatedRoute);
  router = inject(Router);

  BequestType = RAWBequestType;
  enumBequestTypes = Object.keys(RAWBequestType).filter(k => isNaN(Number(k)));


  private loggedInUserTag = "";
  private loggedInUserDisplayNameForNote = "";
  private loggedInUserId = 0;
  private loggedInUserRoles: any[] = [];
  private loggedInUser: any = null;

  // Self Verify - lets a reporter with the SelfVerify role report AND verify a probate in one
  // step, skipping the normal supervisor assignment. Threshold is a server-side app setting
  // (SelfVerifyBequestThreshold in appsettings.json) fetched once on load.
  selfVerifyChecked = false;
  private selfVerifyBequestThreshold = 3;

  incomingProbateId: string = '';
  probate!: Probate;

  // Current will file name filter (null means show all bequests)
  currentWillFileName: string | null = null;

  // Getter for charitable bequests - filters directly from probate.bequests
  get charitableRawBequests(): Bequest[] {
    if (!this.probate?.bequests) return [];
    let filtered = this.probate.bequests.filter(b => b.isCharitable === true);
    if (this.currentWillFileName) {
      filtered = filtered.filter(b => b.fileName === this.currentWillFileName);
    }
    return filtered;
  }

  // Getter for non-charitable bequests - filters directly from probate.bequests
  get nonCharitableRawBequests(): Bequest[] {
    if (!this.probate?.bequests) return [];
    let filtered = this.probate.bequests.filter(b => b.isCharitable === false);
    if (this.currentWillFileName) {
      filtered = filtered.filter(b => b.fileName === this.currentWillFileName);
    }
    return filtered;
  }

  // Method to populate bequest UI-friendly aliases from probate data
  // This maps core properties to UI-friendly aliases for backward compatibility
  populateBequestArrays() {
    if (this.probate?.bequests) {
      // Clear existing assignments
      this.standalonePreconditions = [];
      this.bequestPreconditionAssignments = {};
      const preconditionsMap = new Map<string, { precondition: any, parentKey: string | null, willFileName?: string }>();
      
      // Map each bequest in-place to include UI-friendly aliases
      this.probate.bequests.forEach(bequest => {
        const bequestTypeCode = this.mapBequestTypeEnumToCode(bequest.bequestType);
        const valueType = this.getBequestValueType(bequestTypeCode);

        // Add UI-friendly aliases directly to the bequest object
        bequest.name = bequest.beneficiaryNameOnWill;
        bequest.conditionText = bequest.precondition?.text || '';
        bequest.commonBequestText = bequest.associatedTextOnWill;
        bequest.bequestText = bequest.associatedTextOnWill;
        bequest.bequestSubType = bequestTypeCode;
        bequest.charityCommissionNumber = bequest.beneficiaryURNOnWill;
        bequest.fileName = bequest.will?.fileName || '';
        // Restore notificationType from serviceTypeId if set; otherwise default to 'named'
        if (bequest.serviceTypeId && this.availableServices.length > 0) {
          const service = this.availableServices.find(s => s.id === bequest.serviceTypeId);
          bequest.notificationType = service ? service.name.toLowerCase() : (bequest.notificationType || 'named');
        } else {
          bequest.notificationType = bequest.notificationType || 'named';
        }
        
        // Restore selected category state from loaded charityCategoryId.
        // Parent/child names are resolved locally via allCharityCategories so that
        // named/trust restore works without the server having to send the strings.
        if (bequest.charityCategoryId && bequest.charityCategoryId.length > 0) {
          if (bequest.notificationType === 'discretionary') {
            bequest.selectedDiscretionaryActivities = bequest.charityCategoryId
              .map(id => this.allCharityCategories.find(c => c.id === id))
              .filter((c): c is CharityCategory => c != null)
              .map(c => ({ id: c.id, category: c.parent, activity: c.child }));
          } else {
            const firstId = bequest.charityCategoryId[0];
            const match = this.allCharityCategories.find(c => c.id === firstId);
            if (match) {
              const activity = { id: match.id, category: match.parent, activity: match.child };
              if (bequest.notificationType === 'trust') {
                bequest.selectedTrustActivity = activity;
              } else {
                bequest.selectedNamedActivity = activity;
              }
            }
          }
        }
        
        // Map amount values based on bequest type to avoid showing both £ and %
        bequest.cashAmount = valueType === 'pecuniary' && bequest.cashAmount != null ? bequest.cashAmount : null;
        bequest.cashAmountFormatted = this.formatNumberWithCommas(bequest.cashAmount);
        bequest.percentageAmount = valueType === 'residual' && bequest.percentageAmount != null ? bequest.percentageAmount : null;
        bequest.effectDescription = valueType === 'effect' ? bequest.effectDescription : null;
        
        // Initialize bequest precondition assignments for drag-and-drop system
        if (bequest.conditionText && bequest.conditionText.trim().length > 0 && bequest.isCharitable) {
          this.bequestPreconditionAssignments[bequest.id] = bequest.conditionText;
          
          // Track unique preconditions with their parent info
          if (!preconditionsMap.has(bequest.conditionText)) {
            const parentKey = bequest.precondition?.parentPrecondition?.text || null;
            preconditionsMap.set(bequest.conditionText, {
              precondition: bequest.precondition,
              parentKey,
              willFileName: bequest.will?.fileName || bequest.fileName || undefined
            });
          }
        }
      });
      
      // Build tree structure from flat preconditions with parent references
      this.standalonePreconditions = this.buildPreconditionTree(preconditionsMap);
    }
  }

  // Build a tree structure from preconditions with parent references
  private buildPreconditionTree(preconditionsMap: Map<string, { precondition: any, parentKey: string | null, willFileName?: string }>): PreconditionNode[] {
    const nodeMap = new Map<string, PreconditionNode>();
    const rootNodes: PreconditionNode[] = [];

    // First pass: create all nodes
    preconditionsMap.forEach((info, key) => {
      nodeMap.set(key, {
        key: key,
        precondition: info.precondition,
        children: [],
        willFileName: info.willFileName
      });
    });

    // Second pass: build parent-child relationships
    preconditionsMap.forEach((info, key) => {
      const node = nodeMap.get(key)!;
      if (info.parentKey && nodeMap.has(info.parentKey)) {
        // This node has a parent - add it as a child
        nodeMap.get(info.parentKey)!.children.push(node);
      } else {
        // This is a root node
        rootNodes.push(node);
      }
    });

    return rootNodes;
  }

  // Map backend BequestType enum to frontend BequestTypeCode for dropdown
  mapBequestTypeEnumToCode(bequestType: any): string {
    // Backend enum: 0=AbsoluteEffect, 1=AbsolutePecuniary, 2=AbsoluteResidual, 
    // 3=ConditionalEffect, 4=ConditionalPecuniary, 5=ConditionalResidual,
    // 6=HADEffect, 7=HADPecuniary, 8=HADResidual,
    // 9=ProceedsOfSaleEffect, 10=ProceedsOfSalePecuniary, 11=ProceedsOfSaleResidual
    // Keyed by DB BequestType.Id. Note id 12 ("Proceeds of Sale HAD") has the same
    // code 'RHAD' as id 9 in the DB; we disambiguate client-side as 'POSHAD' (see fetchBequestTypes).
    const enumToCodeMap: { [key: number]: string } = {
      1: 'AE',     // Absolute Effects
      2: 'AP',     // Absolute Pecuniary
      3: 'AR',     // Absolute Residual
      4: 'CE',     // Conditional Effects
      5: 'CP2',    // Conditional Pecuniary
      6: 'CR',     // Conditional Residual
      7: 'EHAD',   // Effects HAD
      8: 'PHAD',   // Pecuniary HAD
      9: 'RHAD',   // Residual HAD
      10: 'APOS',  // Absolute Proceeds of Sale
      11: 'CPOS',  // Conditional Proceeds of Sale
      12: 'POSHAD',// Proceeds of Sale HAD (DB code 'RHAD' renamed client-side)
      13: 'WTRU'   // Will Trust
    };

    // If it's already a string code, return as-is
    if (typeof bequestType === 'string') {
      return bequestType;
    }

    // If it's a number, map to code
    if (typeof bequestType === 'number' && bequestType in enumToCodeMap) {
      return enumToCodeMap[bequestType];
    }

    return '';
  }

  // Map frontend BequestTypeCode back to backend BequestType enum (reverse mapping)
  mapBequestTypeCodeToEnum(bequestTypeCode: string): number | null {
    const codeToEnumMap: { [key: string]: number } = {
      'AE': 1,      // Absolute Effects
      'AP': 2,      // Absolute Pecuniary
      'AR': 3,      // Absolute Residual
      'CE': 4,      // Conditional Effects
      'CP2': 5,     // Conditional Pecuniary
      'CR': 6,      // Conditional Residual
      'EHAD': 7,    // Effects HAD
      'PHAD': 8,    // Pecuniary HAD
      'RHAD': 9,    // Residual HAD
      'APOS': 10,   // Absolute Proceeds of Sale
      'CPOS': 11,   // Conditional Proceeds of Sale
      'POSHAD': 12, // Proceeds of Sale HAD (renamed from DB 'RHAD' duplicate)
      'WTRU': 13    // Will Trust
    };

    const upperCode = bequestTypeCode?.toUpperCase();
    return upperCode in codeToEnumMap ? codeToEnumMap[upperCode] : null;
  }

  // Filter bequests by will file name (or show all if no filter)
  // The actual filtering is done by the getters which use currentWillFileName
  filterBequestsByWill(filterFileName?: string) {
    this.currentWillFileName = filterFileName || null;
    // The getters charitableRawBequests and nonCharitableRawBequests
    // will automatically filter based on currentWillFileName

    // Update missingPage checkbox to reflect the selected will's Incomplete value
    if (this.probate?.wills) {
      const selectedWill = this.currentWillFileName
        ? this.probate.wills.find(w => w.fileName === this.currentWillFileName)
        : (this.probate.wills.find(w => w.final) || this.probate.wills[0]);
      this.editableWill.missingPage = !!(selectedWill?.incomplete);
    }
  }

  // Bequest type options from database (sorted by BequestTypeSortOrder)
  bequestTypeOptions: BequestTypeOption[] = [];

  probateTabsToDisplay: TabDisplay[] = [];
  willTabsToDisplay: TabDisplay[] = [];

  activeTab!: TabDisplay;

  selectedGender: string = '';

  // Will-specific editable fields
  editableWill: any = {
    dateOfWill: '',
    solicitor: '',
    missingPage: false
  };

  // Codicil dates array for UI
  codicilDates: string[] = [];
  selectedCodicilIndex: number = 0;
  showCodicilPopup: boolean = false;
  newCodicilDate: string = '';
  newCodicilMoment: Moment | null = null;
  newCodicilInputText: string = '';

  // Single codicil date getter/setter for the input box (when 0 or 1 dates)
  get singleCodicilDate(): string {
    return this.codicilDates.length > 0 ? this.codicilDates[0] : '';
  }
  set singleCodicilDate(value: string) {
    if (this.codicilDates.length === 0) {
      if (value) this.codicilDates.push(value);
    } else {
      this.codicilDates[0] = value;
    }
  }

  // Memoize the Moment so matDatepicker doesn't overwrite the input while the user is typing.
  private _cachedSingleMomentSource: string = '';
  private _cachedSingleMoment: Moment | null = null;
  getSingleCodicilMoment(): Moment | null {
    const str = this.codicilDates.length > 0 ? this.codicilDates[0] : '';
    if (str === this._cachedSingleMomentSource) {
      return this._cachedSingleMoment;
    }
    this._cachedSingleMomentSource = str;
    if (!str) {
      this._cachedSingleMoment = null;
    } else {
      const m = moment(str, 'DD/MM/YYYY', true);
      this._cachedSingleMoment = m.isValid() ? m : null;
    }
    return this._cachedSingleMoment;
  }

  onSingleCodicilDateChange(value: Moment | null) {
    const dateStr = value ? value.format('DD/MM/YYYY') : '';
    if (this.codicilDates.length === 0) {
      if (dateStr) this.codicilDates.push(dateStr);
    } else {
      this.codicilDates[0] = dateStr;
    }
  }

  // Block any character other than digits and forward-slash.
  onDateInputKeyPress(event: KeyboardEvent) {
    if (event.key.length > 1) return; // Allow Tab/Enter/Backspace/etc.
    if (!/^[0-9/]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  // Enforce DD/MM/YYYY shape while typing: strip non-digits, clamp per-position digit
  // ranges so DD stays 01-31 and MM stays 01-12, auto-insert slashes after DD and MM.
  formatDateInput(raw: string): string {
    const digits = (raw || '').replace(/\D/g, '').slice(0, 8);
    let out = '';
    // Day (positions 0-1)
    if (digits.length >= 1) {
      let d1 = parseInt(digits[0], 10);
      if (d1 > 3) d1 = 3;
      out += d1.toString();
    }
    if (digits.length >= 2) {
      let d2 = parseInt(digits[1], 10);
      const d1 = parseInt(out[0], 10);
      if (d1 === 3 && d2 > 1) d2 = 1;            // max day 31
      if (d1 === 0 && d2 === 0) d2 = 1;          // no 00
      out += d2.toString();
    }
    // Month (positions 2-3 after the slash)
    if (digits.length >= 3) {
      out += '/';
      let m1 = parseInt(digits[2], 10);
      if (m1 > 1) m1 = 1;
      out += m1.toString();
    }
    if (digits.length >= 4) {
      let m2 = parseInt(digits[3], 10);
      const m1 = parseInt(out[3], 10);
      if (m1 === 1 && m2 > 2) m2 = 2;            // max month 12
      if (m1 === 0 && m2 === 0) m2 = 1;          // no 00
      out += m2.toString();
    }
    // Year (positions 4-7 after the second slash)
    if (digits.length >= 5) {
      out += '/';
      out += digits.slice(4);
    }
    return out;
  }

  onSingleCodicilTextInput(event: Event) {
    const inputEl = event.target as HTMLInputElement;
    const before = inputEl.value;
    const caretBefore = inputEl.selectionStart || 0;
    const formatted = this.formatDateInput(before);
    if (before !== formatted) {
      inputEl.value = formatted;
      const newCaret = Math.min(formatted.length, caretBefore + (formatted.length - before.length));
      try { inputEl.setSelectionRange(newCaret, newCaret); } catch { /* ignore */ }
    }
  }

  onSingleCodicilTextBlur(event: Event) {
    const inputEl = event.target as HTMLInputElement;
    const text = (inputEl.value || '').trim();
    if (!text) {
      // Empty input clears the (single) codicil date
      if (this.codicilDates.length > 0) {
        this.codicilDates.splice(0, 1);
      }
      return;
    }
    const m = moment(text, 'DD/MM/YYYY', true);
    if (m.isValid()) {
      const dateStr = m.format('DD/MM/YYYY');
      if (this.codicilDates.length === 0) {
        this.codicilDates.push(dateStr);
      } else {
        this.codicilDates[0] = dateStr;
      }
      inputEl.value = dateStr;
    } else {
      // Invalid — revert to last committed value
      inputEl.value = this.codicilDates.length > 0 ? this.codicilDates[0] : '';
    }
  }

  onNewCodicilTextInput(event: Event) {
    const inputEl = event.target as HTMLInputElement;
    const before = inputEl.value;
    const caretBefore = inputEl.selectionStart || 0;
    const formatted = this.formatDateInput(before);
    if (before !== formatted) {
      inputEl.value = formatted;
      const newCaret = Math.min(formatted.length, caretBefore + (formatted.length - before.length));
      try { inputEl.setSelectionRange(newCaret, newCaret); } catch { /* ignore */ }
    }
    this.newCodicilInputText = formatted;
    const m = moment(formatted, 'DD/MM/YYYY', true);
    this.newCodicilMoment = m.isValid() ? m : null;
  }

  onNewCodicilTextBlur(event: Event) {
    const inputEl = event.target as HTMLInputElement;
    const text = (inputEl.value || '').trim();
    if (!text) {
      this.newCodicilInputText = '';
      this.newCodicilMoment = null;
      return;
    }
    const m = moment(text, 'DD/MM/YYYY', true);
    if (m.isValid()) {
      this.newCodicilInputText = m.format('DD/MM/YYYY');
      this.newCodicilMoment = m;
      inputEl.value = this.newCodicilInputText;
    } else {
      inputEl.value = this.newCodicilInputText;
      // Keep newCodicilMoment as-is (null if not previously valid)
    }
  }

  benhamClauseText: string = '';

  // Notes for bequests sections
  charitableBequestNotes: string = '';

  // Generic save prompt dialog
  showSavePrompt = false;
  savePromptProceedFn: (() => void) | null = null;
  private navGuardSub?: Subscription;

  // Footer popup
  isFooterPopupOpen: boolean = false;
  allFooterOptions: { id: number; footer: string; selected: boolean }[] = [];
  currentFooterTarget: 'charitable' | 'nonCharitable' = 'charitable';
  nonCharitableBequestNotes: string = '';

  // Editable probate object for form binding
  editableProbate: any = {
    caseReferenceNumber: '',
    dateOfIssue: '',
    title: '',
    afterTitle: '',
    fullNameOfDeceased: '',
    allNamesWhichTheDeceasedWasOtherwiseKnownAs: '',
    typeOfGrant: '',
    dateOfBirth: '',
    dateOfDeath: '',
    deceasedAddress: '',
    deceasedTown: '',
    deceasedCounty: '',
    deceasedPostcode: '',
    deceasedCountry: '',
    gross: '',
    net: '',
    grossFormatted: '',
    netFormatted: '',
    solicitorName: '',
    solicitorReference: '',
    solicitorAddress: '',
    solicitorTown: '',
    solicitorCounty: '',
    solicitorPostcode: '',
    solicitorCountry: ''
  };

  relationToRelative: string = '';
  relativePersonName: string = '';

  // Executors array for dynamic management
  executors: any[] = [];

  // Alternative names array for "Also Known As"
  alternativeNames: any[] = [];

  // Track which alias indices are selected for removal
  selectedAliasIndices: Set<number> = new Set();

  // Track selection for the first "Also Known As" field
  firstAlsoKnownAsSelected: boolean = false;

  // Former addresses - stored in probate.addresses with rank > 0
  // Track if former addresses are grouped into bars (multiple former addresses)
  formerAddressesGrouped: boolean = false;

  // Getter for former addresses - filters probate.addresses to exclude rank 0 (deceased address)
  get formerAddresses(): any[] {
    return this.probate?.addresses?.filter(a => a.rank > 0) || [];
  }

  // Getter for the first/default former address (shown in flat view)
  get defaultFormerAddress(): any {
    const formerAddrs = this.formerAddresses;
    if (formerAddrs.length === 0) {
      // No former address yet - will be created when user types
      return this._pendingFormerAddress;
    }
    return formerAddrs[0];
  }

  // Pending former address - used before adding to probate.addresses
  _pendingFormerAddress: any = {
    address: '',
    town: '',
    county: '',
    postcode: '',
    country: '',
    isFinal: true
  };

  sanitizedPdfUrl: SafeResourceUrl = '';
  lastPdfUrl: string = '';
  isLayoutExpanded: boolean = false;
  isCaseActionsOpen: boolean = false;
  selectedCaseAction: string = 'Case Actions';
  // Left-side content tab, independent of the PDF (activeTab) shown on the right.
  activeLeftTab: 'general' | 'charitable' = 'general';
  selectedWillIndex: number | string = '';
  finalWillFileName: string = '';  // Track which will is marked as final version

  // Will PDF Upload properties
  willUploadPending: boolean = false;
  willUploadFile: File | null = null;
  willUploadDate: string = '';
  willUploadFinal: boolean = false;
  willUploadInProgress: boolean = false;

  // GOP PDF Upload properties
  gopUploadPending: boolean = false;
  gopUploadFile: File | null = null;
  gopUploadInProgress: boolean = false;

  // PDF Page Reorder properties
  pdfPages: { pageNumber: number, originalIndex: number }[] = [];
  currentPdfBytes: ArrayBuffer | null = null;
  currentPdfUrl: string = '';
  reorderedPdfUrl: SafeResourceUrl | null = null;
  cachedOriginalPdfUrl: SafeResourceUrl | null = null;
  displayPdfUrl: SafeResourceUrl | null = null;
  pdfViewerSrc: string = '';
  isLoadingPages: boolean = false;
  isGeneratingPreview: boolean = false;
  notificationPreviewed: boolean = false;
  selectedPageIndex: number = 0;
  hasPageOrderChanged: boolean = false;
  originalPageOrder: number[] = [];
  private previewTimeout: any = null;
  pdfViewerKey: number = 1;
  showPdfViewer: boolean = true;
  private pdfBasePageWidth = 0;
  autoFitZoomEnabled = true;

  @ViewChild('fullPageView', { static: false }) fullPageViewElement!: ElementRef;

  // PDF Search properties
  pdfSearchQuery: string = '';
  currentSearchMatch: number = 0;
  totalSearchMatches: number = 0;
  private searchTimeout: any = null;
  currentZoom: number = 100;

  accordionStates: { [key: string]: boolean } = {
    'nonCharitable1': false,
    'nonCharitable2': false,
    'charitable1': false,
    'charitable2': false
  };
  private readonly PRECONDITION_PLACEHOLDER_PREFIX = '__NEW_PRECONDITION__';
  placeholderParentMap: Record<string, string | null> = {};
  standalonePreconditions: PreconditionNode[] = []; // All preconditions across all wills (used for saving)
  bequestPreconditionAssignments: Record<number, string> = {}; // Maps bequestId -> precondition key (local assignments by user)

  // Filtered preconditions for the currently active will tab (used for rendering only)
  get activeStandalonePreconditions(): PreconditionNode[] {
    if (!this.currentWillFileName) return this.standalonePreconditions;
    return this.filterPreconditionNodesByWill(this.standalonePreconditions, this.currentWillFileName);
  }

  private filterPreconditionNodesByWill(nodes: PreconditionNode[], willFileName: string): PreconditionNode[] {
    return nodes.filter(node => {
      // Include if this node's will matches (set when node was created or built from bequests)
      if (node.willFileName && node.willFileName !== willFileName) return false;
      if (node.willFileName === willFileName) return true;
      // If no willFileName set, check if any bequest on the current will has this precondition
      return this.BequestItemsForPreCondition(node.key).length > 0
        || node.key.startsWith(this.PRECONDITION_PLACEHOLDER_PREFIX);
    });
  }

  // Drag and drop state
  isDragging: boolean = false;
  currentlyDraggingBequest: Bequest | null = null;

  // Charity Lookup properties
  isCharityLookupOpen: boolean = false;
  charitySearchQuery = {
    charityName: '',
    ccn: '',
    address: '',
    postcode: ''
  };
  beneficiarySearchQuery = {
    beneficiaryName: '',
    beneficiaryAliasName: '',
    beneficiaryURN: '',
    address: '',
    postcode: ''
  };
  currentLookupBequestId: number | null = null;
  charitySearchResults: any[] = [];
  beneficiarySearchResults: Beneficiary[] = [];
  selectedBeneficiaryForLookup: Beneficiary | null = null;
  private charityNameSearchSubject = new Subject<string>();
  private beneficiaryNameSearchSubject = new Subject<string>();

  // Lookup sort state
  lookupSortColumn: string = '';
  lookupSortDirection: 'asc' | 'desc' = 'asc';

  sortLookupBy(column: string): void {
    if (this.lookupSortColumn === column) {
      this.lookupSortDirection = this.lookupSortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.lookupSortColumn = column;
      this.lookupSortDirection = 'asc';
    }
    this.beneficiarySearchResults = [...this.beneficiarySearchResults].sort((a: any, b: any) => {
      let aVal = a[column] ?? '';
      let bVal = b[column] ?? '';
      if (typeof aVal === 'boolean') { aVal = aVal ? 'Y' : 'N'; bVal = bVal ? 'Y' : 'N'; }
      const cmp = String(aVal).localeCompare(String(bVal), undefined, { sensitivity: 'base', numeric: true });
      return this.lookupSortDirection === 'asc' ? cmp : -cmp;
    });
  }

  // Tooltip properties for beneficiary lookup
  hoveredRow: number | null = null;
  tooltipVisible: boolean[] = [];
  tooltipContent: { aliases: string[], previousNames: string[] }[] = [];
  tooltipPosition: { top: number; left: number }[] = [];

  // Wishes Popup properties
  isWishesPopupOpen: boolean = false;
  wishesPopupText: string = '';
  currentWishesBequestId: number | null = null;

  // Generic Text Popup properties (for Name on Will, Address on Will, etc.)
  isTextPopupOpen: boolean = false;
  textPopupValue: string = '';
  textPopupTitle: string = '';
  textPopupFieldType: string = '';  // 'nameOnWill', 'addressOnWill', 'organisationName', 'addressLinked', 'benhamClause', 'footer', 'precondition'
  textPopupBequestId: number | null = null;
  textPopupPreconditionKey: string | null = null;  // Used for precondition popup to track which precondition is being edited

  // Trust Popup properties
  isTrustPopupOpen: boolean = false;
  trustPopupText: string = '';
  currentTrustBequestId: number | null = null;
  currentTrustIsCharitable: boolean = true;

  // Discretionary Popup properties
  isDiscretionaryPopupOpen: boolean = false;
  discretionaryPopupText: string = '';
  currentDiscretionaryBequestId: number | null = null;
  currentDiscretionaryIsCharitable: boolean = true;

  // Discretionary Category Lookup properties
  isDiscretionaryCategoryPopupOpen: boolean = false;
  currentDiscretionaryCategoryIsCharitable: boolean = true;

  // CharityCategory data from database
  allCharityCategories: CharityCategory[] = [];
  mainActivityHeads: string[] = []; // Distinct parent category values
  selectedCategory: string = ''; // Currently selected category from dropdown
  isCategoryDropdownOpen: boolean = false; // Custom dropdown open state
  filteredSubActivities: CharityCategory[] = []; // Sub activities for selected category
  selectedSubActivities: SelectedDiscretionaryCategory[] = []; // Selected sub activities with their IDs
  
  // New single-list activities view
  activitySearchTerm: string = '';
  filteredActivitiesList: CharityCategory[] = [];

  // Named Category Lookup properties
  isNamedCategoryPopupOpen: boolean = false;
  currentNamedCategoryBequestId: number | null = null;
  currentNamedCategoryIsCharitable: boolean = true;
  namedActivitySearchTerm: string = '';
  filteredNamedActivitiesList: CharityCategory[] = [];
  selectedNamedActivityTemp: { id: number; category: string; activity: string } | null = null;

  // Trust Category Lookup properties (same structure as Named)
  isTrustCategoryPopupOpen: boolean = false;
  currentTrustCategoryBequestId: number | null = null;
  currentTrustCategoryIsCharitable: boolean = true;
  trustActivitySearchTerm: string = '';
  filteredTrustActivitiesList: CharityCategory[] = [];
  selectedTrustActivityTemp: { id: number; category: string; activity: string } | null = null;

  // Available service types loaded from /GetServiceTypes (id + name)
  availableServices: { id: number; name: string }[] = [];

  isNotesOpen = false;
  newNoteText: string = '';

  notes: Note[] = [];

  constructor(
    private http: HttpClient,
    private sanitizer: DomSanitizer,
    private pdfService: PdfService,
    private zone: NgZone,
    private cdr: ChangeDetectorRef,
    private toastr: ToastrService,
    private navGuard: NavigationGuardService,
    private dialog: MatDialog,
    public feedbackTracking: FeedbackTrackingService
  ) { }

  // ========== UTILITY METHODS ==========

  /**
   * Generate a new negative ID for unsaved items.
   * Negative IDs indicate items that haven't been saved to the server yet.
   */
  private generateNewId(existingItems: { id?: number }[]): number {
    if (existingItems.length === 0) return -1;
    const minId = Math.min(...existingItems.map(item => item.id || 0));
    return minId >= 0 ? -1 : minId - 1;
  }

  /**
   * Find a bequest by ID from probate.bequests.
   * @param searchCharitable - If true, only search charitable bequests; if false, only non-charitable
   */
  private findBequestById(bequestId: number, searchCharitable: boolean = true): Bequest | undefined {
    if (!this.probate?.bequests) return undefined;
    return this.probate.bequests.find(b => b.id === bequestId && b.isCharitable === searchCharitable);
  }

  /**
   * Find a bequest by ID, searching all bequests in probate.
   */
  private findBequestInAllArrays(bequestId: number): { bequest: Bequest | undefined, isCharitable: boolean } {
    if (!this.probate?.bequests) return { bequest: undefined, isCharitable: false };
    const bequest = this.probate.bequests.find(b => b.id === bequestId);
    return { bequest, isCharitable: bequest?.isCharitable ?? false };
  }

  /**
   * Generic handler for overlay mousedown events.
   * Closes the popup only if clicking directly on the overlay background.
   */
  handleOverlayMouseDown(event: MouseEvent, overlayClass: string, closeCallback: () => void): void {
    const target = event.target as HTMLElement;
    if (target.classList.contains(overlayClass)) {
      closeCallback();
    }
  }

  /**
   * Remove an item from an array by ID.
   */
  private removeItemById<T extends { id?: number }>(array: T[], itemId: number): boolean {
    const index = array.findIndex(item => item.id === itemId);
    if (index > -1) {
      array.splice(index, 1);
      return true;
    }
    return false;
  }

  /**
   * Factory method to create a new bequest with default values.
   * Centralizes bequest creation logic to avoid duplication.
   */
  private createNewBequest(options: {
    isCharitable: boolean;
    preCondition?: string | null;
    includeExtendedFields?: boolean;
  }): Bequest {
    const { isCharitable, preCondition, includeExtendedFields = false } = options;
    // Generate ID from all bequests in probate to ensure uniqueness
    const newId = this.generateNewId(this.probate?.bequests || []);

    const bequest: Bequest = {
      id: newId,
      probateId: this.probate?.probateId || 0,
      beneficiaryId: null,
      will: null as any,
      beneficiaryNameOnWill: '',
      beneficiaryURNOnWill: '',
      beneficiaryAddressOnWill: '',
      associatedTextOnWill: '',
      bequestType: null as any,
      cashAmount: null,
      percentageAmount: null,
      effectDescription: null,
      charityCategoryId: null,
      isCharitable,
      precondition: preCondition ? { id: 0, probateId: this.probate?.probateId || 0, text: preCondition } : null,
      name: '',
      conditionText: preCondition || '',
      bequestSubType: null as any,
      charityCommissionNumber: '',
      fileName: '',
      organisationName: '',
      notificationType: 'named',
      serviceTypeId: this.availableServices.find(s => s.name.toLowerCase() === 'named')?.id,
      isNewlyAdded: true,
      isDone: false,
      beneficiaryNameOnWillVisible: false,
      beneficiaryURNOnWillVisible: false,
      beneficiaryAddressOnWillVisible: true,
      bequestItemOnWillVisible: true,
      triggerHads: false
    };

    if (includeExtendedFields) {
      bequest.caseNumber = '';
      bequest.dateOfWill = new Date();
      bequest.ccnLinked = '';
      bequest.addressLinked = '';
      bequest.wishes = '';
    }

    return bequest;
  }

  /**
   * Generic popup open method for Trust/Discretionary type popups.
   */
  private openBequestFieldPopup(
    bequestId: number,
    isCharitable: boolean,
    fieldName: 'trustLookup' | 'discretionaryLookup',
    setCurrentId: (id: number) => void,
    setIsCharitable: (isCharitable: boolean) => void,
    setText: (text: string) => void,
    openPopup: () => void
  ): void {
    const bequest = this.findBequestById(bequestId, isCharitable);
    if (bequest) {
      setCurrentId(bequestId);
      setIsCharitable(isCharitable);
      setText((bequest as any)[fieldName] || '');
      openPopup();
    }
  }

  /**
   * Generic popup apply method for Trust/Discretionary type popups.
   */
  private applyBequestFieldPopup(
    currentBequestId: number | null,
    isCharitable: boolean,
    fieldName: 'trustLookup' | 'discretionaryLookup',
    popupText: string,
    closePopup: () => void
  ): void {
    if (currentBequestId !== null) {
      const bequest = this.findBequestById(currentBequestId, isCharitable);
      if (bequest) {
        (bequest as any)[fieldName] = popupText;
        this.markAsChanged();
      }
    }
    closePopup();
  }

  // ========== END UTILITY METHODS ==========

  @HostListener('window:resize')
  onWindowResize() {
    if (this.autoFitZoomEnabled) {
      this.calculateAutoZoom();
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent) {
    if (this.navGuard.hasUnsavedChanges) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  // Save prompt dialog handlers
  savePromptYes() {
    this.showSavePrompt = false;
    const fn = this.savePromptProceedFn;
    this.savePromptProceedFn = null;
    fn?.();
  }

  async savePromptSaveAndContinue() {
    this.showSavePrompt = false;
    const saved = await this.onSave();
    const fn = this.savePromptProceedFn;
    this.savePromptProceedFn = null;
    // If the save was aborted (e.g. the Feedback review was cancelled), don't proceed with
    // whatever action (navigation, etc.) was waiting on it - the user needs to sort out their
    // pending changes first.
    if (saved) fn?.();
  }

  savePromptCancel() {
    this.showSavePrompt = false;
    this.savePromptProceedFn = null;
  }

  ngOnInit() {

    this.extractRouteValues();

    // Register save callback and subscribe to prompt requests from the navigation guard.
    // saveCallback is typed as returning Promise<void>; onSave() now returns Promise<boolean>
    // (so callers that chain further actions after a save can tell if it was aborted), so adapt it here.
    this.navGuard.saveCallback = () => this.onSave().then(() => {});
    this.navGuardSub = this.navGuard.promptRequested$.subscribe(proceedFn => {
      this.savePromptProceedFn = proceedFn;
      this.showSavePrompt = true;
    });

    // Fetch bequest types from database
    this.fetchBequestTypes();

    // Fetch charitable activities for Discretionary Lookup
    this.fetchCharityCategories();

    // Fetch the Self Verify bequest-count threshold (app setting, configurable server-side)
    this.http.get<{ threshold: number }>('/charitycentral/GetSelfVerifyBequestThreshold').subscribe({
      next: (result) => this.selfVerifyBequestThreshold = result.threshold,
      error: (err) => console.error('Error fetching self-verify threshold:', err)
    });

    this.populateComponents(this.incomingProbateId);

    // Setup auto-search for charity name with debounce and switchMap to cancel previous requests
    // Note: removed distinctUntilChanged() to allow searching same term after popup reopens
    this.charityNameSearchSubject.pipe(
      debounceTime(500), // Wait for user to stop typing before searching
      switchMap(searchTerm => {
        const trimmedSearchTerm = searchTerm.trim();
        // Only search if 3 or more characters
        if (trimmedSearchTerm.length >= 3) {
          let parameters = new HttpParams();
          const trimmedCcn = this.charitySearchQuery.ccn?.trim() || '';
          const trimmedAddress = this.charitySearchQuery.address?.trim() || '';
          const trimmedPostcode = this.charitySearchQuery.postcode?.trim() || '';

          // Use the searchTerm directly for charity name
          parameters = parameters.set('charityName', trimmedSearchTerm);
          parameters = parameters.set('orgName', trimmedSearchTerm);
          
          if (trimmedCcn) {
            parameters = parameters.set('ccn', trimmedCcn);
          }
          if (trimmedAddress) {
            parameters = parameters.set('address', trimmedAddress);
          }
          if (trimmedPostcode) {
            parameters = parameters.set('postcode', trimmedPostcode);
          }

          return this.http.get<any[]>('/charitycentral/SearchCharityLookup', { params: parameters }).pipe(
            catchError(error => {
              console.error('Error searching charities:', error);
              return of([]);
            })
          );
        } else if (trimmedSearchTerm.length === 0) {
          // Clear results if search box is empty
          return of([]);
        }
        // For 1-2 characters, return current results (no change)
        return of(this.charitySearchResults);
      })
    ).subscribe(results => {
      this.charitySearchResults = results;
    });

    // Setup auto-search for beneficiary name with debounce and switchMap to cancel previous requests
    // Note: removed distinctUntilChanged() to allow searching same term after popup reopens
    this.beneficiaryNameSearchSubject.pipe(
      debounceTime(500), // Wait for user to stop typing before searching
      switchMap(searchTerm => {
        const trimmedSearchTerm = searchTerm.trim();
        if (trimmedSearchTerm.length >= 3) {
          // Use the searchTerm directly for name fields, and current query state for other fields
          const trimmedBeneficiaryURN = this.beneficiarySearchQuery.beneficiaryURN?.trim() || '';
          const trimmedAddress = this.beneficiarySearchQuery.address?.trim() || '';
          const trimmedPostcode = this.beneficiarySearchQuery.postcode?.trim() || '';

          const parameters = new HttpParams()
            .set('beneficiaryname', trimmedSearchTerm)
            .set('beneficiaryaliasname', trimmedSearchTerm) // Also search aliases with the typed term
            .set('beneficiaryurn', trimmedBeneficiaryURN)
            .set('address', trimmedAddress)
            .set('postcode', trimmedPostcode);

          return this.http.get<any[]>('/charitycentral/getbeneficiaries', { params: parameters }).pipe(
            catchError(error => {
              console.error('Error searching beneficiaries:', error);
              return of([]);
            })
          );
        } else if (trimmedSearchTerm.length === 0) {
          return of([]);
        }
        // For 1-2 characters, return current results (no change)
        return of(this.beneficiarySearchResults);
      })
    ).subscribe(results => {
      this.beneficiarySearchResults = results;
    });

  }

  ngOnDestroy() {
    this.navGuardSub?.unsubscribe();
    this.navGuard.clearGuard();
    // FeedbackTrackingService is a root singleton - clear it so a snapshot from this probate
    // can't leak into whichever probate is opened next.
    this.feedbackTracking.stopTracking();
  }

  // Get charitable bequests for a specific pre-condition (used for drag-drop)
  BequestItemsForPreCondition(preCondition: string | null): Bequest[] {
    return this.charitableRawBequests.filter(b => {
      const bPreCond = b.conditionText && b.conditionText.trim().length > 0 ? b.conditionText : null;
      return bPreCond === preCondition;
    });
  }

  // Get unique pre-conditions for drag-drop grouping
  get UniquePreConditions(): (string | null)[] {
    const result: (string | null)[] = [];
    const seen = new Set<string | null>();

    for (const bequest of this.charitableRawBequests) {
      const preCondition = bequest.conditionText && bequest.conditionText.trim().length > 0
        ? bequest.conditionText : null;
      if (!seen.has(preCondition)) {
        seen.add(preCondition);
        result.push(preCondition);
      }
    }
    // Add standalone preconditions (recursively collect all keys from nested structure)
    const collectKeys = (nodes: PreconditionNode[]) => {
      for (const node of nodes) {
        if (!seen.has(node.key)) {
          seen.add(node.key);
          result.push(node.key);
        }
        if (node.children && node.children.length > 0) {
          collectKeys(node.children);
        }
      }
    };
    collectKeys(this.standalonePreconditions);
    return result;
  }

  updatePreCondition(oldPreCondition: string, newPreCondition: string) {
    if (!oldPreCondition) {
      return;
    }
    if (oldPreCondition === newPreCondition) return;
    
    console.log(`[updatePreCondition] Updating from "${oldPreCondition}" to "${newPreCondition}"`);
    let updatedCount = 0;
    
    // Update all bequests with this precondition
    for (const member of this.charitableRawBequests) {
      if (member.conditionText === oldPreCondition) {
        console.log(`  - Updating bequest ${member.id}: "${member.name}"`);
        member.conditionText = newPreCondition;
        
        // Also update the precondition object itself
        if (member.precondition) {
          member.precondition.text = newPreCondition;
          console.log(`    - Updated precondition object (ID: ${member.precondition.id})`);
        } else {
          console.log(`    - No precondition object, creating one`);
          member.precondition = {
            id: undefined,
            probateId: this.probate.probateId,
            text: newPreCondition
          };
        }
        updatedCount++;
      }
    }
    
    console.log(`  - Updated ${updatedCount} bequest(s)`);
    
    // Update the placeholder parent map
    if (this.placeholderParentMap.hasOwnProperty(oldPreCondition)) {
      this.placeholderParentMap[newPreCondition] = this.placeholderParentMap[oldPreCondition];
      delete this.placeholderParentMap[oldPreCondition];
      console.log(`  - Updated placeholder parent map`);
    }
    
    // Update the standalone preconditions array (search recursively in nested structure)
    const updateNodeKey = (nodes: PreconditionNode[]): boolean => {
      for (const node of nodes) {
        if (node.key === oldPreCondition) {
          node.key = newPreCondition;
          console.log(`  - Updated standalonePreconditions node key`);
          return true;
        }
        if (node.children && node.children.length > 0) {
          if (updateNodeKey(node.children)) return true;
        }
      }
      return false;
    };
    updateNodeKey(this.standalonePreconditions);
    
    // Update the bequest precondition assignments mapping
    for (const bequestId in this.bequestPreconditionAssignments) {
      if (this.bequestPreconditionAssignments[bequestId] === oldPreCondition) {
        this.bequestPreconditionAssignments[bequestId] = newPreCondition;
      }
    }
    
    console.log(`[updatePreCondition] Complete. Bequests with precondition after update:`, 
      this.charitableRawBequests.filter(b => b.conditionText === newPreCondition).map(b => ({
        id: b.id,
        name: b.name,
        conditionText: b.conditionText,
        preconditionId: b.precondition?.id,
        preconditionText: b.precondition?.text
      }))
    );
  }

  extractRouteValues() {

    var probateIdAsString = this.route.snapshot.paramMap.get('id');

    // Case reference numbers can exceed Number.MAX_SAFE_INTEGER, so this is kept as the exact
    // route-param string rather than converted with Number(), which would silently round it.
    this.incomingProbateId = probateIdAsString ?? '';

    var loggedInUser = this.router.lastSuccessfulNavigation?.extras?.state?.['user'];
    this.loggedInUser = loggedInUser;
    this.loggedInUserId = loggedInUser?.id ?? 0;
    this.loggedInUserTag = loggedInUser ? `${loggedInUser.lastName}, ${loggedInUser.firstName}` : 'System';
    this.loggedInUserDisplayNameForNote = loggedInUser ? `${loggedInUser.firstName} ${loggedInUser.lastName}` : 'System';
    this.loggedInUserRoles = loggedInUser?.roles ?? [];

  }

  navigateToFormerGrantProbate() {
    if (!this.probate?.formerGrantProbateId) {
      return;
    }

    this.router.navigate(['/reporting', this.probate.formerGrantProbateId], {
      state: { user: this.loggedInUser }
    });
  }

  async populateComponents(probateId: string) {

    forkJoin([
      this.getProbate(probateId),
      this.getNotes(probateId),
      this.http.get<{ id: number; name: string }[]>('/charitycentral/GetServiceTypes'),
      this.http.get<CharityCategory[]>('/charitycentral/getcharitycategories')
    ]).subscribe(results => {


      this.probate = results[0];
      this.availableServices = results[2] || [];
      this.allCharityCategories = results[3] || [];

      // FeedbackTrackingService is a root singleton - a fresh probate load must never inherit
      // a snapshot/tracking state left over from whichever probate was open before (e.g. if
      // Angular reuses this component instance across a route param change).
      this.feedbackTracking.stopTracking();

      // Default the Preview Notification dropdown to "Named" when it is among the available services.
      // Use the same object reference that the previewServiceTypes getter returns so the native
      // <select> recognises the option as selected (Angular's [ngValue] uses reference equality).
      const namedService = this.availableServices.find(s => s.name.toLowerCase() === 'named');
      if (namedService) {
        this.selectedPreviewServiceType = namedService;
      }

      // Populate bequest arrays from probate data
      this.populateBequestArrays();

      // Populate editable probate for form binding
      this.populateEditableProbate();

      var willFileURLS = this.getWillFilesURLS();
      this.probateTabsToDisplay = this.getProbateFileURLS();

      this.willTabsToDisplay = this.processWillTabs(willFileURLS);

      this.notes = results[1];

      // Load finalWillFileName from saved data (the will marked as final in the DB)
      const savedFinalWill = this.probate?.wills?.find(w => w.final);
      if (savedFinalWill) {
        this.finalWillFileName = savedFinalWill.fileName;
      } else if (this.willTabsToDisplay.length === 1) {
        // Auto-mark single will as final by default
        this.finalWillFileName = this.willTabsToDisplay[0].fileName;
      } else {
        this.finalWillFileName = '';
      }

      // Auto-select will if there's only one
      if (this.willTabsToDisplay.length === 1) {
        this.selectedWillIndex = this.willTabsToDisplay[0].index;
        // Set the single will tab as active by default
        this.setActiveTab(this.willTabsToDisplay[0]);
      } else {
        // Set default tab: start with Grant of Probate when there are multiple or no wills
        this.setActiveTab(this.probateTabsToDisplay[0]);
      }


      // Set default tab: always start with Grant of Probate
      this.setActiveTab(this.probateTabsToDisplay[0]);

      // Populate editable probate with the data
      this.populateEditableProbate();

    })
  }

  // Process will tabs using file names from RAW_Bequest table
  processWillTabs(rawWillTabs: TabDisplay[]): TabDisplay[] {
    // Get unique file names from RAW_Bequest data
    const uniqueFileNames = this.probate?.wills.map(will => will.fileName);

    if (uniqueFileNames?.length === 0) {
      // No RAW_Bequest data, return filtered raw will tabs
      return this.filterWillTabsByVersion(rawWillTabs);
    }

    // Filter will tabs to only include final versions and match with RAW_Bequest file names
    const filteredTabs = this.filterWillTabsByVersion(rawWillTabs);

    // Update display names based on RAW_Bequest file names
    return filteredTabs.map((tab, index) => {
      // Find matching RAW_Bequest file name
      const matchingFileName = this.findMatchingBequestFileName(tab.fileName, uniqueFileNames);

      return {
        ...tab,
        index: index + 1,
        displayName: matchingFileName ? this.formatWillDisplayName(matchingFileName) : tab.displayName,
        fileName: matchingFileName || tab.fileName
      };
    });
  }

  // Find RAW_Bequest file name that matches a will tab
  findMatchingBequestFileName(tabFileName: string, bequestFileNames: string[]): string | null {
    if (!tabFileName) return null;

    const tabLower = tabFileName.toLowerCase();

    for (const bequestFileName of bequestFileNames) {
      const bequestLower = bequestFileName.toLowerCase();

      // Check if they match (handle cases like "1234_scanned_will_1.pdf")
      if (tabLower.includes(bequestLower) || bequestLower.includes(tabLower)) {
        return bequestFileName;
      }

      // Check for type pattern matching
      const tabScanned = tabLower.match(/scanned_will_(\d+)/);
      const tabUploaded = tabLower.match(/uploaded_will_(\d+)/);
      const bequestScanned = bequestLower.match(/scanned_will_(\d+)/);
      const bequestUploaded = bequestLower.match(/uploaded_will_(\d+)/);

      if (tabScanned && bequestScanned && tabScanned[1] === bequestScanned[1]) {
        return bequestFileName;
      }
      if (tabUploaded && bequestUploaded && tabUploaded[1] === bequestUploaded[1]) {
        return bequestFileName;
      }
      if (tabLower.includes('admonwillgrant') && bequestLower.includes('admonwillgrant')) {
        return bequestFileName;
      }
    }

    return null;
  }

  // Filter will tabs to keep only highest version of each type
  filterWillTabsByVersion(tabs: TabDisplay[]): TabDisplay[] {
    const versionMap: Map<string, TabDisplay> = new Map();
    const admonTabs: TabDisplay[] = [];

    tabs.forEach(tab => {
      const fileName = tab.fileName.toLowerCase();

      if (fileName.includes('admonwillgrant')) {
        admonTabs.push(tab);
        return;
      }

      const scannedMatch = fileName.match(/scanned_will_(\d+)/i);
      const uploadedMatch = fileName.match(/uploaded_will_(\d+)/i);

      if (scannedMatch) {
        const version = parseInt(scannedMatch[1], 10);
        const existing = versionMap.get('scanned');
        if (!existing || version > this.extractVersion(existing.fileName, 'scanned')) {
          versionMap.set('scanned', tab);
        }
      } else if (uploadedMatch) {
        const version = parseInt(uploadedMatch[1], 10);
        const existing = versionMap.get('uploaded');
        if (!existing || version > this.extractVersion(existing.fileName, 'uploaded')) {
          versionMap.set('uploaded', tab);
        }
      } else {
        // For other file types, keep them
        versionMap.set(fileName, tab);
      }
    });

    const result: TabDisplay[] = [];
    versionMap.forEach(tab => result.push(tab));
    result.push(...admonTabs);

    return result;
  }

  // Extract version number from file name
  extractVersion(fileName: string, type: string): number {
    const pattern = type === 'scanned'
      ? /scanned_will_(\d+)/i
      : /uploaded_will_(\d+)/i;
    const match = fileName.match(pattern);
    return match ? parseInt(match[1], 10) : 0;
  }




  // Fetch bequest types from database (sorted by BequestTypeSortOrder).
  // DB has a duplicate BequestTypeCode 'RHAD' on ids 9 ("Residual HAD") and 12 ("Proceeds of Sale HAD"),
  // which makes the dropdown indistinguishable. Disambiguate id 12 to 'POSHAD' client-side so the
  // selected value (and all downstream lookups: color, value-type, save) can tell them apart.
  fetchBequestTypes() {
    this.http.get<BequestTypeOption[]>("/charitycentral/getbequesttypes")
      .subscribe({
        next: (types) => {
          this.bequestTypeOptions = (types || []).map(t => {
            if (t.id === 12 && (t.bequestTypeCode ?? '').toUpperCase() === 'RHAD') {
              return { ...t, bequestTypeCode: 'POSHAD' };
            }
            return t;
          });
        },
        error: (err) => {
          console.error('Failed to fetch bequest types:', err);
          // Fallback to empty array if fetch fails
          this.bequestTypeOptions = [];
        }
      });
  }

  // Fetch charity categories from database for Discretionary Lookup
  fetchCharityCategories() {
    this.http.get<CharityCategory[]>("/charitycentral/getcharitycategories")
      .subscribe({
        next: (categories) => {
          this.allCharityCategories = categories.sort((a, b) => {
            const parentCmp = a.parent.localeCompare(b.parent);
            return parentCmp !== 0 ? parentCmp : a.child.localeCompare(b.child);
          });
          // Extract distinct parent categories (main activity heads)
          this.mainActivityHeads = [...new Set(categories.map(c => c.parent))].sort();
        },
        error: (err) => {
          console.error('Failed to fetch charity categories:', err);
          this.allCharityCategories = [];
          this.mainActivityHeads = [];
        }
      });
  }

  async getRAWProbate(probateId: number) {
    let parameters = new HttpParams()
      .set('user', this.loggedInUserTag)
      .set('probateId', probateId);

    var response = this.http.get<RAWProbate>("/charitycentral/getrawprobate", { params: parameters });
    return await lastValueFrom(response);

  }

  async getRAWBequests(probateId: number) {


    let parameters = new HttpParams()
      .set('user', this.loggedInUserTag)
      .set('probateId', probateId);

    var response = this.http.get<RAWBequest[]>("/charitycentral/getrawbequests", { params: parameters });

    return await lastValueFrom(response);

  }

  async getProbate(probateId: string | number) {
    let parameters = new HttpParams()
      .set('user', this.loggedInUserTag)
      .set('probateId', probateId);

    var response = this.http.get<Probate>("/charitycentral/getprobate", { params: parameters });
    return await lastValueFrom(response);

  }

  async getBequests(probateId: number) {


    let parameters = new HttpParams()
      .set('user', this.loggedInUserTag)
      .set('probateId', probateId);

    var response = this.http.get<RAWBequest[]>("/charitycentral/getbequests", { params: parameters });

    return await lastValueFrom(response);

  }

  async setActiveTab(selectedTab: TabDisplay) {
    if (this.activeTab === selectedTab) {
      return;
    }

    // Prevent loading if already loading
    if (this.isLoadingPages) {
      return;
    }

    // Auto-save when switching between Grant of Probate and Will areas (both directions)
    const isMovingFromProbateToWill = this.activeTab?.type === 'probate' && selectedTab.type === 'will';
    const isMovingFromWillToProbate = this.activeTab?.type === 'will' && selectedTab.type === 'probate';

    if (isMovingFromProbateToWill || isMovingFromWillToProbate) {
      // Auto-save (snackbar is shown inside onSave). Must never interrupt with the Feedback
      // review dialog - that only happens on a deliberate Save.
      this.onSave(false);
    }

    this.activeTab = selectedTab;
    this.sanitizedPdfUrl = selectedTab.safeResourceURL;

    // Sync the dropdown selection if it's a will tab
    if (selectedTab.type === 'will') {
      this.selectedWillIndex = selectedTab.index;
      // Filter bequest data to show only data for the selected will
      this.filterBequestsByWill(selectedTab.fileName);
    } else {
      // Reset the will dropdown so re-selecting the same will fires change events
      this.selectedWillIndex = '';
      // Keep the currently-selected will's bequests visible even on Grant of Probate.
      // If no will has been selected yet, default to the Final will (or the first will),
      // so the list always reflects a single will rather than all wills combined.
      if (!this.currentWillFileName && this.probate?.wills?.length) {
        const defaultWill = this.probate.wills.find(w => w.final) || this.probate.wills[0];
        this.filterBequestsByWill(defaultWill?.fileName);
      }
    }

    // Load pages for the new PDF
    await this.loadPdfPages();
  }

  async loadPdfPages() {
    try {
      this.autoFitZoomEnabled = true;
      this.pdfBasePageWidth = 0;
      this.currentZoom = 100;
      // Clean up any previous blob URLs
      if (this.reorderedPdfUrl) {
        const oldUrl = (this.reorderedPdfUrl as any).changingThisBreaksApplicationSecurity;
        if (oldUrl && oldUrl.startsWith('blob:')) {
          URL.revokeObjectURL(oldUrl);
        }
        this.reorderedPdfUrl = null;
      }

      this.isLoadingPages = true;
      this.currentPdfUrl = this.getCurrentPdfUrl();

      // Cache the sanitized original URL
      this.cachedOriginalPdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.currentPdfUrl);
      this.displayPdfUrl = this.cachedOriginalPdfUrl;
      this.pdfViewerSrc = this.currentPdfUrl;

      this.currentPdfBytes = await this.pdfService.extractPages(this.currentPdfUrl);
      const pageCount = await this.pdfService.getPageCount(this.currentPdfBytes);

      this.pdfPages = Array.from({ length: pageCount }, (_, i) => ({
        pageNumber: i + 1,
        originalIndex: i
      }));

      // Store original order
      this.originalPageOrder = this.pdfPages.map(p => p.originalIndex);
      this.hasPageOrderChanged = false;
      this.selectedPageIndex = 0;
      this.isLoadingPages = false;

      // Ensure viewer is visible
      if (!this.showPdfViewer) {
        this.showPdfViewer = true;
      }
    } catch (error) {
      this.isLoadingPages = false;
      this.showPdfViewer = true;
      this.pdfPages = [];
    }
  }


  getTabNameFromURL(URL: string): string {
    if (!URL) return '';

    // Remove query string if present
    let cleanUrl = URL.split('?')[0];

    // Remove trailing slashes
    cleanUrl = cleanUrl.replace(/\/+$/, '');

    // Get the last part (file name)
    const parts = cleanUrl.split('/');
    let fileName = parts[parts.length - 1];

    // If it looks like a folder path (no file extension and doesn't match will patterns), 
    // try to find a PDF file name in the URL
    if (!fileName.includes('.pdf') && !fileName.match(/scanned_will|uploaded_will|admonwillgrant/i)) {
      // Try to find a PDF file name anywhere in the URL
      const pdfMatch = URL.match(/([^\/]+\.pdf)/i);
      if (pdfMatch) {
        fileName = pdfMatch[1];
      }
    }

    // URL decode the file name
    try {
      fileName = decodeURIComponent(fileName);
    } catch (e) {
      // Keep original if decoding fails
    }

    return fileName;
  }

  getProbateFileURLS(): TabDisplay[] {

    var probateURLS = [this.probate.documentURL];

    const localProbateTabs: TabDisplay[] = [];

    var indexCounter: number = 1;

    probateURLS.forEach(probateURL => {
      const fileName = this.getTabNameFromURL(probateURL);

      localProbateTabs.push({
        index: indexCounter,
        type: "probate",
        displayName: fileName,
        fileName: fileName,
        originalURL: this.getEstateDocument(probateURL, this.probate.documentsArchived),
        safeResourceURL: this.sanitizer.bypassSecurityTrustResourceUrl(this.getEstateDocument(probateURL, this.probate.documentsArchived))
      })

      indexCounter += 1;

    });

    return localProbateTabs;
  }

  getWillFilesURLS(): TabDisplay[] {

    const localWillTabs: TabDisplay[] = [];

    var indexCounter: number = 1;

    this.probate.wills.forEach(will => {

      localWillTabs.push({
        index: indexCounter,
        type: "will",
        displayName: will.fileName,  // Will be updated in processWillTabs
        fileName: will.fileName,
        originalURL: this.getEstateDocument(will.documentURL, this.probate.documentsArchived),
        safeResourceURL: this.sanitizer.bypassSecurityTrustResourceUrl(this.getEstateDocument(will.documentURL, this.probate.documentsArchived))

      })

      indexCounter += 1;

    });

    return localWillTabs;
  }

  // Format will file name for display - show the actual file name from RAW_Bequest
  formatWillDisplayName(fileName: string): string {
    if (!fileName) return '';

    // Return the actual file name (remove .pdf extension for cleaner display)
    return fileName.replace(/\.(pdf|PDF)$/i, '');
  }

  getFullApiUrl(endpoint: string, params: HttpParams): string {
    return `${endpoint}?${params.toString()}`;
  }

  getEstateDocument(documentUrl: string, documentsArchived: boolean) {
    return `/charitycentral/getestatedocument?documentUrl=${encodeURIComponent(documentUrl)}&documentsArchived=${documentsArchived}&user=${this.loggedInUserTag}`;
  }

  onSubmit() {
    // Add form submission logic here
  }

  /**
   * @param promptFeedbackReview When true (the default), a Save that has tracked Feedback
   * candidates pending will open the review dialog first. Pass false for non-deliberate
   * auto-saves (e.g. tab-switch) that must never interrupt the user. Returns false (without
   * saving anything) if validation fails or the feedback review is cancelled - callers that
   * chain further actions after a save (doReportWill, doVerifyWill) must check this and bail
   * out rather than assuming the save happened.
   */
  async onSave(promptFeedbackReview: boolean = true): Promise<boolean> {
    try {
      // Sync basic probate fields (grantType, deceasedName, aliases, dates, values)
      this.syncBasicProbateFields();
      // Sync deceased address from editableProbate to probate.addresses
      this.syncDeceasedAddress();
      // Sync pending former address to probate.addresses before saving
      this.syncPendingFormerAddress();
      // Sync solicitor from editableProbate to probate.solicitor
      this.syncSolicitor();
      // Sync executors UI array to probate.executors
      this.syncExecutors();
      // Sync will data (dateOfWill, codicilDate) to probate.wills
      this.syncWillData();
      // Sync preconditions hierarchy (must be before syncBequests so server processes them first)
      this.syncPreconditions();
      // Sync bequest UI aliases back to actual properties
      this.syncBequests();

      // Validate that all CHARITABLE bequests have a bequest type selected.
      // Non-charitable bequests are intentionally excluded from the save, so they must
      // not block it (they may legitimately have no bequest type).
      const invalidBequest = this.probate.bequests?.find(
        b => b.isCharitable === true && (b.bequestType === undefined || b.bequestType === null)
      );
      if (invalidBequest) {
        const displayName = invalidBequest.organisationName
          || invalidBequest.name
          || invalidBequest.beneficiaryNameOnWill
          || 'New Beneficiary';
        this.toastr.error(`Data Save Failed: "${displayName}" does not have Bequest Type selected.`, '', { timeOut: 8000 });
        return false;
      }

      console.log('[onSave] Full probate object:', this.probate);
      console.log('[onSave] Bequests with preconditions:',
        this.probate.bequests.filter(b => b.precondition).map(b => ({
          id: b.id,
          name: b.name,
          conditionText: b.conditionText,
          precondition: b.precondition
        }))
      );

      // Feedback Tracking Mode: if there are tracked changes pending, the supervisor must review
      // and categorize them before the save proceeds at all.
      let feedbackItems: FeedbackItem[] | null = null;
      if (promptFeedbackReview && this.feedbackTracking.isTracking && this.feedbackTracking.hasSnapshot()) {
        const candidates = this.feedbackTracking.computeCandidates(this.probate, (t) => this.formatBequestType(t));
        if (candidates.length > 0) {
          const ref = this.dialog.open(FeedbackReviewDialogComponent, {
            data: { candidates },
            width: '1000px',
            maxWidth: '95vw'
          });
          const result: FeedbackItem[] | null = await firstValueFrom(ref.afterClosed());
          if (!result) {
            // Cancelled - abort the whole save, nothing persisted.
            return false;
          }
          feedbackItems = result;
        }
      }

      let parameters = new HttpParams()
        .set('user', this.loggedInUserTag);
      if (this.loggedInUserId) {
        parameters = parameters.set('feedbackUserId', String(this.loggedInUserId));
      }

      // Persist charitable bequests only. Non-charitable bequests are excluded from the
      // payload so they are never updated (and the backend leaves them untouched, not deleted).
      const probateToSave = {
        ...this.probate,
        bequests: (this.probate.bequests || []).filter(b => b.isCharitable === true),
        feedbackItems: feedbackItems ?? undefined
      };
      const response = this.http.post("/charitycentral/saveprobate", probateToSave, { params: parameters });

      console.log(response);

      await lastValueFrom(response);

      // Show success snackbar
      this.toastr.success('Data is Saved', '', { timeOut: 3000 });
      // Reset unsaved changes flag after successful save
      this.hasUnsavedChanges = false;
      this.navGuard.hasUnsavedChanges = false;

      // Re-baseline tracking to the just-saved state so already-submitted corrections don't
      // reappear if the supervisor keeps correcting in the same session.
      if (this.feedbackTracking.isTracking) {
        this.feedbackTracking.startTracking(this.probate);
      }

      return true;
    } catch (error) {
      console.error('Error saving probate data:', error);
      // Show error snackbar
      this.toastr.error('Error saving data. Please try again.', '', { timeOut: 5000 });
      return false;
    }
  }

  // Get deceased address tooltip for Former Addresses section
  getDeceasedAddressTooltip(): string {
    const parts: string[] = [];

    if (this.editableProbate.deceasedAddress) {
      parts.push('Address: ' + this.editableProbate.deceasedAddress);
    }
    if (this.editableProbate.deceasedTown) {
      parts.push('Town: ' + this.editableProbate.deceasedTown);
    }
    if (this.editableProbate.deceasedCounty) {
      parts.push('County: ' + this.editableProbate.deceasedCounty);
    }
    if (this.editableProbate.deceasedPostcode) {
      parts.push('Postcode: ' + this.editableProbate.deceasedPostcode);
    }
    if (this.editableProbate.deceasedCountry) {
      parts.push('Country: ' + this.editableProbate.deceasedCountry);
    }

    return parts.length > 0 ? parts.join('\n') : 'No address information available';
  }

  // Get executor tooltip for Executor Relationship section
  getExecutorTooltip(): string {
    if (!this.executors || this.executors.length === 0) {
      return 'No executor information available';
    }
    return this.executors.map((executor, i) => {
      const lines: string[] = [`Executor ${i + 1}:`];
      if (executor.name) lines.push('  Name: ' + executor.name);
      if (executor.address) lines.push('  Address: ' + executor.address);
      if (executor.town) lines.push('  Town: ' + executor.town);
      if (executor.county) lines.push('  County: ' + executor.county);
      if (executor.postcode) lines.push('  Postcode: ' + executor.postcode);
      if (executor.country) lines.push('  Country: ' + executor.country);
      return lines.join('\n');
    }).join('\n\n');
  }

  // Track if there are unsaved changes
  hasUnsavedChanges = false;

  areAllCharitableBequestsSelected(): boolean {
    return this.charitableRawBequests.length > 0 &&
      this.charitableRawBequests.every(b => b.isDone === true);
  }

  toggleSelectAllCharitableBequests(checked: boolean) {
    this.charitableRawBequests.forEach(b => b.isDone = checked);
    this.markAsChanged();
  }

  hasAnyBequestSelected(): boolean {
    return this.charitableRawBequests.some(b => b.isDone === true);
  }

  get selectedCharitableBequestCount(): number {
    return this.charitableRawBequests.filter(b => b.isDone === true).length;
  }

  showDeleteBequestsConfirm = false;

  deleteSelectedBequests() {
    if (!this.probate?.bequests || !this.hasAnyBequestSelected()) return;
    this.showDeleteBequestsConfirm = true;
  }

  confirmDeleteSelectedBequests() {
    this.showDeleteBequestsConfirm = false;
    const selectedIds = new Set(this.charitableRawBequests.filter(b => b.isDone === true).map(b => b.id));
    if (selectedIds.size === 0) return;
    this.probate.bequests = this.probate.bequests.filter(b => !selectedIds.has(b.id));
    this.markAsChanged();
  }

  cancelDeleteSelectedBequests() {
    this.showDeleteBequestsConfirm = false;
  }

  markAsChanged() {
    this.hasUnsavedChanges = true;
    this.navGuard.hasUnsavedChanges = true;
  }

  getCurrentPdfUrl(): string {

    return this.activeTab.originalURL;

  }

  openInNewWindow() {
    // If pages have been reordered, open the reordered version
    if (this.reorderedPdfUrl) {
      const url = (this.reorderedPdfUrl as any).changingThisBreaksApplicationSecurity;
      window.open(url, '_blank');
    } else {
      // Open original PDF
      window.open(this.activeTab.originalURL, '_blank');
    }
  }

  onWillFileSelected(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    this.willUploadFile = file;
    this.willUploadDate = new Date().toISOString().split('T')[0];
    this.willUploadFinal = false;
    this.willUploadPending = true;
    (event.target as HTMLInputElement).value = '';
  }

  cancelWillUpload() {
    this.willUploadPending = false;
    this.willUploadFile = null;
  }

  async confirmWillUpload() {
    if (!this.willUploadFile || !this.willUploadDate) return;
    this.willUploadInProgress = true;

    const formData = new FormData();
    formData.append('file', this.willUploadFile);
    formData.append('probateId', this.probate.probateId.toString());
    formData.append('dateOfWill', new Date(this.willUploadDate).toISOString());
    formData.append('final', this.willUploadFinal.toString());
    formData.append('documentsArchived', this.probate.documentsArchived.toString());
    formData.append('user', this.loggedInUserTag);

    this.http.post('/charitycentral/uploadwillpdf', formData).subscribe({
      next: async () => {
        this.cancelWillUpload();
        this.willUploadInProgress = false;
        await this.reloadWills();
        this.toastr.success('Will PDF uploaded successfully', '', { timeOut: 3000 });
      },
      error: () => {
        this.willUploadInProgress = false;
        this.toastr.error('Upload failed', '', { timeOut: 3000 });
      }
    });
  }

  async reloadWills() {
    const refreshedProbate = await this.getProbate(this.probate.probateId);
    this.probate.wills = refreshedProbate.wills;
    const willFileURLS = this.getWillFilesURLS();
    this.willTabsToDisplay = this.processWillTabs(willFileURLS);
    const savedFinalWill = this.probate.wills?.find(w => w.final);
    if (savedFinalWill) {
      this.finalWillFileName = savedFinalWill.fileName;
    }
  }

  get hasExistingGop(): boolean {
    return !!this.probate?.documentURL;
  }

  onGopFileSelected(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    this.gopUploadFile = file;
    this.gopUploadPending = true;
    (event.target as HTMLInputElement).value = '';
  }

  cancelGopUpload() {
    this.gopUploadPending = false;
    this.gopUploadFile = null;
  }

  async confirmGopUpload() {
    if (!this.gopUploadFile) return;
    this.gopUploadInProgress = true;

    const formData = new FormData();
    formData.append('file', this.gopUploadFile);
    formData.append('probateId', this.probate.probateId.toString());
    formData.append('documentsArchived', this.probate.documentsArchived.toString());
    formData.append('user', this.loggedInUserTag);

    this.http.post('/charitycentral/uploadgoppdf', formData).subscribe({
      next: async () => {
        this.cancelGopUpload();
        this.gopUploadInProgress = false;
        const refreshedProbate = await this.getProbate(this.probate.probateId);
        this.probate.documentURL = refreshedProbate.documentURL;
        this.probate.filename = refreshedProbate.filename;
        this.probateTabsToDisplay = this.getProbateFileURLS();
        this.toastr.success('Grant of Probate PDF uploaded successfully', '', { timeOut: 3000 });
      },
      error: () => {
        this.gopUploadInProgress = false;
        this.toastr.error('Upload failed', '', { timeOut: 3000 });
      }
    });
  }

  toggleAccordion(accordionId: string) {
    this.accordionStates[accordionId] = !this.accordionStates[accordionId];
  }

  onReportWill() {
    this.navGuard.requestAction(() => this.doReportWill());
  }

  private async doReportWill() {
    // Validate that every charitable bequest is linked (beneficiary or category/activity).
    // Sync first so beneficiaryId/charityCategoryId reflect the latest dropdown state.
    this.syncBequests();
    const unlinkedCharitable = this.charitableRawBequests.find(b => {
      const hasBeneficiary = !!(b.beneficiaryId && b.beneficiaryId > 0);
      const cat: any = b.charityCategoryId;
      const hasCategory = Array.isArray(cat) ? cat.length > 0 : (cat !== null && cat !== undefined);
      return !hasBeneficiary && !hasCategory;
    });
    if (unlinkedCharitable) {
      this.toastr.error('All bequests must be linked!', '', { timeOut: 8000 });
      return;
    }

    // Self Verify: resolve whether this submission should report AND verify together, before
    // anything is saved so a decline aborts with zero side effects.
    let performSelfVerify = this.selfVerifyChecked;
    if (this.isSelfVerify()) {
      const bequestCount = this.charitableRawBequests.length;
      if (performSelfVerify && bequestCount >= this.selfVerifyBequestThreshold) {
        const proceed = await this.confirmYesNo(
          'Self Verify',
          `This Probate has ${bequestCount} bequests are you sure you wish to self verify?`
        );
        if (!proceed) return;
      } else if (!performSelfVerify && bequestCount < this.selfVerifyBequestThreshold) {
        performSelfVerify = await this.confirmYesNo(
          'Self Verify',
          `This Probate only has ${bequestCount} bequests, would you like to self verify?`
        );
      }
    } else {
      performSelfVerify = false;
    }

    // Mark the currently selected will as final
    if (this.currentWillFileName) {
      this.finalWillFileName = this.currentWillFileName;
    } else if (this.willTabsToDisplay.length === 1) {
      this.finalWillFileName = this.willTabsToDisplay[0].fileName;
    }

    if (this.finalWillFileName) {
      this.toastr.success(`Final will set to ${this.finalWillFileName}`, '', { timeOut: 3000 });
    }

    // Save everything (including the final will marking)
    const saved = await this.onSave();
    if (!saved) return;

    // Mark data complete with the logged-in user
    if (this.loggedInUserId) {
      try {
        await firstValueFrom(this.http.post(
          `/charitycentral/markdatacomplete?ProbateId=${this.probate.probateId}&UserId=${this.loggedInUserId}`,
          null
        ));
      } catch (error) {
        console.error('Error marking data complete:', error);
      }
    }

    if (performSelfVerify) {
      // Report and verify together - reuses the exact same completion steps a supervisor's
      // Verify Will performs, just with the reporter recorded as their own approver.
      await this.completeVerification();
      return;
    }

    // If reporter, check whether there are any bequests
    if (this.isReporter()) {
      const hasNoBequests = this.charitableRawBequests.length === 0;
      if (hasNoBequests) {
        // Non-charitable will: skip supervisor, mark directly as completed
        try {
          await firstValueFrom(this.http.post(
            `/charitycentral/marknoncharitablecomplete?ProbateId=${this.probate.probateId}&UserId=${this.loggedInUserId}`,
            null
          ));
          this.toastr.success('Non-charitable will completed.', '', { timeOut: 3000 });
          this.router.navigate(['/probate-list']);
        } catch (error) {
          console.error('Error marking non-charitable complete:', error);
          this.toastr.error('Failed to complete non-charitable will.', '', { timeOut: 4000 });
        }
      } else {
        // Normal flow: assign to supervisor for verification
        this.doAssignToSupervisor();
      }
    }
  }

  isSupervisor(): boolean {
    return this.loggedInUserRoles.some((role: any) => role.id === 2 || role.code === 'Supervisor');
  }

  isReporter(): boolean {
    // Only Supervisor is a senior probate-processing role - SystemAdministrator is a separate
    // concern (Pricing/Admin menu access) and must not affect the Report/Verify Will branching.
    return this.loggedInUserRoles.some((role: any) => role.id === 4 || role.code === 'Reporter') &&
      !this.loggedInUserRoles.some((role: any) => role.id === 2 || role.code === 'Supervisor');
  }

  // Not checked against a hardcoded id (unlike the roles above) since this role was added directly
  // to the database and its identity-column id isn't known/fixed in code - matched by code only.
  // Mutually exclusive with Supervisor by admin/DB convention, not enforced here - isReporter()
  // already excludes Supervisors, so the Self Verify checkbox can never show for one regardless.
  isSelfVerify(): boolean {
    return this.loggedInUserRoles.some((role: any) => role.code === 'SelfVerify');
  }

  // Promise-wrapped ConfirmDialogComponent so callers can `await` a yes/no answer inline
  // (e.g. within doReportWill's Self Verify branching) instead of nesting in a subscribe callback.
  private async confirmYesNo(title: string, message: string): Promise<boolean> {
    const ref = this.dialog.open(ConfirmDialogComponent, {
      width: '380px',
      data: { title, message, confirmText: 'Yes', cancelText: 'No' }
    });
    const result = await firstValueFrom(ref.afterClosed());
    return result === true;
  }

  get feedbackReporterName(): string {
    const user = this.probate?.reportedByUser;
    if (!user) return '';
    return [user.firstName, user.lastName].filter(n => !!n).join(' ');
  }

  toggleFeedbackTracking(): void {
    if (!this.probate?.dataCompleteUserId) return;

    if (this.feedbackTracking.isTracking) {
      const candidates = this.feedbackTracking.computeCandidates(this.probate, (t) => this.formatBequestType(t));
      if (candidates.length === 0) {
        this.feedbackTracking.stopTracking();
        return;
      }
      const ref = this.dialog.open(ConfirmDialogComponent, {
        width: '380px',
        data: {
          title: 'Discard Tracked Changes?',
          message: `You have ${candidates.length} tracked change${candidates.length !== 1 ? 's' : ''} that hasn't been reviewed yet. Turning tracking off will discard them.`,
          confirmText: 'Discard',
          cancelText: 'Keep Tracking'
        }
      });
      ref.afterClosed().subscribe(confirmed => {
        if (confirmed === true) {
          this.feedbackTracking.stopTracking();
        }
      });
    } else {
      // Several editable fields live in UI-mirror objects (editableProbate, executors, etc.) and
      // only land on `this.probate` via these sync*() calls at Save time. Run them before
      // snapshotting so any edits already sitting unsaved in those mirrors become part of the new
      // baseline instead of being misattributed as changes made "during" tracking.
      this.syncBasicProbateFields();
      this.syncDeceasedAddress();
      this.syncPendingFormerAddress();
      this.syncSolicitor();
      this.syncExecutors();
      this.syncWillData();
      this.syncPreconditions();
      this.syncBequests();
      this.feedbackTracking.startTracking(this.probate);
    }
  }

  assignToSupervisor() {
    if (!this.probate?.probateId || !this.loggedInUserId) return;
    this.navGuard.requestAction(() => this.doAssignToSupervisor());
  }

  private doAssignToSupervisor() {
    const params = new HttpParams()
      .set('ProbateId', this.probate.probateId.toString())
      .set('ReporterUserId', this.loggedInUserId.toString())
      .set('User', this.loggedInUserTag);
    this.http.post('/charitycentral/assignprobatetosupervisor', null, { params }).subscribe({
      next: () => {
        this.toastr.success('Case assigned to your supervisor.', '', { timeOut: 3000 });
        this.router.navigate(['/probate-list']);
      },
      error: (err) => this.toastr.error(
        err.status === 400 ? 'No supervisor assigned to your account.' : 'Failed to assign to supervisor.',
        '', { timeOut: 4000 }
      )
    });
  }

  onVerifyWill() {
    const ref = this.dialog.open(ConfirmDialogComponent, {
      width: '380px',
      height: '240px',
      panelClass: 'verify-will-confirm-dialog',
      data: {
        title: 'Verify Will',
        message: 'Are you sure?',
        confirmText: 'Yes',
        cancelText: 'No'
      }
    });
    ref.afterClosed().subscribe(confirmed => {
      if (confirmed === true) {
        this.navGuard.requestAction(() => this.doVerifyWill());
      }
    });
  }

  private async doVerifyWill() {
    // Mark the currently selected will as final (mirrors Report Will behavior)
    if (this.currentWillFileName) {
      this.finalWillFileName = this.currentWillFileName;
    } else if (this.willTabsToDisplay.length === 1) {
      this.finalWillFileName = this.willTabsToDisplay[0].fileName;
    }

    if (this.finalWillFileName) {
      this.toastr.success(`Final will set to ${this.finalWillFileName}`, '', { timeOut: 3000 });
    }

    // Save any pending changes (this also persists the final will marking)
    const saved = await this.onSave();
    if (!saved) return;

    await this.completeVerification();
  }

  // Shared by the supervisor "Verify Will" flow and the reporter "Self Verify" flow (doReportWill)
  // - both need the exact same "extra steps that happen after an approval": create the final
  // notification PDF, mark it generated, and end the session. The only difference between a
  // supervisor verifying and a reporter self-verifying is WHO the backend records as the
  // approver, and that's just whichever UserId is passed to /verifywill - the endpoint itself
  // doesn't distinguish, so no separate self-verify endpoint is needed.
  private async completeVerification(): Promise<void> {
    try {
      const willId = this.getSelectedWillId();
      let url = `/charitycentral/verifywill?ProbateId=${this.probate.probateId}&User=${this.loggedInUserTag}`;
      if (willId) {
        url += `&WillId=${willId}`;
      }
      if (this.loggedInUserId) {
        url += `&UserId=${this.loggedInUserId}`;
      }

      await firstValueFrom(this.http.post(url, null));

      this.toastr.success('Will verified and notification PDF saved successfully', '', { timeOut: 3000 });
      // The probate is now considered fully complete - end the Feedback Tracking Mode session
      // entirely rather than leaving it re-baselined for a probate that's about to be left behind.
      this.feedbackTracking.stopTracking();
      this.router.navigate(['/probate-list']);
    } catch (error) {
      this.toastr.error('Will verified but failed to create notification PDF', '', { timeOut: 5000 });
      console.error('Error creating notification PDF:', error);
    }
  }

  toggleLayout() {
    this.isLayoutExpanded = !this.isLayoutExpanded;
    if (this.autoFitZoomEnabled) {
      setTimeout(() => this.calculateAutoZoom(), 0);
    }
  }

  toggleCaseActions() {
    this.isCaseActionsOpen = !this.isCaseActionsOpen;
  }

  onCaseAction(action: string) {
    this.isCaseActionsOpen = false; // Close dropdown after selection

    // Update the selected action text
    switch (action) {
      case 'intestate':
        this.selectedCaseAction = 'Intestate';
        break;
      case 'non-charitable-will':
        this.selectedCaseAction = 'Non-Charitable Will';
        break;
      case 'missing-page':
        this.selectedCaseAction = 'Missing Page';
        break;
      default:
        this.selectedCaseAction = 'Case Actions';
    }
  }

  setLeftTab(tab: 'general' | 'charitable') {
    this.activeLeftTab = tab;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    const target = event.target as HTMLElement;
    const dropdown = target.closest('.case-actions-dropdown');

    // Close dropdown if click is outside the dropdown
    if (!dropdown && this.isCaseActionsOpen) {
      this.isCaseActionsOpen = false;
    }
  }

  // Helper method to format dates for HTML date inputs (yyyy-MM-dd format)
  formatDateForInput(dateString: string | undefined): string {
    if (!dateString) return '';

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');

      return `${year}-${month}-${day}`;
    } catch (error) {
      return '';
    }
  }

  // Helper method to format dates with slashes (DD/MM/YYYY format)
  formatDateWithSlashes(dateValue: Date | string | undefined | null): string {
    if (!dateValue) return '';

    try {
      const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
      if (isNaN(date.getTime())) return '';

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');

      return `${day}/${month}/${year}`;
    } catch (error) {
      return '';
    }
  }

  // Add a new executor
  addExecutor() {
    const newTrackingId = this.executors.length > 0
      ? Math.max(...this.executors.map(e => e.trackingId)) + 1
      : 1;

    this.executors.push({
      trackingId: newTrackingId,
      name: '',
      address: '',
      town: '',
      county: '',
      postcode: '',
      country: '',
      relationship: '',
      isNewlyAdded: true
    });
  }

  // Remove an executor
  removeExecutor(trackingId: number) {
    const index = this.executors.findIndex(e => e.trackingId === trackingId);
    if (index > -1) {
      this.executors.splice(index, 1);
    }
  }

  // Open the codicil date popup
  openCodicilPopup() {
    this.newCodicilDate = '';
    this.newCodicilMoment = null;
    this.newCodicilInputText = '';
    this.showCodicilPopup = true;
  }

  // Close the codicil date popup
  closeCodicilPopup() {
    this.showCodicilPopup = false;
    this.newCodicilDate = '';
    this.newCodicilMoment = null;
    this.newCodicilInputText = '';
  }

  // Confirm adding a new codicil date
  confirmAddCodicilDate() {
    // Prefer the typed text if it parses, otherwise fall back to the picker-selected Moment.
    let dateStr: string | null = null;
    if (this.newCodicilInputText) {
      const m = moment(this.newCodicilInputText, 'DD/MM/YYYY', true);
      if (m.isValid()) dateStr = m.format('DD/MM/YYYY');
    }
    if (!dateStr && this.newCodicilMoment && this.newCodicilMoment.isValid()) {
      dateStr = this.newCodicilMoment.format('DD/MM/YYYY');
    }
    if (dateStr) {
      this.codicilDates.push(dateStr);
      this.selectedCodicilIndex = this.codicilDates.length - 1;
    }
    this.closeCodicilPopup();
  }

  // Remove a codicil date
  removeCodicilDate(index: number) {
    if (index >= 0 && index < this.codicilDates.length) {
      this.codicilDates.splice(index, 1);
      if (this.selectedCodicilIndex >= this.codicilDates.length) {
        this.selectedCodicilIndex = Math.max(0, this.codicilDates.length - 1);
      }
    }
  }

  // Track codicil dates by index for ngFor
  trackCodicilDate(index: number): number {
    return index;
  }

  // Add a new charitable beneficiary
  addIndependentCharitableBequest() {
    const newBequest = this.createNewBequest({
      isCharitable: true,
      includeExtendedFields: true
    });

    // Set the fileName and will reference to match current will filter so it shows up and saves correctly
    if (this.currentWillFileName && this.probate?.wills) {
      newBequest.fileName = this.currentWillFileName;
      newBequest.will = this.probate.wills.find(w => w.fileName === this.currentWillFileName) ?? null as any;
    }

    // Ensure bequests array exists and push the new bequest
    if (this.probate) {
      if (!this.probate.bequests) {
        this.probate.bequests = [];
      }
      this.probate.bequests.push(newBequest);
    }
  }

  // Check if any charitable bequest is selected (isDone checked)
  hasSelectedBequest(): boolean {
    return this.charitableRawBequests.some(b => b.isDone === true);
  }

  // Duplicate selected charitable bequests
  duplicateSelectedBequest() {
    if (!this.probate?.bequests) return;
    
    // Find all selected bequests
    const selectedBequests = this.charitableRawBequests.filter(b => b.isDone === true);
    
    if (selectedBequests.length === 0) return;
    
    // Duplicate each selected bequest
    for (const originalBequest of selectedBequests) {
      // Find the index of the original bequest in probate.bequests
      const originalIndex = this.probate.bequests.findIndex(b => b.id === originalBequest.id);
      
      if (originalIndex === -1) continue;
      
      // Create a partial copy - only Name on Will and Linked Beneficiary fields
      const duplicatedBequest: Bequest = {
        ...originalBequest,
        id: this.generateNewBequestId(), // Generate new unique ID
        isDone: false, // Uncheck the duplicate
        
        // Clear Bequest Type/Amount - set to unselected so dropdown shows "Select Type"
        bequestType: undefined as any,
        bequestSubType: '', // Empty string = "Select Type" in dropdown
        cashAmount: null,
        percentageAmount: null,
        effectDescription: null,
        
        // Clear Associated Text
        associatedTextFromUser: '',
        
        // Clear precondition (not copied)
        precondition: null,
        
        // Clear other non-essential fields
        selectedDiscretionaryActivities: undefined,
        selectedNamedActivity: null,
        selectedTrustActivity: null,
        wishes: ''
      };
      
      // Insert the duplicate right after the original
      this.probate.bequests.splice(originalIndex + 1, 0, duplicatedBequest);
      
      // Note: Precondition assignments are NOT copied since we only copy Name on Will and Linked Beneficiary
      
      // Uncheck the original bequest after duplication
      originalBequest.isDone = false;
    }
    
    this.markAsChanged();
  }

  // Generate a new unique bequest ID
  private generateNewBequestId(): number {
    if (!this.probate?.bequests || this.probate.bequests.length === 0) {
      return -1;
    }
    // Find the minimum negative ID (for new items) or use -1
    const minId = Math.min(...this.probate.bequests.map(b => b.id), 0);
    return minId - 1;
  }

  // Add a new PreCondition for a specific bequest (by id)
  addPreConditionForBequest(bequestId: number) {
    const bequest = this.findBequestById(bequestId, true);
    if (!bequest) return;

    // Create a new placeholder precondition
    const timestamp = Date.now();
    const newPreConditionValue = `${this.PRECONDITION_PLACEHOLDER_PREFIX}${timestamp}`;
    this.placeholderParentMap[newPreConditionValue] = null;

    // Assign the new precondition to this specific bequest
    bequest.conditionText = newPreConditionValue;
  }

  // Add a new standalone Precondition group (empty, ready for bequests to be dragged into)
  addNewPrecondition() {
    // Create a new placeholder precondition
    const timestamp = Date.now();
    const newPreConditionValue = `${this.PRECONDITION_PLACEHOLDER_PREFIX}${timestamp}`;
    this.placeholderParentMap[newPreConditionValue] = null;

    // Add to standalone preconditions as a PreconditionNode (will be rendered as an empty drop zone)
    this.standalonePreconditions.push({
      key: newPreConditionValue,
      precondition: undefined,
      children: [],
      willFileName: this.currentWillFileName || undefined
    });
    this.markAsChanged();
  }

  // Add a child precondition to an existing precondition (nested preconditions)
  addChildPrecondition(parentNode: PreconditionNode) {
    // Create a new placeholder precondition
    const timestamp = Date.now();
    const newPreConditionValue = `${this.PRECONDITION_PLACEHOLDER_PREFIX}${timestamp}`;
    this.placeholderParentMap[newPreConditionValue] = parentNode.key;

    // Add as a child of the parent node
    parentNode.children.push({
      key: newPreConditionValue,
      precondition: undefined,
      children: [],
      willFileName: this.currentWillFileName || undefined
    });
    this.markAsChanged();
  }

  getPreConditionDisplayValue(preCondition: string | null): string {
    if (!preCondition) {
      return '';
    }
    if (preCondition.startsWith(this.PRECONDITION_PLACEHOLDER_PREFIX)) {
      return '';
    }
    return preCondition;
  }

  onPreConditionInput(oldPreCondition: string, inputValue: string) {
    const normalizedNewValue = (inputValue ?? '').trim();
    
    // If the new value is empty, don't update (user might still be typing)
    if (normalizedNewValue.length === 0) {
      return;
    }
    
    // If nothing changed, don't update
    if (oldPreCondition === normalizedNewValue) {
      return;
    }
    
    // Update the precondition text for all bequests
    this.updatePreCondition(oldPreCondition, normalizedNewValue);
    this.markAsChanged();
  }

  // Check if lookup is completed for a bequest item (has linked data)
  isLookupCompleted(bequestId: number): boolean {
    const bequest = this.findBequestById(bequestId, true);
    if (!bequest) return false;

    return !!(
      (bequest.organisationName?.trim()) ||
      (bequest.ccnLinked?.trim()) ||
      (bequest.addressLinked?.trim())
    );
  }

  // Check if a Named category is selected for a bequest
  hasNamedCategory(bequestId: number): boolean {
    const bequest = this.findBequestById(bequestId, true);
    return !!bequest?.selectedNamedActivity;
  }

  // Clear Named category selection for a bequest
  clearNamedCategory(bequestId: number): void {
    const bequest = this.findBequestById(bequestId, true);
    if (bequest) {
      bequest.selectedNamedActivity = null;
      bequest.charityCategoryId = null;
      this.markAsChanged();
    }
  }

  // Check if the linked charity is a client (Y) - used for green color
  isLookupClientCharity(bequestId: number): boolean {
    const bequest = this.findBequestById(bequestId, true);
    if (!bequest) return false;
    return this.isLookupCompleted(bequestId) && bequest.linkedClientStatus?.toUpperCase() === 'Y';
  }

  // Check if the linked charity is a non-client (N) - used for grey color
  isLookupNonClientCharity(bequestId: number): boolean {
    const bequest = this.findBequestById(bequestId, true);
    if (!bequest) return false;
    return this.isLookupCompleted(bequestId) && bequest.linkedClientStatus?.toUpperCase() !== 'Y';
  }

  // Returns the CSS color class for a bequest bar based on link status and bequest type
  getBequestColorClass(bequestId: number): string {
    const bequest = this.findBequestById(bequestId, true);
    if (!bequest || !this.isLookupCompleted(bequestId)) return '';

    const isClient = bequest.linkedClientStatus?.toUpperCase() === 'Y';
    if (!isClient) return 'bequest-color-grey'; // non-client → grey

    // Use bequestSubType (string code) — reliably set by the UI dropdown
    const typeCode = (bequest.bequestSubType as string)?.toUpperCase();
    if (!typeCode) return ''; // no bequest type selected yet — stay neutral
    if (typeCode === 'AE' || typeCode === 'AP' || typeCode === 'AR' || typeCode === 'APOS')
      return 'bequest-color-green';
    if (typeCode === 'EHAD' || typeCode === 'PHAD' || typeCode === 'RHAD' || typeCode === 'POSHAD')
      return 'bequest-color-red';
    if (typeCode === 'CE' || typeCode === 'CP2' || typeCode === 'CR' || typeCode === 'CPOS' || typeCode === 'WTRU')
      return 'bequest-color-amber';
    return ''; // unknown type — stay neutral
  }

  getBequestPanelClass(bequestId: number): string {
    const c = this.getBequestColorClass(bequestId);
    return c ? c + '-panel' : '';
  }

  /**
   * Clear all linked data from lookup for a bequest item
   */
  clearLinkedData(bequestId: number): void {
    const bequest = this.findBequestById(bequestId, true);
    if (bequest) {
      bequest.beneficiaryId = null;
      bequest.organisationName = '';
      bequest.ccnLinked = '';
      bequest.addressLinked = '';
      bequest.linkedClientStatus = '';
      bequest.triggerHads = false;
      this.markAsChanged();
      this.cdr.detectChanges();
    }
  }

  // Remove a PreCondition text box (sets conditionText to empty, keeps the bequests)
  removePreCondition(preCondition: string) {
    // Set conditionText to empty for all bequests with this PreCondition
    // This removes the PreCondition but keeps the BequestText and charity boxes
    const fallbackValue = this.placeholderParentMap.hasOwnProperty(preCondition)
      ? (this.placeholderParentMap[preCondition] || '')
      : '';
    for (const bequest of this.charitableRawBequests) {
      if (bequest.conditionText === preCondition) {
        bequest.conditionText = fallbackValue;
      }
    }
    if (this.placeholderParentMap.hasOwnProperty(preCondition)) {
      delete this.placeholderParentMap[preCondition];
    }
    // Also remove from standalonePreconditions if it was a standalone one (recursive search)
    const removeFromNodes = (nodes: PreconditionNode[], keyToRemove: string): boolean => {
      for (let i = 0; i < nodes.length; i++) {
        if (nodes[i].key === keyToRemove) {
          nodes.splice(i, 1);
          return true;
        }
        if (removeFromNodes(nodes[i].children, keyToRemove)) return true;
      }
      return false;
    };
    removeFromNodes(this.standalonePreconditions, preCondition);
  }

  // Calculate age from DOB and DOD
  calculateAge(): string {
    if (!this.editableProbate.dateOfBirth || !this.editableProbate.dateOfDeath) {
      return '';
    }

    try {
      // Parse DD/MM/YYYY format
      const dobParts = this.editableProbate.dateOfBirth.split('/');
      const dodParts = this.editableProbate.dateOfDeath.split('/');

      if (dobParts.length !== 3 || dodParts.length !== 3) {
        return '';
      }

      const dobDay = parseInt(dobParts[0], 10);
      const dobMonth = parseInt(dobParts[1], 10) - 1; // Months are 0-indexed
      const dobYear = parseInt(dobParts[2], 10);

      const dodDay = parseInt(dodParts[0], 10);
      const dodMonth = parseInt(dodParts[1], 10) - 1;
      const dodYear = parseInt(dodParts[2], 10);

      if (isNaN(dobDay) || isNaN(dobMonth) || isNaN(dobYear) ||
        isNaN(dodDay) || isNaN(dodMonth) || isNaN(dodYear)) {
        return '';
      }

      const dob = new Date(dobYear, dobMonth, dobDay);
      const dod = new Date(dodYear, dodMonth, dodDay);

      // Calculate age
      let age = dodYear - dobYear;

      // Adjust if birthday hasn't occurred yet in the year of death
      if (dodMonth < dobMonth || (dodMonth === dobMonth && dodDay < dobDay)) {
        age--;
      }

      return age >= 0 ? age.toString() : '';
    } catch {
      return '';
    }
  }

  // Add a new non-charitable beneficiary
  addNonCharitableBequest() {
    const newBequest = this.createNewBequest({
      isCharitable: false,
      includeExtendedFields: true
    });

    // Set the fileName and will reference to match current will filter so it shows up and saves correctly
    if (this.currentWillFileName && this.probate?.wills) {
      newBequest.fileName = this.currentWillFileName;
      newBequest.will = this.probate.wills.find(w => w.fileName === this.currentWillFileName) ?? null as any;
    }

    // Ensure bequests array exists and push the new bequest
    if (this.probate) {
      if (!this.probate.bequests) {
        this.probate.bequests = [];
      }
      this.probate.bequests.push(newBequest);
    }
  }

  removeCharitableBequest(bequestId: number) {
    // Remove directly from probate.bequests
    if (this.probate?.bequests) {
      this.removeItemById(this.probate.bequests, bequestId);
    }
  }

  // Remove a non-charitable beneficiary
  removeNonCharitableBequest(bequestId: number) {
    // Remove directly from probate.bequests
    if (this.probate?.bequests) {
      this.removeItemById(this.probate.bequests, bequestId);
    }
  }

  // Get all drop list IDs (main list + all precondition lists including nested)
  getAllDropListIds(): string[] {
    const ids = ['main-bequest-list'];
    
    // Recursively collect all precondition node keys
    const collectNodeIds = (nodes: PreconditionNode[]) => {
      for (const node of nodes) {
        ids.push('precondition-list-' + node.key);
        if (node.children && node.children.length > 0) {
          collectNodeIds(node.children);
        }
      }
    };
    collectNodeIds(this.standalonePreconditions);
    
    return ids;
  }

  // Get bequests not assigned to any user-created precondition
  getUnassignedBequests(): Bequest[] {
    return this.charitableRawBequests.filter(b =>
      !this.bequestPreconditionAssignments[b.id]
    );
  }

  // Get bequests assigned to a specific standalone precondition
  getBequestsForStandalonePrecondition(precondition: string): Bequest[] {
    return this.charitableRawBequests.filter(b =>
      this.bequestPreconditionAssignments[b.id] === precondition
    );
  }

  // Drag start - track dragging state
  onDragStarted(bequest: Bequest) {
    this.isDragging = true;
    this.currentlyDraggingBequest = bequest;
  }

  // Drag ended - reset state
  onDragEnded() {
    // Reset dragging state after a delay to allow panel click events to be blocked
    setTimeout(() => {
      this.isDragging = false;
      this.currentlyDraggingBequest = null;
    }, 100);
  }

  // Handle panel opened event - only navigate if not dragging
  onPanelOpened(bequest: Bequest) {
    if (!this.isDragging) {
      this.navigateToBequestPage(bequest);
    }
  }

  // Assign a single bequest to a precondition (reuse existing ID if group already exists)
  private assignBequestToPreconditionKey(bequest: Bequest, precondition: string) {
    this.bequestPreconditionAssignments[bequest.id] = precondition;
    bequest.conditionText = precondition;

    const existingBequestWithPrecondition = this.probate.bequests.find(b =>
      b.id !== bequest.id &&
      b.conditionText === precondition &&
      b.precondition?.id
    );

    if (existingBequestWithPrecondition?.precondition) {
      bequest.precondition = {
        id: existingBequestWithPrecondition.precondition.id,
        probateId: this.probate.probateId,
        text: precondition
      };
    } else {
      bequest.precondition = {
        id: bequest.precondition?.id || undefined,
        probateId: this.probate.probateId,
        text: precondition
      };
    }
  }

  // Unassign a single bequest from its precondition
  private unassignBequestFromPreconditionKey(bequest: Bequest) {
    delete this.bequestPreconditionAssignments[bequest.id];
    bequest.conditionText = '';
    bequest.precondition = null;
  }

  // Handle dropping a bequest into a precondition group
  onDropToPrecondition(event: CdkDragDrop<any>, precondition: string) {
    const draggedBequest = event.item.data as Bequest;
    if (!draggedBequest) return;

    const isTransferFromDifferentContainer = event.previousContainer !== event.container;
    const isReorderInSameContainer = event.previousContainer === event.container && event.previousIndex !== event.currentIndex;

    if (!isTransferFromDifferentContainer && !isReorderInSameContainer) {
      return;
    }

    // Assign primary dragged bequest
    this.assignBequestToPreconditionKey(draggedBequest, precondition);

    // If the dragged bequest was selected, also move all other selected bequests
    const coMovers = draggedBequest.isDone
      ? this.charitableRawBequests.filter(b => b.isDone === true && b.id !== draggedBequest.id)
      : [];
    for (const b of coMovers) {
      this.assignBequestToPreconditionKey(b, precondition);
    }

    // Reorder within probate.bequests to reflect the new grouping
    if (this.probate?.bequests) {
      const movedIds = new Set([draggedBequest.id, ...coMovers.map(b => b.id)]);
      const charitableBequests = this.probate.bequests.filter(b => b.isCharitable === true);

      const groupBequests = charitableBequests.filter(b =>
        this.bequestPreconditionAssignments[b.id] === precondition && !movedIds.has(b.id)
      );
      const otherCharitableBequests = charitableBequests.filter(b =>
        this.bequestPreconditionAssignments[b.id] !== precondition && !movedIds.has(b.id)
      );
      const nonCharitableBequests = this.probate.bequests.filter(b => b.isCharitable === false);

      const targetIndex = Math.min(event.currentIndex, groupBequests.length);
      groupBequests.splice(targetIndex, 0, draggedBequest, ...coMovers);

      this.probate.bequests = [...otherCharitableBequests, ...groupBequests, ...nonCharitableBequests];
    }

    this.markAsChanged();
  }

  // Handle dropping back to main list (unassign from precondition or reorder)
  onMainListDrop(event: CdkDragDrop<any>) {
    const draggedBequest = event.item.data as Bequest;
    if (!draggedBequest) return;

    const isTransferFromDifferentContainer = event.previousContainer !== event.container;
    const isReorderInSameContainer = event.previousContainer === event.container && event.previousIndex !== event.currentIndex;

    if (!isTransferFromDifferentContainer && !isReorderInSameContainer) {
      return;
    }

    // Unassign primary dragged bequest
    if (this.bequestPreconditionAssignments[draggedBequest.id]) {
      this.unassignBequestFromPreconditionKey(draggedBequest);
    }

    // If the dragged bequest was selected, also unassign all other selected bequests
    const coMovers = draggedBequest.isDone
      ? this.charitableRawBequests.filter(b => b.isDone === true && b.id !== draggedBequest.id)
      : [];
    for (const b of coMovers) {
      if (this.bequestPreconditionAssignments[b.id]) {
        this.unassignBequestFromPreconditionKey(b);
      }
    }

    // Reorder within probate.bequests
    if (this.probate?.bequests) {
      const movedIds = new Set([draggedBequest.id, ...coMovers.map(b => b.id)]);
      const charitableBequests = this.probate.bequests.filter(b => b.isCharitable === true);

      const unassignedBequests = charitableBequests.filter(b =>
        !this.bequestPreconditionAssignments[b.id] && !movedIds.has(b.id)
      );
      const assignedBequests = charitableBequests.filter(b =>
        this.bequestPreconditionAssignments[b.id] && !movedIds.has(b.id)
      );
      const nonCharitableBequests = this.probate.bequests.filter(b => b.isCharitable === false);

      const targetIndex = Math.min(event.currentIndex, unassignedBequests.length);
      unassignedBequests.splice(targetIndex, 0, draggedBequest, ...coMovers);

      this.probate.bequests = [...unassignedBequests, ...assignedBequests, ...nonCharitableBequests];
    }

    this.markAsChanged();
  }

  // Remove a standalone precondition (unassign all bequests from it)
  removeStandalonePrecondition(preconditionKey: string) {
    // Helper to collect all keys from a node and its children (for cleanup)
    const collectAllKeys = (node: PreconditionNode): string[] => {
      const keys = [node.key];
      for (const child of node.children) {
        keys.push(...collectAllKeys(child));
      }
      return keys;
    };

    // Find and remove the node from the nested structure
    const removeFromNodes = (nodes: PreconditionNode[], keyToRemove: string): PreconditionNode | null => {
      for (let i = 0; i < nodes.length; i++) {
        if (nodes[i].key === keyToRemove) {
          const removed = nodes.splice(i, 1)[0];
          return removed;
        }
        // Check children recursively
        const removedFromChild = removeFromNodes(nodes[i].children, keyToRemove);
        if (removedFromChild) return removedFromChild;
      }
      return null;
    };

    const removedNode = removeFromNodes(this.standalonePreconditions, preconditionKey);
    
    // Get all keys to clean up (including children)
    const keysToClean = removedNode ? collectAllKeys(removedNode) : [preconditionKey];

    // Unassign all bequests from this precondition and all its children
    for (const keyToClean of keysToClean) {
      for (const bequestId of Object.keys(this.bequestPreconditionAssignments)) {
        if (this.bequestPreconditionAssignments[Number(bequestId)] === keyToClean) {
          delete this.bequestPreconditionAssignments[Number(bequestId)];
          
          // CRITICAL: Also clear the bequest's conditionText and precondition
          const bequest = this.findBequestById(Number(bequestId), true);
          if (bequest) {
            bequest.conditionText = '';
            bequest.precondition = null;
          }
        }
      }
      // Clean up placeholder map
      if (this.placeholderParentMap.hasOwnProperty(keyToClean)) {
        delete this.placeholderParentMap[keyToClean];
      }
    }
    
    this.markAsChanged();
  }

  // Handle drag and drop for charitable bequests (legacy)
  onBequestDrop(event: CdkDragDrop<{ preCondition: string | null, items: Bequest[] }>) {
    const draggedBequest = event.item.data as Bequest;
    const targetPreCondition = event.container.data.preCondition;
    const sourcePreCondition = event.previousContainer.data.preCondition;

    // Get fresh copies of items in source and target groups
    const sourceItems = [...this.BequestItemsForPreCondition(sourcePreCondition)];
    const targetItems = event.previousContainer === event.container
      ? sourceItems
      : [...this.BequestItemsForPreCondition(targetPreCondition)];

    if (event.previousContainer === event.container) {
      // Reordering within the same precondition group
      if (event.previousIndex !== event.currentIndex) {
        // Move item in the local array
        moveItemInArray(sourceItems, event.previousIndex, event.currentIndex);

        // Rebuild probate.bequests with new order
        if (this.probate?.bequests) {
          const charitableBequests = this.charitableRawBequests;

          // Remove all items from this precondition group
          const otherCharitableItems = charitableBequests.filter(b => {
          const bPreCond = b.conditionText && b.conditionText.trim().length > 0 ? b.conditionText : null;
          return bPreCond !== targetPreCondition;
        });

        // Find where to insert the reordered items
        let insertPosition = 0;
          for (let i = 0; i < charitableBequests.length; i++) {
            const bPreCond = charitableBequests[i].conditionText && charitableBequests[i].conditionText!.trim().length > 0
              ? charitableBequests[i].conditionText
            : null;
          if (bPreCond === targetPreCondition) {
            insertPosition = i;
            break;
          }
        }

        // Rebuild the array: items before + reordered items + items after
          const itemsBefore = otherCharitableItems.filter((_, idx) => {
            const origIdx = charitableBequests.indexOf(_);
          return origIdx < insertPosition;
        });
          const itemsAfter = otherCharitableItems.filter(item => !itemsBefore.includes(item));

          // Get non-charitable bequests
          const nonCharitableBequests = this.probate.bequests.filter(b => b.isCharitable === false);

          // Rebuild probate.bequests
          this.probate.bequests = [...itemsBefore, ...sourceItems, ...itemsAfter, ...nonCharitableBequests];
        }
      }
    } else {
      // Moving to a different precondition group
      // Update the bequest's conditionText to match the target group
      draggedBequest.conditionText = targetPreCondition || '';

      // Note: With nested preconditions, we keep the precondition node visible
      // even after a bequest is dropped into it (it can have child preconditions)

      // Remove from source group
      const sourceIdx = sourceItems.findIndex(b => b.id === draggedBequest.id);
      if (sourceIdx > -1) {
        sourceItems.splice(sourceIdx, 1);
      }

      // Add to target group at the drop position
      targetItems.splice(event.currentIndex, 0, draggedBequest);

      // Rebuild probate.bequests preserving order of all groups
      if (this.probate?.bequests) {
        const newCharitableArray: Bequest[] = [];
      const processedIds = new Set<number>();

      for (const preCondition of this.UniquePreConditions) {
        const itemsForGroup = preCondition === sourcePreCondition ? sourceItems
          : preCondition === targetPreCondition ? targetItems
            : this.BequestItemsForPreCondition(preCondition);

        for (const item of itemsForGroup) {
            if (!processedIds.has(item.id)) {
              newCharitableArray.push(item);
              processedIds.add(item.id);
            }
          }
        }

        // Get non-charitable bequests
        const nonCharitableBequests = this.probate.bequests.filter(b => b.isCharitable === false);

        // Rebuild probate.bequests
        this.probate.bequests = [...newCharitableArray, ...nonCharitableBequests];
      }
    }
  }

  // Lookup charity for a beneficiary
  onLookupCharity(bequestId: number) {
    const bequest = this.findBequestById(bequestId, true);
    if (!bequest) return;

    this.currentLookupBequestId = bequestId;
      this.isCharityLookupOpen = true;

    const ccn = bequest.charityCommissionNumber?.toString().trim() || '';
      this.beneficiarySearchQuery = {
      beneficiaryName: ccn ? '' : (bequest.name || ''),
        beneficiaryAliasName: '',
      beneficiaryURN: ccn,
        address: '',
        postcode: ''
      };

      this.beneficiarySearchResults = [];
      this.selectedBeneficiaryForLookup = null;

      if (this.beneficiarySearchQuery.beneficiaryName || this.beneficiarySearchQuery.beneficiaryURN) {
        this.onBeneficiarySearch();
    }

    // setting fields for newBeneficiary
    this.newBeneficiary = {
      ...this.newBeneficiary,
      name: bequest.name || '',
      addressLine1: bequest.beneficiaryAddressOnWill || '',
      beneficiarySourceURN: ccn,
    };
    this.alert = { text: '', type: '' };

  }

  // Open lookup with current FROM WILL data (triggered by Enter key) - same as Lookup button
  openLookupWithCurrentData(bequestId: number, event: Event) {
    event.preventDefault(); // Prevent form submission
    this.onLookupCharity(bequestId); // Use same logic as Lookup button
  }

  closeCharityLookup() {
    this.isCharityLookupOpen = false;
    this.currentLookupBequestId = null;
    this.beneficiarySearchResults = [];
    this.selectedBeneficiaryForLookup = null;
    this.lookupSortColumn = '';
    this.lookupSortDirection = 'asc';
    // Reset search fields
    this.beneficiarySearchQuery = {
      beneficiaryName: '',
      beneficiaryAliasName: '',
      beneficiaryURN: '',
      address: '',
      postcode: ''
    };
    // re-setting fields for newBeneficiary
    this.newBeneficiary = {
      ...this.newBeneficiary,
      name: '',
      addressLine1: '',
      addressLine2: '',
      addressLine3: '',
      addressLine5: '',
      postcode: '',
      web: '',
      email: '',
      telephone: '',
      activities: '',
      aliases: [],
      beneficiarySourceURN: '',
    };
    this.alert = { text: '', type: '' };
  }

  // Handle mousedown on popup overlays - close only when clicking overlay background
  onLookupOverlayMouseDown(event: MouseEvent) {
    this.handleOverlayMouseDown(event, 'charity-lookup-overlay', () => this.closeCharityLookup());
  }

  onNotesOverlayMouseDown(event: MouseEvent) {
    this.handleOverlayMouseDown(event, 'notes-overlay', () => this.closeNotes());
  }

  onWishesOverlayMouseDown(event: MouseEvent) {
    this.handleOverlayMouseDown(event, 'wishes-popup-overlay', () => this.closeWishesPopup());
  }

  onTextPopupOverlayMouseDown(event: MouseEvent) {
    this.handleOverlayMouseDown(event, 'text-popup-overlay', () => this.closeTextPopup());
  }

  onTrustOverlayMouseDown(event: MouseEvent) {
    this.handleOverlayMouseDown(event, 'trust-popup-overlay', () => this.closeTrustPopup());
  }

  onDiscretionaryOverlayMouseDown(event: MouseEvent) {
    this.handleOverlayMouseDown(event, 'trust-popup-overlay', () => this.closeDiscretionaryPopup());
    }

  onDiscretionaryCategoryOverlayMouseDown(event: MouseEvent) {
    this.handleOverlayMouseDown(event, 'category-popup-overlay', () => this.closeDiscretionaryCategoryPopup());
  }

  // Wishes Popup Methods
  openWishesPopup(bequestId: number) {
    const bequest = this.findBequestById(bequestId, true);
    if (!bequest) return;
    this.currentWishesBequestId = bequestId;
      this.wishesPopupText = bequest.wishes || '';
      this.isWishesPopupOpen = true;
  }

  closeWishesPopup() {
    this.isWishesPopupOpen = false;
    this.currentWishesBequestId = null;
    this.wishesPopupText = '';
  }

  applyWishesPopup() {
    if (this.currentWishesBequestId === null) {
      this.closeWishesPopup();
      return;
    }
    const bequest = this.findBequestById(this.currentWishesBequestId, true);
      if (bequest) {
        bequest.wishes = this.wishesPopupText;
        this.markAsChanged();
    }
    this.closeWishesPopup();
  }

  // Text popup field configuration mapping
  private readonly textPopupConfig: Record<string, { title: string; field?: keyof Bequest; isNonCharitable?: boolean }> = {
    nameOnWill: { title: 'Name on Will', field: 'name' },
    addressOnWill: { title: 'Address on Will', field: 'beneficiaryAddressOnWill' },
    organisationName: { title: 'Organisation Name', field: 'organisationName' },
    addressLinked: { title: 'Address Linked', field: 'addressLinked' },
    effectDescription: { title: 'Effect', field: 'effectDescription' },
    effectDescriptionNonCharitable: { title: 'Effect', field: 'effectDescription', isNonCharitable: true },
    associatedTextFromUser: { title: 'Associated Text', field: 'associatedTextFromUser' },
  };

  // Generic Text Popup Methods
  openTextPopup(fieldType: string, bequestId?: number, preconditionKey?: string) {
    this.textPopupFieldType = fieldType;
    this.textPopupBequestId = bequestId ?? null;
    this.textPopupPreconditionKey = preconditionKey ?? null;

    const config = this.textPopupConfig[fieldType];
    if (config && bequestId) {
      this.textPopupTitle = config.title;
      const bequest = this.findBequestById(bequestId, !config.isNonCharitable);
      this.textPopupValue = (bequest as any)?.[config.field!] || '';
    } else if (fieldType === 'benhamClause') {
        this.textPopupTitle = 'Benham Clause';
        this.textPopupValue = this.benhamClauseText || '';
    } else if (fieldType === 'footer') {
        this.textPopupTitle = 'Footer';
        // Convert pipe-separated notes to newlines for easier editing
        this.textPopupValue = (this.charitableBequestNotes || '')
          .split('|').map(s => s.trim()).filter(s => s.length > 0).join('\n');
    } else if (fieldType === 'precondition' && preconditionKey) {
        this.textPopupTitle = 'Pre-Condition';
          this.textPopupValue = this.getPreConditionDisplayValue(preconditionKey);
    } else if (fieldType.startsWith('formerAddress_')) {
        // Former address fields: formerAddress_address, formerAddress_town, etc.
        // bequestId is used as addressId here, or -1 for pending address
        const fieldName = fieldType.replace('formerAddress_', '');
        const titleMap: Record<string, string> = {
          address: 'Former Address',
          town: 'Former Address - Town',
          county: 'Former Address - County',
          postcode: 'Former Address - Postcode',
          country: 'Former Address - Country'
        };
        this.textPopupTitle = titleMap[fieldName] || 'Former Address';
        
        if (bequestId === -1) {
          // Pending (flat view) address
          this.textPopupValue = (this._pendingFormerAddress as any)?.[fieldName] || '';
        } else if (bequestId !== undefined) {
          // Grouped view - find address by ID
          const address = this.formerAddresses.find(a => a.id === bequestId);
          this.textPopupValue = (address as any)?.[fieldName] || '';
        }
    } else if (fieldType.startsWith('deceasedAddress_')) {
        // Deceased address fields: deceasedAddress_address, deceasedAddress_town, etc.
        const fieldName = fieldType.replace('deceasedAddress_', '');
        const fieldMap: Record<string, string> = {
          address: 'deceasedAddress',
          town: 'deceasedTown',
          county: 'deceasedCounty',
          postcode: 'deceasedPostcode',
          country: 'deceasedCountry'
        };
        const titleMap: Record<string, string> = {
          address: 'Deceased Address',
          town: 'Deceased Address - Town',
          county: 'Deceased Address - County',
          postcode: 'Deceased Address - Postcode',
          country: 'Deceased Address - Country'
        };
        this.textPopupTitle = titleMap[fieldName] || 'Deceased Address';
        const editableField = fieldMap[fieldName];
        this.textPopupValue = (this.editableProbate as any)?.[editableField] || '';
    } else if (fieldType.startsWith('executor_')) {
        // Executor fields: executor_name, executor_address, executor_town, executor_relationship, etc.
        // bequestId is used as executor trackingId here
        const fieldName = fieldType.replace('executor_', '');
        const titleMap: Record<string, string> = {
          name: 'Executor Name',
          address: 'Executor Address',
          town: 'Executor - Town',
          county: 'Executor - County',
          postcode: 'Executor - Postcode',
          country: 'Executor - Country',
          relationship: 'Relationship to Deceased'
        };
        this.textPopupTitle = titleMap[fieldName] || 'Executor';
        
        if (bequestId !== undefined) {
          const executor = this.executors.find(e => e.trackingId === bequestId);
          this.textPopupValue = (executor as any)?.[fieldName] || '';
        }
    } else if (fieldType.startsWith('solicitor_')) {
        // Solicitor/Extracted By fields
        const fieldName = fieldType.replace('solicitor_', '');
        const fieldMap: Record<string, string> = {
          name: 'solicitorName',
          reference: 'solicitorReference',
          address: 'solicitorAddress',
          town: 'solicitorTown',
          county: 'solicitorCounty',
          postcode: 'solicitorPostcode',
          country: 'solicitorCountry'
        };
        const titleMap: Record<string, string> = {
          name: 'Solicitor Name',
          reference: 'Reference',
          address: 'Solicitor Address',
          town: 'Solicitor - Town',
          county: 'Solicitor - County',
          postcode: 'Solicitor - Postcode',
          country: 'Solicitor - Country'
        };
        this.textPopupTitle = titleMap[fieldName] || 'Solicitor';
        const editableField = fieldMap[fieldName];
        this.textPopupValue = (this.editableProbate as any)?.[editableField] || '';
    } else if (fieldType === 'relativePersonName') {
        this.textPopupTitle = 'Relative Name';
        this.textPopupValue = this.relativePersonName || '';
    } else {
        this.textPopupTitle = 'Edit Text';
        this.textPopupValue = '';
    }

    this.isTextPopupOpen = true;
  }

  closeTextPopup() {
    this.isTextPopupOpen = false;
    this.textPopupValue = '';
    this.textPopupTitle = '';
    this.textPopupFieldType = '';
    this.textPopupBequestId = null;
    this.textPopupPreconditionKey = null;
  }

  applyTextPopup() {
    const fieldType = this.textPopupFieldType;
    const bequestId = this.textPopupBequestId;

    const config = this.textPopupConfig[fieldType];
    if (config && bequestId && config.field) {
      const bequest = this.findBequestById(bequestId, !config.isNonCharitable);
          if (bequest) {
        (bequest as any)[config.field] = this.textPopupValue;
            this.markAsChanged();
          }
    } else if (fieldType === 'benhamClause') {
        this.benhamClauseText = this.textPopupValue;
        this.markAsChanged();
    } else if (fieldType === 'footer') {
        // Convert newlines back to pipe-separated format
        this.charitableBequestNotes = this.textPopupValue
          .split('\n').map(s => s.trim()).filter(s => s.length > 0).join(' | ');
        this.markAsChanged();
    } else if (fieldType === 'precondition') {
        const oldPrecondition = this.textPopupPreconditionKey;
        const newPrecondition = this.textPopupValue.trim();
        if (oldPrecondition && newPrecondition.length > 0 && oldPrecondition !== newPrecondition) {
          // updatePreCondition already handles updating the node key in standalonePreconditions
          this.updatePreCondition(oldPrecondition, newPrecondition);
          this.markAsChanged();
        }
    } else if (fieldType.startsWith('formerAddress_')) {
        // Former address fields
        const fieldName = fieldType.replace('formerAddress_', '');
        const addressId = bequestId; // bequestId is used as addressId here
        
        if (addressId === -1) {
          // Pending (flat view) address
          (this._pendingFormerAddress as any)[fieldName] = this.textPopupValue;
        } else if (addressId !== undefined && addressId !== null) {
          // Grouped view - find address by ID
          const address = this.formerAddresses.find(a => a.id === addressId);
          if (address) {
            (address as any)[fieldName] = this.textPopupValue;
          }
        }
        this.markAsChanged();
    } else if (fieldType.startsWith('deceasedAddress_')) {
        // Deceased address fields
        const fieldName = fieldType.replace('deceasedAddress_', '');
        const fieldMap: Record<string, string> = {
          address: 'deceasedAddress',
          town: 'deceasedTown',
          county: 'deceasedCounty',
          postcode: 'deceasedPostcode',
          country: 'deceasedCountry'
        };
        const editableField = fieldMap[fieldName];
        if (editableField) {
          (this.editableProbate as any)[editableField] = this.textPopupValue;
          this.markAsChanged();
        }
    } else if (fieldType.startsWith('executor_')) {
        // Executor fields
        const fieldName = fieldType.replace('executor_', '');
        const executorTrackingId = bequestId; // bequestId is used as executor trackingId
        
        if (executorTrackingId !== undefined && executorTrackingId !== null) {
          const executor = this.executors.find(e => e.trackingId === executorTrackingId);
          if (executor) {
            (executor as any)[fieldName] = this.textPopupValue;
            this.markAsChanged();
          }
        }
    } else if (fieldType.startsWith('solicitor_')) {
        // Solicitor/Extracted By fields
        const fieldName = fieldType.replace('solicitor_', '');
        const fieldMap: Record<string, string> = {
          name: 'solicitorName',
          reference: 'solicitorReference',
          address: 'solicitorAddress',
          town: 'solicitorTown',
          county: 'solicitorCounty',
          postcode: 'solicitorPostcode',
          country: 'solicitorCountry'
        };
        const editableField = fieldMap[fieldName];
        if (editableField) {
          (this.editableProbate as any)[editableField] = this.textPopupValue;
          this.markAsChanged();
        }
    } else if (fieldType === 'relativePersonName') {
        this.relativePersonName = this.textPopupValue;
        this.markAsChanged();
    }

    this.closeTextPopup();
  }

  // Trust Popup Methods
  openTrustPopup(bequestId: number, isCharitable: boolean = true) {
    this.openBequestFieldPopup(
      bequestId, isCharitable, 'trustLookup',
      (id) => this.currentTrustBequestId = id,
      (isChar) => this.currentTrustIsCharitable = isChar,
      (text) => this.trustPopupText = text,
      () => this.isTrustPopupOpen = true
    );
  }

  closeTrustPopup() {
    this.isTrustPopupOpen = false;
    this.currentTrustBequestId = null;
    this.trustPopupText = '';
  }

  applyTrustPopup() {
    this.applyBequestFieldPopup(
      this.currentTrustBequestId,
      this.currentTrustIsCharitable,
      'trustLookup',
      this.trustPopupText,
      () => this.closeTrustPopup()
    );
  }

  // Discretionary Popup Methods
  openDiscretionaryPopup(bequestId: number, isCharitable: boolean = true) {
    this.openBequestFieldPopup(
      bequestId, isCharitable, 'discretionaryLookup',
      (id) => this.currentDiscretionaryBequestId = id,
      (isChar) => this.currentDiscretionaryIsCharitable = isChar,
      (text) => this.discretionaryPopupText = text,
      () => this.isDiscretionaryPopupOpen = true
    );
  }

  closeDiscretionaryPopup() {
    this.isDiscretionaryPopupOpen = false;
    this.currentDiscretionaryBequestId = null;
    this.discretionaryPopupText = '';
  }

  applyDiscretionaryPopup() {
    this.applyBequestFieldPopup(
      this.currentDiscretionaryBequestId,
      this.currentDiscretionaryIsCharitable,
      'discretionaryLookup',
      this.discretionaryPopupText,
      () => this.closeDiscretionaryPopup()
    );
  }

  onCharitySearch() {
    // Build query parameters with trimmed values (without modifying the original model)
    let parameters = new HttpParams();

    const trimmedCharityName = this.charitySearchQuery.charityName?.trim() || '';
    const trimmedCcn = this.charitySearchQuery.ccn?.trim() || '';
    const trimmedAddress = this.charitySearchQuery.address?.trim() || '';
    const trimmedPostcode = this.charitySearchQuery.postcode?.trim() || '';

    if (trimmedCharityName) {
      parameters = parameters.set('charityName', trimmedCharityName);
      parameters = parameters.set('orgName', trimmedCharityName);
    }

    if (trimmedCcn) {
      parameters = parameters.set('ccn', trimmedCcn);
    }
    if (trimmedAddress) {
      parameters = parameters.set('address', trimmedAddress);
    }
    if (trimmedPostcode) {
      parameters = parameters.set('postcode', trimmedPostcode);
    }

    // Call the API
    this.http.get<any[]>('/charitycentral/SearchCharityLookup', { params: parameters })
      .subscribe({
        next: (results) => {
          this.charitySearchResults = results;
        },
        error: (error) => {
          console.error('Error searching charities:', error);
          this.charitySearchResults = [];
        }
      });
  }

  onCharitySearchKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.onCharitySearch();
    }
  }

  onCharityNameInput(value: string) {
    // Emit the search term to the Subject for debounced search
    this.charityNameSearchSubject.next(value);
  }

  // Beneficiary Search Methods
  onBeneficiarySearch() {
    const trimmedBeneficiaryName = this.beneficiarySearchQuery.beneficiaryName?.trim() || '';
    const trimmedBeneficiaryAliasName = this.beneficiarySearchQuery.beneficiaryName?.trim() || ''; //JP deliberately set to the name, so it searches aliases as well
    const trimmedBeneficiaryURN = this.beneficiarySearchQuery.beneficiaryURN?.trim() || '';
    const trimmedAddress = this.beneficiarySearchQuery.address?.trim() || '';
    const trimmedPostcode = this.beneficiarySearchQuery.postcode?.trim() || '';

    const parameters = new HttpParams()
      .set('beneficiaryname', trimmedBeneficiaryName)
      .set('beneficiaryaliasname', trimmedBeneficiaryAliasName)
      .set('beneficiaryurn', trimmedBeneficiaryURN)
      .set('address', trimmedAddress)
      .set('postcode', trimmedPostcode);

    // Call the API
    this.http.get<any[]>('/charitycentral/getbeneficiaries', { params: parameters })
      .subscribe({
        next: (results) => {
          this.beneficiarySearchResults = results;
        },
        error: (error) => {
          console.error('Error searching beneficiaries:', error);
          this.beneficiarySearchResults = [];
        }
      });
  }

  onBeneficiarySearchKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.onBeneficiarySearch();
    }
  }

  onBeneficiaryNameInput(value: string) {
    this.beneficiaryNameSearchSubject.next(value);
  }

  // Tooltip methods for beneficiary lookup
  private tooltipHideTimeouts: { [key: number]: any } = {};
  private isMouseOverTooltip: { [key: number]: boolean } = {};

  showTooltip(index: number, aliases: string[], previousNames: string[], cell: HTMLElement) {
    // Clear any pending hide timeout
    if (this.tooltipHideTimeouts[index]) {
      clearTimeout(this.tooltipHideTimeouts[index]);
      this.tooltipHideTimeouts[index] = null;
    }

    this.tooltipVisible[index] = true;
    this.tooltipContent[index] = {
      aliases: aliases || [],
      previousNames: previousNames || []
    };

    const rect = cell.getBoundingClientRect();
    const offset = 8;
    const tooltipWidth = 550; // Wider for two-column layout
    const maxItems = Math.max(aliases?.length || 0, previousNames?.length || 0);
    const tooltipHeight = Math.min(maxItems * 30 + 80, 400);

    // Always position below the row
    let top = rect.bottom + offset;

    // If tooltip would go off screen bottom, position above instead
    if (top + tooltipHeight > window.innerHeight - 20) {
      top = rect.top - tooltipHeight - offset;
    }

    let left = rect.left;
    const maxLeft = window.innerWidth - tooltipWidth - 20;
    if (left > maxLeft) left = maxLeft;
    if (left < 0) left = 0;

    this.tooltipPosition[index] = { top, left };
  }

  hideTooltip(index: number) {
    // Add a delay before hiding to allow mouse to move to tooltip
    this.tooltipHideTimeouts[index] = setTimeout(() => {
      if (!this.isMouseOverTooltip[index]) {
        this.tooltipVisible[index] = false;
      }
    }, 150);
  }

  onTooltipMouseEnter(index: number) {
    this.isMouseOverTooltip[index] = true;
    // Clear any pending hide timeout
    if (this.tooltipHideTimeouts[index]) {
      clearTimeout(this.tooltipHideTimeouts[index]);
      this.tooltipHideTimeouts[index] = null;
    }
  }

  onTooltipMouseLeave(index: number) {
    this.isMouseOverTooltip[index] = false;
    this.tooltipVisible[index] = false;
  }

  selectCharity(beneficiary: Beneficiary) {
    this.selectedBeneficiaryForLookup = beneficiary;
  }

  onCharityRowDoubleClick(beneficiary: Beneficiary) {
    this.selectedBeneficiaryForLookup = beneficiary;
    this.applySelectedBeneficiary();
  }

  onNameOnWillChange(bequest: any) {
    const nameOnWill = (bequest.name || '').toLowerCase().trim();
    const orgName = (bequest.organisationName || '').toLowerCase().trim();
    if (nameOnWill !== orgName) {
      bequest.beneficiaryNameOnWillVisible = true;
    }
    this.markAsChanged();
  }

  applySelectedBeneficiary() {
    if (!this.selectedBeneficiaryForLookup || !this.currentLookupBequestId) return;

    const bequest = this.findBequestById(this.currentLookupBequestId, true);
    if (bequest) {
      // Set the beneficiaryId from the selected beneficiary
      bequest.beneficiaryId = this.selectedBeneficiaryForLookup.id;
      bequest.organisationName = this.selectedBeneficiaryForLookup.name;
      const fullAddress = [
        this.selectedBeneficiaryForLookup.addressLine1,
        this.selectedBeneficiaryForLookup.addressLine2,
        this.selectedBeneficiaryForLookup.addressLine3,
        this.selectedBeneficiaryForLookup.addressLine4,
        this.selectedBeneficiaryForLookup.addressLine5,
        this.selectedBeneficiaryForLookup.postcode,
      ].filter(line => line?.trim()).join(' ');
      if (fullAddress) {
        bequest.addressLinked = fullAddress;
      }
      if (this.selectedBeneficiaryForLookup.beneficiarySourceURN?.trim()) {
        bequest.ccnLinked = this.selectedBeneficiaryForLookup.beneficiarySourceURN;
      }
      // Set client status from beneficiary's isClient flag (Y = client/subscribed, N = non-client)
      bequest.linkedClientStatus = this.selectedBeneficiaryForLookup.isClient ? 'Y' : 'N';
      // Mirror the beneficiary's HAD flag onto the bequest so the Named_HAD preview option
      // surfaces immediately after Apply, without waiting for a backend round-trip to recompute it.
      bequest.triggerHads = this.selectedBeneficiaryForLookup.triggerHads === true;
      
      // Auto-set BeneficiaryNameOnWillVisible based on name comparison
      const nameOnWill = (bequest.name || bequest.beneficiaryNameOnWill || '').toLowerCase().trim();
      const orgName = (bequest.organisationName || '').toLowerCase().trim();
      bequest.beneficiaryNameOnWillVisible = nameOnWill !== orgName;

      // Auto-set BeneficiaryURNOnWillVisible based on CCN comparison (only if CCN on Will is not empty)
      const ccnOnWill = (bequest.charityCommissionNumber || bequest.beneficiaryURNOnWill || '').toLowerCase().trim();
      const ccnLinked = (bequest.ccnLinked || '').toLowerCase().trim();
      bequest.beneficiaryURNOnWillVisible = ccnOnWill.length > 0 && ccnOnWill !== ccnLinked;
      
      this.markAsChanged();
    }
    this.closeCharityLookup();
    this.cdr.detectChanges();
  }

  // Add a new alternative name
  addAlternativeName() {
    if (!this.probate) return;
    if (!this.probate.aliases) {
      this.probate.aliases = [];
    }
    // Add an empty string to the aliases array
    this.probate.aliases.push('');
    this.markAsChanged();
  }

  // Remove an alternative name
  removeAlternativeName(trackingId: number) {
    const index = this.alternativeNames.findIndex(a => a.trackingId === trackingId);
    if (index > -1) {
      this.alternativeNames.splice(index, 1);
    }
  }

  // Toggle alias selection
  toggleAliasSelection(index: number) {
    if (this.selectedAliasIndices.has(index)) {
      this.selectedAliasIndices.delete(index);
    } else {
      this.selectedAliasIndices.add(index);
    }
  }

  // Remove selected alternative names
  removeSelectedAlternativeNames() {
    if (!this.probate?.aliases) return;
    
    // Remove aliases at selected indices (in reverse order to maintain correct indices)
    const indicesToRemove = Array.from(this.selectedAliasIndices).sort((a, b) => b - a);
    for (const index of indicesToRemove) {
      if (index >= 0 && index < this.probate.aliases.length) {
        this.probate.aliases.splice(index, 1);
      }
    }
    
    // Clear selections
    this.selectedAliasIndices.clear();
    this.markAsChanged();
  }

  // Check if any names are selected
  hasSelectedAlternativeNames(): boolean {
    return this.selectedAliasIndices.size > 0;
  }

  // Add a new former address
  addFormerAddress() {
    if (!this.probate) return;
    if (!this.probate.addresses) {
      this.probate.addresses = [];
    }

    if (!this.formerAddressesGrouped) {
      // First time clicking "Add Additional" - convert flat view to grouped
      this.formerAddressesGrouped = true;

      // Sync _pendingFormerAddress to probate.addresses before switching to grouped view
      const existingFormer = this.probate.addresses.find(a => a.rank > 0);
      if (existingFormer) {
        // Address already in probate.addresses (loaded from DB) — update it with current pending data
        existingFormer.address = this._pendingFormerAddress.address || '';
        existingFormer.town = this._pendingFormerAddress.town || '';
        existingFormer.county = this._pendingFormerAddress.county || '';
        existingFormer.postcode = this._pendingFormerAddress.postcode || '';
        existingFormer.country = this._pendingFormerAddress.country || '';
      } else if (this._pendingFormerAddress.address || this._pendingFormerAddress.town ||
        this._pendingFormerAddress.postcode) {
        // No existing entry — add from pending data
        this.probate.addresses.push({
          id: this.getNextAddressId(),
          probateId: this.probate.probateId,
          address: this._pendingFormerAddress.address,
          town: this._pendingFormerAddress.town,
          county: this._pendingFormerAddress.county,
          postcode: this._pendingFormerAddress.postcode,
          country: this._pendingFormerAddress.country,
          rank: 1,  // First former address gets rank 1
          isFinal: false
        });
      }

      // Add a new empty former address
      this.probate.addresses.push({
        id: this.getNextAddressId(),
        probateId: this.probate.probateId,
        address: '',
        town: '',
        county: '',
        postcode: '',
        country: '',
        rank: 2,
        isFinal: false
      });

      // Reset pending
      this._pendingFormerAddress = { address: '', town: '', county: '', postcode: '', country: '', isFinal: true };
    } else {
      // Subsequent clicks - just add a new address
      const maxRank = Math.max(...this.probate.addresses.map(a => a.rank), 0);
      this.probate.addresses.push({
        id: this.getNextAddressId(),
        probateId: this.probate.probateId,
        address: '',
        town: '',
        county: '',
        postcode: '',
        country: '',
        rank: maxRank + 1,
        isFinal: false
      });
    }
    this.markAsChanged();
  }

  // Get next negative ID for new addresses
  private getNextAddressId(): number {
    const allIds = this.probate?.addresses?.map(a => a.id) || [];
    const minId = Math.min(...allIds, 0);
    return minId - 1;
  }

  // Recalculate address ranks: deceased=0, formers in insertion order=1, 2, 3, ...
  // Strict convention — only a row already at rank 0 counts as the deceased current;
  // every other row is treated as a former. Does not auto-migrate legacy rows.
  private recalculateAddressRanks() {
    if (!this.probate?.addresses || this.probate.addresses.length === 0) return;

    const deceasedAddr = this.probate.addresses.find(a => a.rank === 0);

    const formerAddrs = this.probate.addresses.filter(a => a !== deceasedAddr);

    if (deceasedAddr) deceasedAddr.rank = 0;

    let nextRank = 1;
    for (const addr of formerAddrs) {
      addr.rank = nextRank++;
    }
  }

  // Remove a former address
  removeFormerAddress(addressId: number) {
    if (!this.probate?.addresses) return;

    const index = this.probate.addresses.findIndex(a => a.id === addressId);
    if (index > -1) {
      this.probate.addresses.splice(index, 1);

      // If only one or no former addresses left, revert to flat view
      const remainingFormer = this.formerAddresses;
      if (remainingFormer.length <= 1) {
        if (remainingFormer.length === 1) {
          // Copy to pending and mark as final
          this._pendingFormerAddress = {
            address: remainingFormer[0].address,
            town: remainingFormer[0].town,
            county: remainingFormer[0].county,
            postcode: remainingFormer[0].postcode,
            country: remainingFormer[0].country,
            isFinal: true
          };
          // Remove from probate.addresses
          const idx = this.probate.addresses.findIndex(a => a.id === remainingFormer[0].id);
          if (idx > -1) this.probate.addresses.splice(idx, 1);
        }
        this.formerAddressesGrouped = false;
      }

      this.recalculateAddressRanks();
      this.markAsChanged();
    }
  }

  // Ensure pending former address is synced to probate.addresses before save
  syncPendingFormerAddress() {
    if (!this.formerAddressesGrouped && this.probate) {
      if (!this.probate.addresses) this.probate.addresses = [];

      const existingFormer = this.probate.addresses.find(a => a.rank > 0);
      const hasPendingData = this._pendingFormerAddress.address || this._pendingFormerAddress.town ||
        this._pendingFormerAddress.postcode;

      if (hasPendingData) {
        if (existingFormer) {
          // Update the existing address entry in place with what the user typed
          existingFormer.address = this._pendingFormerAddress.address || '';
          existingFormer.town = this._pendingFormerAddress.town || '';
          existingFormer.county = this._pendingFormerAddress.county || '';
          existingFormer.postcode = this._pendingFormerAddress.postcode || '';
          existingFormer.country = this._pendingFormerAddress.country || '';
        } else {
          // No existing entry - insert a new one
          this.probate.addresses.push({
            id: this.getNextAddressId(),
            probateId: this.probate.probateId,
            address: this._pendingFormerAddress.address,
            town: this._pendingFormerAddress.town,
            county: this._pendingFormerAddress.county,
            postcode: this._pendingFormerAddress.postcode,
            country: this._pendingFormerAddress.country,
            rank: 1,
            isFinal: true
          });
        }
      } else if (existingFormer) {
        // User cleared all fields — remove the former address entry entirely
        const idx = this.probate.addresses.indexOf(existingFormer);
        if (idx > -1) this.probate.addresses.splice(idx, 1);
      }
    }
    this.recalculateAddressRanks();
  }

  // Sync deceased address from editableProbate back to probate.addresses (rank 0).
  // Strict: only a row already at rank 0 is treated as the deceased current. If none exists,
  // a new rank-0 row is created when the user has entered any data; otherwise no row is added.
  // Existing rows at rank > 0 stay as former addresses and are not repurposed.
  syncDeceasedAddress() {
    if (!this.probate) return;
    if (!this.probate.addresses) this.probate.addresses = [];

    const deceasedAddr = this.probate.addresses.find(a => a.rank === 0);

    if (deceasedAddr) {
      deceasedAddr.address = this.editableProbate.deceasedAddress || '';
      deceasedAddr.town = this.editableProbate.deceasedTown || '';
      deceasedAddr.county = this.editableProbate.deceasedCounty || '';
      deceasedAddr.postcode = this.editableProbate.deceasedPostcode || '';
      deceasedAddr.country = this.editableProbate.deceasedCountry || '';
    } else {
      const hasData = !!(this.editableProbate.deceasedAddress
        || this.editableProbate.deceasedTown
        || this.editableProbate.deceasedCounty
        || this.editableProbate.deceasedPostcode
        || this.editableProbate.deceasedCountry);
      if (!hasData) return;
      this.probate.addresses.push({
        id: this.getNextAddressId(),
        probateId: this.probate.probateId,
        address: this.editableProbate.deceasedAddress || '',
        town: this.editableProbate.deceasedTown || '',
        county: this.editableProbate.deceasedCounty || '',
        postcode: this.editableProbate.deceasedPostcode || '',
        country: this.editableProbate.deceasedCountry || '',
        rank: 0,
        isFinal: false
      });
    }
  }

  // Sync solicitor from editableProbate back to probate.solicitor
  syncSolicitor() {
    if (!this.probate) return;

    // Initialize solicitor if not exists
    if (!this.probate.solicitor) {
      this.probate.solicitor = {
        id: 0,
        probateId: this.probate.probateId,
        name: '',
        reference: '',
        address: '',
        town: '',
        county: '',
        postcode: '',
        country: ''
      };
    }

    // Update solicitor fields from editableProbate
    this.probate.solicitor.name = this.editableProbate.solicitorName || '';
    this.probate.solicitor.reference = this.editableProbate.solicitorReference || '';
    this.probate.solicitor.address = this.editableProbate.solicitorAddress || '';
    this.probate.solicitor.town = this.editableProbate.solicitorTown || '';
    this.probate.solicitor.county = this.editableProbate.solicitorCounty || '';
    this.probate.solicitor.postcode = this.editableProbate.solicitorPostcode || '';
    this.probate.solicitor.country = this.editableProbate.solicitorCountry || '';
  }

  applyGenderTitleDefault() {
    if (this.selectedGender === 'M') {
      this.editableProbate.title = 'Mr';
    }
  }

  // Sync basic probate fields from editableProbate back to probate
  syncBasicProbateFields() {
    if (!this.probate) return;

    // Sync grantType (Type of Grant)
    this.probate.grantType = this.editableProbate.typeOfGrant || '';

    // Sync title, afterTitle, gender
    this.probate.title = this.editableProbate.title || '';
    this.probate.afterTitle = this.editableProbate.afterTitle || '';
    this.probate.gender = this.selectedGender || '';

    // Note: deceasedName is directly bound to probate.deceasedName in HTML, no sync needed

    // Sync dates (parse DD/MM/YYYY format)
    if (this.editableProbate.dateOfIssue) {
      const parsedDate = this.parseDateFromSlashes(this.editableProbate.dateOfIssue);
      if (parsedDate) this.probate.dateOfIssue = parsedDate;
    }
    if (this.editableProbate.dateOfBirth?.trim()) {
      const parsedDate = this.parseDateFromSlashes(this.editableProbate.dateOfBirth);
      if (parsedDate) this.probate.dateOfBirth = parsedDate;
    } else {
      this.probate.dateOfBirth = null;
    }
    if (this.editableProbate.dateOfDeath?.trim()) {
      const parsedDate = this.parseDateFromSlashes(this.editableProbate.dateOfDeath);
      if (parsedDate) this.probate.dateOfDeath = parsedDate;
    } else {
      this.probate.dateOfDeath = null;
    }

    // Sync gross and net values
    this.probate.grossValue = this.parseNumberFromFormatted(this.editableProbate.gross);
    this.probate.netValue = this.parseNumberFromFormatted(this.editableProbate.net);

    // Sync relationship to relative
    const rel = this.relationToRelative.trim();
    const name = this.relativePersonName.trim();
    this.probate.relationshipToRelative = rel && name ? `${rel} ${name}` : (rel || name || '');
  }

  // Helper to parse DD/MM/YYYY date string to Date object
  // Uses UTC noon to avoid timezone shift when serializing to JSON
  private parseDateFromSlashes(dateStr: string): Date | null {
    if (!dateStr) return null;
    if (typeof dateStr === 'string' && dateStr.includes('/')) {
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        const day = parseInt(parts[0]);
        const month = parseInt(parts[1]) - 1; // JS months are 0-indexed
        const year = parseInt(parts[2]);
        // Create date at UTC noon to prevent day shift during JSON serialization
        return new Date(Date.UTC(year, month, day, 12, 0, 0));
      }
    }
    return null;
  }

  // Helper to parse number from potentially formatted string
  private parseNumberFromFormatted(value: any): number {
    if (!value) return 0;
    if (typeof value === 'number') return value;
    // Remove commas and parse
    const cleaned = String(value).replace(/,/g, '');
    return parseFloat(cleaned) || 0;
  }

  // Sync executors UI array back to probate.executors before saving
  syncExecutors() {
    if (!this.probate) return;

    // Clear existing executors and rebuild from UI array
    this.probate.executors = [];

    // Add ALL executors to probate.executors array
    this.executors.forEach((executor, index) => {
      this.probate.executors.push({
        id: executor.id || 0,
        probateId: this.probate.probateId,
        rank: index + 1,
        name: executor.name || '',
        address: executor.address || '',
        town: executor.town || '',
        county: executor.county || '',
        postcode: executor.postcode || '',
        country: executor.country || '',
        relationship: executor.relationship || ''
      });
    });
  }

  // Sync editableWill UI object and benhamClauseText back to probate before saving
  syncWillData() {
    if (!this.probate) return;

    // Sync final will selection from UI to probate.wills
    if (this.probate.wills) {
      this.probate.wills.forEach(w => {
        w.final = w.fileName === this.finalWillFileName;
      });
    }

    // Sync benhamClauseText to probate.benhamClause
    this.probate.benhamClause = this.benhamClauseText || '';

    // Sync charitableBequestNotes (Footer) to probate.notes
    this.probate.notes = this.charitableBequestNotes || '';

    // Sync codicilDates array to probate.CodicilDates
    this.probate.CodicilDates = [];
    for (const dateStr of this.codicilDates) {
      if (dateStr && dateStr.trim()) {
        // Parse DD/MM/YYYY to Date - use UTC noon to avoid timezone shift
        if (typeof dateStr === 'string' && dateStr.includes('/')) {
          const parts = dateStr.split('/');
          if (parts.length === 3) {
            const day = parseInt(parts[0]);
            const month = parseInt(parts[1]) - 1;
            const year = parseInt(parts[2]);
            this.probate.CodicilDates.push(new Date(Date.UTC(year, month, day, 12, 0, 0)));
          }
        }
      }
    }
    // Remove camelCase property from API response to avoid duplicate keys in POST body
    delete (this.probate as any).codicilDates;

    // Also sync dateOfWill and missingPage to the final will if needed
    if (this.probate.wills && this.probate.wills.length > 0) {
      const finalWill = this.probate.wills.find(w => w.final) || this.probate.wills[0];

      // Sync missingPage to the currently viewed will (not just the final will)
      const currentWill = this.currentWillFileName
        ? this.probate.wills.find(w => w.fileName === this.currentWillFileName)
        : finalWill;
      if (currentWill) {
        currentWill.incomplete = !!this.editableWill.missingPage;
      }

      if (finalWill) {
        const dateStr = this.editableWill.dateOfWill;
        // User has cleared the field → persist null so backend stores the 1900-01-01 sentinel.
        if (!dateStr || (typeof dateStr === 'string' && dateStr.trim() === '')) {
          finalWill.dateOfWill = null;
        } else if (typeof dateStr === 'string' && dateStr.includes('/')) {
          // Use UTC noon to avoid timezone shift
          const parts = dateStr.split('/');
          if (parts.length === 3) {
            const day = parseInt(parts[0]);
            const month = parseInt(parts[1]) - 1;
            const year = parseInt(parts[2]);
            finalWill.dateOfWill = new Date(Date.UTC(year, month, day, 12, 0, 0));
          }
        } else {
          finalWill.dateOfWill = this.editableWill.dateOfWill;
        }
      }
    }
  }

  // Sync preconditions hierarchy to probate.preconditions before saving
  // This collects ALL preconditions (including empty ones without bequests) in parent-first order
  syncPreconditions() {
    if (!this.probate) return;

    const preconditions: any[] = [];
    
    // Recursively flatten the precondition tree, parents before children
    const flattenTree = (nodes: PreconditionNode[], parentText: string | null) => {
      for (const node of nodes) {
        // Skip placeholder keys that haven't been given real text
        if (node.key.startsWith(this.PRECONDITION_PLACEHOLDER_PREFIX)) {
          console.log(`[syncPreconditions] Skipping placeholder: ${node.key}`);
          continue;
        }

        const precondition: any = {
          id: node.precondition?.id || undefined,
          probateId: this.probate.probateId,
          text: node.key.trim(),  // Trim to match syncBequests
          parentPreconditionText: parentText?.trim() || null  // Server uses this to look up parent
        };
        // Note: Don't set parentPrecondition object - server uses parentPreconditionText instead

        preconditions.push(precondition);
        console.log(`[syncPreconditions] Added precondition: text="${node.key}", parentText="${parentText}", id=${precondition.id}`);

        // Recursively process children with this node as parent
        if (node.children && node.children.length > 0) {
          flattenTree(node.children, node.key.trim());
        }
      }
    };

    // Start with root preconditions (no parent)
    flattenTree(this.standalonePreconditions, null);

    this.probate.preconditions = preconditions;
    console.log(`[syncPreconditions] Total preconditions to save: ${preconditions.length}`, preconditions);
  }

  // Sync bequest UI aliases back to the actual properties before saving
  syncBequests() {
    if (!this.probate?.bequests) return;

    this.probate.bequests.forEach(bequest => {
      // CRITICAL FIX 1: Assign a will to new bequests if they don't have one
      if (!bequest.will && this.probate.wills && this.probate.wills.length > 0) {
        // Assign the final will, or the first will if no final will exists
        bequest.will = this.probate.wills.find(w => w.final) || this.probate.wills[0];
        console.log(`[syncBequests] Bequest ${bequest.id} (${bequest.name}): Assigned will ID=${bequest.will.id}`);
      }

      // CRITICAL FIX 2: Convert bequestSubType (string code) to bequestType (numeric enum)
      if (bequest.bequestSubType && typeof bequest.bequestSubType === 'string') {
        const enumValue = this.mapBequestTypeCodeToEnum(bequest.bequestSubType);
        if (enumValue !== null) {
          bequest.bequestType = enumValue;
          console.log(`[syncBequests] Bequest ${bequest.id} (${bequest.name}): Converted bequestSubType "${bequest.bequestSubType}" to bequestType ${enumValue}`);
        }
      } else if (!bequest.bequestSubType) {
        // "Select Type" was chosen — clear bequestType so validation below catches it
        bequest.bequestType = null as any;
      }

      // Clear inapplicable amount fields based on current bequest type
      // Prevents stale cashAmount showing in PDF when switching to residual/effect, and vice versa
      if (bequest.bequestSubType) {
        const valueType = this.getBequestValueType(bequest.bequestSubType);
        if (valueType !== 'pecuniary' && valueType !== 'proceedsOfSale') bequest.cashAmount = null;
        if (valueType !== 'residual' && valueType !== 'proceedsOfSale') bequest.percentageAmount = null;
        if (valueType !== 'effect') bequest.effectDescription = null;
      }
      
      // Sync name back to beneficiaryNameOnWill
      if (bequest.name !== undefined) {
        bequest.beneficiaryNameOnWill = bequest.name;
      }
      
      // Sync charityCommissionNumber back to beneficiaryURNOnWill (CCN on Will)
      if (bequest.charityCommissionNumber !== undefined) {
        bequest.beneficiaryURNOnWill = bequest.charityCommissionNumber;
      }
      
      // Sync bequestText/commonBequestText back to associatedTextOnWill
      if (bequest.bequestText !== undefined) {
        bequest.associatedTextOnWill = bequest.bequestText;
      } else if (bequest.commonBequestText !== undefined) {
        bequest.associatedTextOnWill = bequest.commonBequestText;
      }

      // Map notificationType → serviceTypeId before saving
      if (this.availableServices.length > 0) {
        const svcName = (bequest.notificationType || 'named').toLowerCase();
        const service = this.availableServices.find(s => s.name.toLowerCase() === svcName);
        if (service) bequest.serviceTypeId = service.id;
      }

      // Sync beneficiaryId / charityCategoryId logic:
      // Discretionary/Trust: beneficiary linking is disabled — clear any previously linked beneficiary
      // Named: beneficiary lookup OR category (mutually exclusive)
      if (bequest.notificationType === 'discretionary') {
        // Beneficiary linking not available — clear any previously linked data
        bequest.beneficiaryId = null;
        bequest.organisationName = '';
        bequest.ccnLinked = '';
        bequest.addressLinked = '';
        bequest.linkedClientStatus = '';
        bequest.triggerHads = false;
        bequest.charityCategoryId = (bequest.selectedDiscretionaryActivities && bequest.selectedDiscretionaryActivities.length > 0)
          ? bequest.selectedDiscretionaryActivities.map((a: any) => a.id)
          : null;
      } else if (bequest.notificationType === 'trust') {
        // Beneficiary linking not available — clear any previously linked data
        bequest.beneficiaryId = null;
        bequest.organisationName = '';
        bequest.ccnLinked = '';
        bequest.addressLinked = '';
        bequest.linkedClientStatus = '';
        bequest.triggerHads = false;
        if (bequest.selectedTrustActivity) {
          bequest.charityCategoryId = [bequest.selectedTrustActivity.id];
        } else {
          bequest.charityCategoryId = null;
        }
      } else if (bequest.beneficiaryId && bequest.beneficiaryId > 0) {
        // Named with lookup done — keep beneficiaryId, clear charityCategoryId
        bequest.charityCategoryId = null;
      } else if (bequest.selectedNamedActivity && bequest.notificationType === 'named') {
        // Named category selected, no lookup — set charityCategoryId, clear beneficiaryId
        bequest.beneficiaryId = null;
        bequest.charityCategoryId = [bequest.selectedNamedActivity.id];
      } else {
        // No lookup and no category selected — clear both
        bequest.beneficiaryId = null;
        bequest.charityCategoryId = null;
      }
      
      // Sync conditionText back to precondition
      if (bequest.conditionText !== undefined) {
        const trimmedText = bequest.conditionText.trim();
        
        // Don't save placeholder keys - only save actual text
        const isPlaceholder = bequest.conditionText.startsWith(this.PRECONDITION_PLACEHOLDER_PREFIX);
        
        if (trimmedText.length > 0 && !isPlaceholder) {
          // Create or update precondition if conditionText has actual value (not placeholder)
          const oldPreconditionId = bequest.precondition?.id;
          const oldParentPrecondition = bequest.precondition?.parentPrecondition;
          
          // Check if this precondition has a parent (from placeholderParentMap or existing)
          let parentPrecondition = oldParentPrecondition;
          const parentKey = this.placeholderParentMap[bequest.conditionText];
          if (parentKey && !parentKey.startsWith(this.PRECONDITION_PLACEHOLDER_PREFIX)) {
            // Find the parent precondition from other bequests
            const parentBequest = this.probate.bequests.find(b => 
              b.conditionText === parentKey && b.precondition
            );
            if (parentBequest?.precondition) {
              parentPrecondition = parentBequest.precondition;
            }
          }
          
          bequest.precondition = {
            id: bequest.precondition?.id || undefined,
            probateId: this.probate.probateId,
            text: trimmedText,
            parentPrecondition: parentPrecondition
          };
          console.log(`[syncBequests] Bequest ${bequest.id} (${bequest.name}): Setting precondition text="${trimmedText}", id=${oldPreconditionId}, parentId=${parentPrecondition?.id}`);
        } else {
          // Clear precondition if conditionText is empty or is a placeholder
          if (isPlaceholder) {
            console.log(`[syncBequests] Bequest ${bequest.id} (${bequest.name}): Skipping placeholder key "${bequest.conditionText}"`);
          }
          bequest.precondition = null;
        }
      }
    });
  }

  // Format number with commas
  formatNumberWithCommas(value: any): string {
    if (value === null || value === undefined || value === '') return '';

    // Convert to string and remove any existing commas
    let numStr = value.toString().replace(/,/g, '');

    // Handle decimal numbers
    const parts = numStr.split('.');
    const integerPart = parts[0];
    const decimalPart = parts.length > 1 ? '.' + parts[1] : '';

    // Add commas to integer part
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return formattedInteger + decimalPart;
  }

  // Remove commas from formatted number for storage
  removeCommasFromNumber(value: string): string {
    if (!value) return '';
    return value.replace(/,/g, '');
  }

  // Handle gross value change
  onGrossChange(event: any) {
    const rawValue = this.removeCommasFromNumber(event.target.value);
    this.editableProbate.gross = rawValue;
    this.editableProbate.grossFormatted = this.formatNumberWithCommas(rawValue);
  }

  // Handle net value change
  onNetChange(event: any) {
    const rawValue = this.removeCommasFromNumber(event.target.value);
    this.editableProbate.net = rawValue;
    this.editableProbate.netFormatted = this.formatNumberWithCommas(rawValue);
  }

  onCashAmountChange(event: any, bequest: any) {
    const rawValue = this.removeCommasFromNumber(event.target.value);
    bequest.cashAmount = rawValue ? parseFloat(rawValue) : null;
    bequest.cashAmountFormatted = this.formatNumberWithCommas(rawValue);
    this.markAsChanged();
  }

  populateEditableProbate() {
    if (this.probate) {
      // Reset former address state
      this.formerAddressesGrouped = false;
      this._pendingFormerAddress = { address: '', town: '', county: '', postcode: '', country: '', isFinal: true };

      // Get deceased address (rank 0 only) for editable fields. Strict — if no rank-0 row
      // exists, the deceased fields stay empty and any rank > 0 rows display as former addresses.
      const firstAddress = this.probate.addresses?.find(a => a.rank === 0);

      this.editableProbate = {
        caseReferenceNumber: this.probate.probateId || '',
        dateOfIssue: this.formatDateWithSlashes(this.probate.dateOfIssue),
        title: this.probate.title || '',
        afterTitle: this.probate.afterTitle || '',
        fullNameOfDeceased: this.probate.deceasedName || '',
        allNamesWhichTheDeceasedWasOtherwiseKnownAs: this.probate.aliases?.join(', ') || '',
        typeOfGrant: this.probate.grantType || '',
        dateOfBirth: this.formatDateWithSlashes(this.probate.dateOfBirth),
        dateOfDeath: this.formatDateWithSlashes(this.probate.dateOfDeath),
        deceasedAddress: firstAddress?.address || '',
        deceasedTown: firstAddress?.town || '',
        deceasedCounty: firstAddress?.county || '',
        deceasedPostcode: firstAddress?.postcode || '',
        deceasedCountry: firstAddress?.country || '',
        gross: this.probate.grossValue ?? '',
        net: this.probate.netValue ?? '',
        grossFormatted: this.formatNumberWithCommas(this.probate.grossValue),
        netFormatted: this.formatNumberWithCommas(this.probate.netValue),
        solicitorName: this.probate.solicitor?.name || '',
        solicitorReference: this.probate.solicitor?.reference || '',
        solicitorAddress: this.probate.solicitor?.address || '',
        solicitorTown: this.probate.solicitor?.town || '',
        solicitorCounty: this.probate.solicitor?.county || '',
        solicitorPostcode: this.probate.solicitor?.postcode || '',
        solicitorCountry: this.probate.solicitor?.country || ''
      };

      this.selectedGender = this.probate.gender || '';
      this.applyGenderTitleDefault();

      // Parse relationshipToRelative back into dropdown + name fields
      const storedRelationship = this.probate.relationshipToRelative || '';
      const relOptions = ['wife of', 'husband of', 'sister of', 'brother of', 'partner of', 'cousin of', 'son of', 'daughter of'];
      const matchedOption = relOptions.find(opt => storedRelationship.startsWith(opt));
      if (matchedOption) {
        this.relationToRelative = matchedOption;
        this.relativePersonName = storedRelationship.slice(matchedOption.length).trim();
      } else {
        this.relationToRelative = '';
        this.relativePersonName = storedRelationship;
      }

      // Populate former address fields from probate.addresses (rank > 0)
      const formerAddrs = this.probate.addresses?.filter(a => a.rank > 0) || [];
      if (formerAddrs.length === 1) {
        // Flat view: mirror the address data into _pendingFormerAddress for two-way binding
        // Do NOT remove from probate.addresses - syncPendingFormerAddress will update it in place
        const fa = formerAddrs[0];
        this._pendingFormerAddress = {
          address: fa.address || '',
          town: fa.town || '',
          county: fa.county || '',
          postcode: fa.postcode || '',
          country: fa.country || '',
          isFinal: fa.isFinal ?? true
        };
        this.formerAddressesGrouped = false;
      } else if (formerAddrs.length > 1) {
        this.formerAddressesGrouped = true;
      }

      // Populate benhamClauseText from probate.benhamClause
      this.benhamClauseText = this.probate.benhamClause || '';

      // Populate charitableBequestNotes (Footer) from probate.notes
      this.charitableBequestNotes = this.probate.notes || '';

      // Populate codicilDates array from probate.CodicilDates
      // API returns camelCase (codicilDates) but model uses PascalCase (CodicilDates)
      this.codicilDates = [];
      const apiCodicilDates = (this.probate as any).codicilDates ?? this.probate.CodicilDates;
      if (apiCodicilDates && apiCodicilDates.length > 0) {
        const seen = new Set<string>();
        for (const date of apiCodicilDates) {
          const formatted = this.formatDateWithSlashes(date);
          if (formatted && !seen.has(formatted)) {
            seen.add(formatted);
            this.codicilDates.push(formatted);
          }
        }
      }

      // Populate editableWill.dateOfWill from the final will
      if (this.probate.wills && this.probate.wills.length > 0) {
        const finalWill = this.probate.wills.find(w => w.final) || this.probate.wills[0];
        if (finalWill && finalWill.dateOfWill) {
          this.editableWill.dateOfWill = this.formatDateWithSlashes(finalWill.dateOfWill);
        }
        const initialWill = this.currentWillFileName
          ? this.probate.wills.find(w => w.fileName === this.currentWillFileName)
          : finalWill;
        this.editableWill.missingPage = !!(initialWill?.incomplete);
      }

      // Populate executors array from Probate data
      this.executors = [];

      // Add executors from the executors array
      if (this.probate.executors) {
        this.probate.executors.forEach((executor, index) => {
        this.executors.push({
            trackingId: this.executors.length,
            name: executor.name || '',
            address: executor.address || '',
            town: executor.town || '',
            county: executor.county || '',
            postcode: executor.postcode || '',
            country: executor.country || '',
            relationship: executor.relationship || '',
          isNewlyAdded: false
        });
        });
      }
    }
  }

  // Match bequest fileName with will tab fileName
  // Handles cases like "1234_scanned_will_1.pdf" matching "scanned_will_1"
  matchesWillFileName(bequestFileName: string, willTabFileName: string): boolean {
    if (!bequestFileName || !willTabFileName) return false;

    const bequestLower = bequestFileName.toLowerCase();
    const willLower = willTabFileName.toLowerCase().replace(/\.(pdf|PDF)$/i, '');

    // Direct match
    if (bequestLower.includes(willLower)) return true;

    // Extract the type pattern (scanned_will_X, uploaded_will_X, admonwillgrant)
    const scannedMatch = willLower.match(/scanned_will_(\d+)/);
    const uploadedMatch = willLower.match(/uploaded_will_(\d+)/);
    const admonMatch = willLower.includes('admonwillgrant');

    if (scannedMatch) {
      return bequestLower.includes(`scanned_will_${scannedMatch[1]}`);
    } else if (uploadedMatch) {
      return bequestLower.includes(`uploaded_will_${uploadedMatch[1]}`);
    } else if (admonMatch) {
      return bequestLower.includes('admonwillgrant');
    }

    return false;
  }

  hasPecuniaryBequest(bequestTypes: RAWBequestType) {

    const pecuniaryBequestTypeIds = [1, 9]

    if (pecuniaryBequestTypeIds.includes(bequestTypes)) {
      return true;
    }

    return false;


  }

  hasResidualBequest(bequestTypes: RAWBequestType) {

    const residualBequestTypeIds = [2, 3, 4, 5, 11]

    if (residualBequestTypeIds.includes(bequestTypes)) {
      return true;
    }

    return false;


  }

  hasEffectBequest(bequestTypes: RAWBequestType) {

    const effectBequestTypeIds = [4]

    if (effectBequestTypeIds.includes(bequestTypes)) {
      return true;
    }

    return false;


  }

  // Map bequestSubType enum to dropdown value (BequestTypeCode from BequestType table)
  mapBequestSubTypeToDropdown(bequestSubType: RAWBequestSubType, bequestType: RAWBequestType): string {
    // BequestSubType enum values (from RAWBequest.ts):
    // 0: Absolute, 1: AbsoluteCharitableBequests, 2: AbsoluteDebtsForgiven, 
    // 3: AbsoluteEffect, 4: AbsoluteInvestmentsAndFinancialInstruments, 
    // 5: AbsoluteMiscellaneous, 6: AbsoluteMiscellaneousItems, 
    // 7: AbsolutePecuniary, 8: AbsoluteResidual, 9: Conditional, 
    // 10: ConditionalCharitableBequests, 11: ConditionalEffect, 
    // 12: ConditionalMiscellaneous, 13: ConditionalMiscellaneousItems, 
    // 14: ConditionalPecuniary, 15: ConditionalResidual, 16: NA

    // Determine if it's Absolute, Conditional, or HAD based on bequestSubType
    const isAbsolute = bequestSubType <= 8;
    const isConditional = bequestSubType >= 9 && bequestSubType <= 15;

    // Check if it's a HAD type based on bequestType enum
    // HAD types are typically identified by specific bequestType values
    const isHAD = this.isHADBequest(bequestType);

    // Determine the type (Pecuniary, Residual, or Effect) based on bequestTypes
    // Return BequestTypeCode values from BequestType table
    if (this.hasPecuniaryBequest(bequestType)) {
      if (isHAD) return 'PHAD';  // Pecuniary HAD
      return isAbsolute ? 'AP' : 'CP2';  // AP: Absolute Pecuniary, CP2: Conditional Pecuniary
    } else if (this.hasResidualBequest(bequestType)) {
      if (isHAD) return 'RHAD';  // Residual HAD
      return isAbsolute ? 'AR' : 'CR';  // AR: Absolute Residual, CR: Conditional Residual
    } else if (this.hasEffectBequest(bequestType)) {
      if (isHAD) return 'EHAD';  // Effects HAD
      return isAbsolute ? 'AE' : 'CE';  // AE: Absolute Effects, CE: Conditional Effects
    }

    // Default to empty if can't determine
    return '';
  }

  // Check if bequest is a HAD (Had Already Died) type
  isHADBequest(bequestType: RAWBequestType): boolean {
    // HAD bequest types - these would need to be determined based on your specific BequestType enum values
    // For now, return false; update this when you have the specific HAD type identifiers
    return false;
  }

  // ========== PDF Page Reorder Methods ==========

  dropPage(event: CdkDragDrop<any[]>) {
    // Reorder the pages array
    moveItemInArray(this.pdfPages, event.previousIndex, event.currentIndex);

    // Check if order has changed from original
    const currentOrder = this.pdfPages.map(p => p.originalIndex);
    this.hasPageOrderChanged = !this.arraysEqual(currentOrder, this.originalPageOrder);

    // Debounce preview generation - only generate after user stops dragging
    if (this.previewTimeout) {
      clearTimeout(this.previewTimeout);
    }

    this.previewTimeout = setTimeout(async () => {
      if (this.hasPageOrderChanged) {
        await this.generateReorderedPdfPreview();
      } else {
        // Reset to original if order is back to normal
        if (this.reorderedPdfUrl) {
          const oldUrl = (this.reorderedPdfUrl as any).changingThisBreaksApplicationSecurity;
          if (oldUrl && oldUrl.startsWith('blob:')) {
            URL.revokeObjectURL(oldUrl);
          }
          this.reorderedPdfUrl = null;
          this.displayPdfUrl = this.cachedOriginalPdfUrl;
          this.pdfViewerSrc = this.currentPdfUrl;

          // Force reload to show original PDF
          this.showPdfViewer = false;
          setTimeout(() => {
            this.showPdfViewer = true;
          }, 50);
        }
      }
    }, 300); // Wait 300ms after last drag before regenerating
  }

  async deletePage(index: number) {
    if (this.pdfPages.length <= 1) {
      alert('Cannot delete the last remaining page — a PDF must have at least one page.');
      return;
    }
    if (!confirm(`Delete page ${index + 1}? Click "Save PDF Changes" afterwards to overwrite the stored PDF.`)) {
      return;
    }

    // Remove the page from the list
    this.pdfPages.splice(index, 1);

    // Keep the selected page index valid
    if (this.selectedPageIndex >= this.pdfPages.length) {
      this.selectedPageIndex = this.pdfPages.length - 1;
    }

    // Mark changed (drives the Save button + preview) and rebuild the preview without the page
    const currentOrder = this.pdfPages.map(p => p.originalIndex);
    this.hasPageOrderChanged = !this.arraysEqual(currentOrder, this.originalPageOrder);
    await this.generateReorderedPdfPreview();
  }

  async generateReorderedPdfPreview() {
    try {
      if (!this.currentPdfBytes) {
        return;
      }

      this.isGeneratingPreview = true;

      const newOrder = this.pdfPages.map(p => p.originalIndex);
      const reorderedPdfBytes = await this.pdfService.reorderPages(this.currentPdfBytes, newOrder);

      // Clean up old blob URL
      if (this.reorderedPdfUrl) {
        const oldUrl = (this.reorderedPdfUrl as any).changingThisBreaksApplicationSecurity;
        if (oldUrl && oldUrl.startsWith('blob:')) {
          URL.revokeObjectURL(oldUrl);
        }
      }

      // Create blob URL for preview
      const blob = new Blob([reorderedPdfBytes as any], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);

      this.reorderedPdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
      this.displayPdfUrl = this.reorderedPdfUrl;
      this.pdfViewerSrc = url;

      // Force PDF viewer to reload by toggling it
      this.showPdfViewer = false;
      setTimeout(() => {
        this.showPdfViewer = true;
        this.isGeneratingPreview = false;
      }, 50);
    } catch (error) {
      this.isGeneratingPreview = false;
      alert('Failed to generate preview. The PDF may be too large or corrupted.');
    }
  }

  arraysEqual(arr1: number[], arr2: number[]): boolean {
    if (arr1.length !== arr2.length) return false;
    return arr1.every((value, index) => value === arr2[index]);
  }

  selectPage(index: number) {
    this.selectedPageIndex = index;

    // Scroll to the selected page in pdf-viewer
    // Use a longer timeout to ensure PDF is rendered
    setTimeout(() => {
      this.scrollToPage(index);
    }, 300);
  }

  // Navigate to the page where a bequest appears in the PDF by searching for the name
  navigateToBequestPage(bequest: Bequest) {
    // Get the search term - use the beneficiary name
    const searchTerm = (bequest.name || bequest.beneficiaryNameOnWill)?.trim();
    if (!searchTerm) return;

    // First, make sure we're viewing the correct will PDF
    const bequestFileName = bequest.fileName || bequest.will?.fileName;
    if (bequestFileName && this.willTabsToDisplay.length > 0) {
      const willTab = this.willTabsToDisplay.find(w => w.fileName === bequestFileName);
      if (willTab && this.activeTab?.fileName !== bequestFileName) {
        this.setActiveTab(willTab).then(() => {
          // After tab is set and PDF loads, search for the name
          setTimeout(() => {
            this.searchAndNavigateToBequest(searchTerm);
          }, 800);
        });
        return;
      }
    }

    // If already on the correct tab, search for the name
    this.searchAndNavigateToBequest(searchTerm);
  }

  // Search for text in the PDF and navigate to it
  private searchAndNavigateToBequest(searchTerm: string) {
    // Set the search query
    this.pdfSearchQuery = searchTerm;

    // Wait for the PDF to render, then search
    setTimeout(() => {
      this.performPdfSearch();
    }, 300);
  }

  onPdfLoadComplete(pdfData: any) {
    // PDF loaded successfully
    // If a page was selected before PDF loaded, scroll to it now
    if (this.selectedPageIndex > 0) {
      setTimeout(() => {
        this.scrollToPage(this.selectedPageIndex);
      }, 300);
    }

    if (pdfData?.getPage) {
      pdfData.getPage(1).then((page: any) => {
        const viewport = page.getViewport({ scale: 1 });
        this.pdfBasePageWidth = viewport?.width || 0;
        if (this.autoFitZoomEnabled) {
          this.zone.run(() => this.calculateAutoZoom());
        }
      }).catch(() => {
        // Ignore errors fetching page dimensions; keep existing zoom
      });
    }
  }

  private scrollToPage(index: number, retryCount: number = 0) {
    const container = this.fullPageViewElement?.nativeElement ||
      document.querySelector('.full-page-view');

    if (!container) {
      // Retry if container not found yet
      if (retryCount < 5) {
        setTimeout(() => this.scrollToPage(index, retryCount + 1), 200);
      }
      return;
    }

    // Try different selectors for page elements
    let pageElements: NodeListOf<Element> | null = null;
    const selectors = [
      '.page',
      'canvas',
      '.pdfViewer .page',
      'pdf-viewer .page',
      '[data-page-number]',
      'div[style*="position: relative"]' // ng2-pdf-viewer often wraps pages in positioned divs
    ];

    for (const selector of selectors) {
      pageElements = container.querySelectorAll(selector);
      if (pageElements && pageElements.length >= this.pdfPages.length) {
        break;
      }
    }

    // If we don't have enough pages yet, retry
    if (!pageElements || pageElements.length < this.pdfPages.length) {
      if (retryCount < 10) {
        setTimeout(() => this.scrollToPage(index, retryCount + 1), 200);
      }
      return;
    }

    if (pageElements.length > index) {
      const pageElement = pageElements[index] as HTMLElement;
      if (pageElement && pageElement.offsetHeight > 0) {
        // Scroll the page into view
        pageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }

    // Fallback: Calculate scroll position based on page heights
    if (pageElements && pageElements.length > 0) {
      let scrollTop = 0;
      for (let i = 0; i < index && i < pageElements.length; i++) {
        const page = pageElements[i] as HTMLElement;
        if (page) {
          scrollTop += page.offsetHeight || 0;
          // Add any margin/padding
          const style = window.getComputedStyle(page);
          scrollTop += parseFloat(style.marginTop) + parseFloat(style.marginBottom);
        }
      }
      if (scrollTop > 0) {
        container.scrollTo({ top: scrollTop, behavior: 'smooth' });
      } else if (retryCount < 10) {
        // Retry if scroll position is 0 (pages might not be rendered yet)
        setTimeout(() => this.scrollToPage(index, retryCount + 1), 200);
      }
    }
  }

  async saveReorderedPages() {
    if (!this.currentPdfBytes) {
      alert('No PDF loaded to save');
      return;
    }

    if (!confirm('Are you sure you want to save the reordered PDF? This will replace the current PDF.')) {
      return;
    }

    try {
      this.isLoadingPages = true;

      // Get new page order (0-indexed original indices)
      const newOrder = this.pdfPages.map(p => p.originalIndex);

      // Create reordered PDF
      const reorderedPdfBytes = await this.pdfService.reorderPages(
        this.currentPdfBytes,
        newOrder
      );

      // Send to backend
      await this.uploadReorderedPdf(reorderedPdfBytes);

      alert('PDF pages reordered successfully!');

      // Clean up blob URL
      if (this.reorderedPdfUrl) {
        const oldUrl = (this.reorderedPdfUrl as any).changingThisBreaksApplicationSecurity;
        if (oldUrl && oldUrl.startsWith('blob:')) {
          URL.revokeObjectURL(oldUrl);
        }
      }

      // Reset change tracking and reload
      this.hasPageOrderChanged = false;
      this.reorderedPdfUrl = null;
      this.displayPdfUrl = null;
      this.pdfViewerSrc = '';
      this.originalPageOrder = newOrder;
      this.isLoadingPages = false;

      // Reload the PDF to show new version
      await this.loadPdfPages();
    } catch (error) {
      this.isLoadingPages = false;
      alert('Failed to save reordered PDF. Please try again.');
    }
  }

  async uploadReorderedPdf(pdfBytes: Uint8Array) {
    const blob = new Blob([pdfBytes as any], { type: 'application/pdf' });
    const formData = new FormData();
    formData.append('pdf', blob, `reordered_${this.activeTab.type}_${Date.now()}.pdf`);
    formData.append('probateId', this.incomingProbateId.toString());
    formData.append('type', this.activeTab.type);
    formData.append('fileName', this.activeTab.fileName);
    formData.append('documentsArchived', (this.probate?.documentsArchived ?? false).toString());

    return lastValueFrom(
      this.http.post('/charitycentral/SaveReorderedPdf', formData)
    );
  }

  // ========== PDF Search Methods ==========

  onPdfSearch() {
    if (!this.pdfSearchQuery || this.pdfSearchQuery.length < 2) {
      this.clearPdfSearch();
      this.removeSearchHighlights();
      return;
    }

    // Debounce search
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    this.searchTimeout = setTimeout(() => {
      this.performPdfSearch();
    }, 300);
  }

  performPdfSearch() {
    // Search through PDF text content rendered by pdf-viewer
    const textLayers = document.querySelectorAll('.textLayer');
    this.removeSearchHighlights();

    if (!this.pdfSearchQuery || textLayers.length === 0) {
      this.totalSearchMatches = 0;
      this.currentSearchMatch = 0;
      return;
    }

    let matchCount = 0;
    const searchTerm = this.pdfSearchQuery.toLowerCase().trim();

    textLayers.forEach((layer: Element) => {
      const textSpans = Array.from(layer.querySelectorAll('span'));

      // Build concatenated text with span mapping for multi-word phrase search
      let concatenatedText = '';
      const spanMap: { start: number; end: number; span: Element }[] = [];

      textSpans.forEach((span: Element) => {
        const text = span.textContent || '';
        const start = concatenatedText.length;
        concatenatedText += text;
        const end = concatenatedText.length;
        spanMap.push({ start, end, span });
      });

      const lowerText = concatenatedText.toLowerCase();
      let searchIndex = 0;

      // Find all occurrences of the search term in concatenated text
      while (searchIndex < lowerText.length) {
        const matchIndex = lowerText.indexOf(searchTerm, searchIndex);
        if (matchIndex === -1) break;

        matchCount++;

        // Find all spans that contain part of this match
        const matchEnd = matchIndex + searchTerm.length;
        spanMap.forEach(({ start, end, span }) => {
          if (start < matchEnd && end > matchIndex) {
            span.classList.add('pdf-search-highlight');

            if (matchCount === 1 && !span.classList.contains('pdf-search-current')) {
              // Scroll to first match
              span.scrollIntoView({ behavior: 'smooth', block: 'center' });
              span.classList.add('pdf-search-current');
            }
          }
        });

        searchIndex = matchIndex + 1;
      }
    });

    this.totalSearchMatches = matchCount;
    this.currentSearchMatch = matchCount > 0 ? 1 : 0;
  }

  findNextInPdf() {
    if (!this.pdfSearchQuery || this.totalSearchMatches === 0) return;

    const highlights = document.querySelectorAll('.pdf-search-highlight');
    if (highlights.length === 0) return;

    // Remove current highlight
    document.querySelectorAll('.pdf-search-current').forEach(el => {
      el.classList.remove('pdf-search-current');
    });

    // Move to next match
    this.currentSearchMatch = this.currentSearchMatch >= this.totalSearchMatches ? 1 : this.currentSearchMatch + 1;
    const currentElement = highlights[this.currentSearchMatch - 1];

    if (currentElement) {
      currentElement.classList.add('pdf-search-current');
      currentElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  findPreviousInPdf() {
    if (!this.pdfSearchQuery || this.totalSearchMatches === 0) return;

    const highlights = document.querySelectorAll('.pdf-search-highlight');
    if (highlights.length === 0) return;

    // Remove current highlight
    document.querySelectorAll('.pdf-search-current').forEach(el => {
      el.classList.remove('pdf-search-current');
    });

    // Move to previous match
    this.currentSearchMatch = this.currentSearchMatch <= 1 ? this.totalSearchMatches : this.currentSearchMatch - 1;
    const currentElement = highlights[this.currentSearchMatch - 1];

    if (currentElement) {
      currentElement.classList.add('pdf-search-current');
      currentElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  removeSearchHighlights() {
    document.querySelectorAll('.pdf-search-highlight').forEach(el => {
      el.classList.remove('pdf-search-highlight', 'pdf-search-current');
    });
  }

  clearPdfSearch() {
    this.pdfSearchQuery = '';
    this.currentSearchMatch = 0;
    this.totalSearchMatches = 0;
    this.removeSearchHighlights();
  }

  // ========== PDF Control Methods ==========

  zoomIn() {
    this.autoFitZoomEnabled = false;
    if (this.currentZoom < 200) {
      this.currentZoom += 25;
    }
  }

  zoomOut() {
    this.autoFitZoomEnabled = false;
    if (this.currentZoom > 50) {
      this.currentZoom -= 25;
    }
  }

  onZoomInputChange(event: Event) {
    this.autoFitZoomEnabled = false;
    const target = event.target as HTMLInputElement;
    let value = parseInt(target.value, 10);

    if (isNaN(value)) {
      return;
    }

    value = Math.max(25, Math.min(400, value));
    this.currentZoom = value;
  }

  confirmZoomInput(event: Event) {
    const target = event.target as HTMLInputElement;
    this.normalizeZoomInput(event);
    target.blur();
  }

  normalizeZoomInput(event: Event) {
    const target = event.target as HTMLInputElement;
    let value = parseInt(target.value, 10);

    if (isNaN(value)) {
      value = this.currentZoom;
    }

    value = Math.max(25, Math.min(400, value));
    target.value = value.toString();
    this.currentZoom = value;
  }

  private calculateAutoZoom() {
    if (!this.autoFitZoomEnabled || !this.pdfBasePageWidth) {
      return;
    }

    const container = this.getFullPageViewContainer();
    if (!container) {
      return;
    }

    const availableWidth = container.clientWidth;
    if (!availableWidth) {
      return;
    }

    const scale = Math.min(availableWidth / this.pdfBasePageWidth, 1);
    let computedZoom = Math.max(Math.round(scale * 100), 50);
    if (computedZoom >= 95 && computedZoom < 100) {
      computedZoom = 100;
    }

    if (computedZoom !== this.currentZoom) {
      this.currentZoom = computedZoom;
    }
  }

  private getFullPageViewContainer(): HTMLElement | null {
    if (this.fullPageViewElement?.nativeElement) {
      return this.fullPageViewElement.nativeElement as HTMLElement;
    }

    const fallback = document.querySelector('.full-page-view');
    return fallback instanceof HTMLElement ? fallback : null;
  }

  downloadPdf() {
    const url = this.pdfViewerSrc || this.currentPdfUrl;
    const link = document.createElement('a');
    link.href = url;
    link.download = `${this.activeTab.type}_${this.activeTab.index}.pdf`;
    link.click();
  }

  printPdf() {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = this.pdfViewerSrc || this.currentPdfUrl;
    document.body.appendChild(iframe);

    iframe.onload = () => {
      iframe.contentWindow?.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
    };
  }

  // Handle will selection from dropdown
  onWillSelected() {
    if (this.selectedWillIndex !== '') {
      const willIndex = typeof this.selectedWillIndex === 'string'
        ? parseInt(this.selectedWillIndex, 10)
        : this.selectedWillIndex;

      const willTab = this.willTabsToDisplay.find(w => w.index === willIndex);
      if (willTab) {
        this.setActiveTab(willTab);
      }
    }
  }

  // Check if the currently selected will is marked as final
  isSelectedWillFinal(): boolean {
    if (!this.selectedWillIndex) return false;

    const willIndex = typeof this.selectedWillIndex === 'string'
      ? parseInt(this.selectedWillIndex, 10)
      : this.selectedWillIndex;

    const willTab = this.willTabsToDisplay.find(w => w.index === willIndex);
    return willTab ? willTab.fileName === this.finalWillFileName : false;
  }

  // Check if single will is marked as final
  isSingleWillFinal(): boolean {
    if (this.willTabsToDisplay.length !== 1) return false;
    return this.willTabsToDisplay[0].fileName === this.finalWillFileName;
  }

  // Toggle the final will status for the currently selected will
  toggleFinalWill() {
    if (!this.selectedWillIndex) return;

    const willIndex = typeof this.selectedWillIndex === 'string'
      ? parseInt(this.selectedWillIndex, 10)
      : this.selectedWillIndex;

    const willTab = this.willTabsToDisplay.find(w => w.index === willIndex);
    if (willTab) {
      if (this.finalWillFileName === willTab.fileName) {
        // Unmark as final
        this.finalWillFileName = '';
      } else {
        // Mark as final
        this.finalWillFileName = willTab.fileName;
      }
      this.markAsChanged();
    }
  }

  // Toggle the final will status for single will
  toggleSingleWillFinal() {
    if (this.willTabsToDisplay.length !== 1) return;

    const willTab = this.willTabsToDisplay[0];
    if (this.finalWillFileName === willTab.fileName) {
      // Unmark as final
      this.finalWillFileName = '';
    } else {
      // Mark as final
      this.finalWillFileName = willTab.fileName;
    }
    this.markAsChanged();
  }

  // Format bequest type for display - returns full name from bequestTypeOptions
  formatBequestType(bequestSubType: RAWBequestSubType | number | string): string {
    if (bequestSubType == null) return '';

    // If it's a string (BequestTypeCode from dropdown), look up the full name
    if (typeof bequestSubType === 'string') {
      // Look up in bequestTypeOptions by code
      const matchedType = this.bequestTypeOptions.find(
        opt => opt.bequestTypeCode.toUpperCase() === bequestSubType.toUpperCase()
      );
      if (matchedType) {
        return matchedType.bequestType;
      }

      // Fallback for legacy hyphenated format (e.g., "absolute-pecuniary")
      const parts = bequestSubType.split('-');
      if (parts.length === 2) {
        const first = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
        const second = parts[1].charAt(0).toUpperCase() + parts[1].slice(1);
        return `${first} ${second}`;
      }
      return bequestSubType;
    }

    // If it's an enum number, convert to string name
    const enumName = RAWBequestSubType[bequestSubType];
    if (!enumName) return '';

    // Convert enum name to display format (e.g. "AbsolutePecuniary" -> "Absolute Pecuniary")
    const result = enumName
      .replace(/([A-Z])/g, ' $1') // Add space before capital letters
      .trim()
      .split(' ')
      .filter(word => word !== 'NA');

    return result.join(' ');
  }

  // Open popup
  openNotes() {
    this.isNotesOpen = true;

    // Sort notes by newest first
    this.notes.sort((a, b) =>
      new Date(b.dateAdded).getTime() - new Date(a.dateAdded).getTime()
    );
  }

  // Close popup
  closeNotes() {
    this.isNotesOpen = false;
  }

  // Add a new note
  addNote() {
    if (!this.newNoteText.trim()) return;

    const newNote: Note = {
      id: 0, // backend will assign ID
      probateId: Number(this.incomingProbateId),
      userId: this.loggedInUserId,
      userDisplayName: this.loggedInUserDisplayNameForNote,
      textContent: this.newNoteText.trim(),
      dateAdded: new Date()
    };

    // Add to array (top)
    this.notes.unshift(newNote);

    // Clear input
    this.newNoteText = '';

    // Save to backend (optional)
    this.saveNewNoteToApi(newNote);
  }

  addNoteFromEnter(event: any) {
    const keyboardEvent = event as KeyboardEvent;
    keyboardEvent.preventDefault();

    if (this.newNoteText.trim()) {
      this.addNote();
    }
  }

  // Delete a note
  deleteNote(index: number) {
    const note = this.notes[index];

    if (!confirm("Delete this note?")) return;

    this.notes.splice(index, 1);

    this.deleteNoteFromApi(note.id);
  }

  async getNotes(probateId: string) {

    const params = new HttpParams()
      .set('user', this.loggedInUserTag)
      .set('probateId', probateId);

    const notes = await firstValueFrom(
      this.http.get<Note[]>("/charitycentral/getnotes", { params: params })
    );

    return notes;

  }

  async saveNewNoteToApi(note: Note) {

    let parameters = new HttpParams()
      .set('user', this.loggedInUserTag)
      .set('probateid', note.probateId)
      .set('userid', note.userId);

    var response = this.http.post(
      "/charitycentral/addnote",
      JSON.stringify(note.textContent),
      {
        params: parameters,
        headers: new HttpHeaders({ "Content-Type": "application/json" })
      }
    );

    return await lastValueFrom(response);

  }

  async deleteNoteFromApi(noteId: number) {
    let parameters = new HttpParams()
      .set('user', this.loggedInUserTag)
      .set('noteid', noteId);

    var response = this.http.get("/charitycentral/deletenote", { params: parameters });

    return await lastValueFrom(response);
  }

  // Helper method to determine which value field to show based on bequest type
  getBequestValueType(bequestSubType: any): string | null {
    if (!bequestSubType) return null;

    const typeStr = String(bequestSubType).toUpperCase();

    // Handle BequestTypeCode values from database

    // Proceeds of Sale types: APOS, CPOS, POSHAD (Proceeds of Sale HAD).
    // These have their own special 3-field display (description, percentage, estimated value)
    const proceedsOfSaleCodes = ['APOS', 'CPOS', 'POSHAD'];
    if (proceedsOfSaleCodes.includes(typeStr)) {
      return 'proceedsOfSale';
    }

    // Check by name for "Proceeds of Sale HAD" (in case it has a different code)
    if (this.isProceedsOfSaleHAD(typeStr)) {
      return 'proceedsOfSale';
    }

    // Pecuniary types: AP (Absolute Pecuniary), CP2 (Conditional Pecuniary), PHAD (Pecuniary HAD)
    const pecuniaryCodes = ['AP', 'CP2', 'PHAD'];
    if (pecuniaryCodes.includes(typeStr)) {
      return 'pecuniary';
    }

    // Residual types: AR (Absolute Residual), CR (Conditional Residual), RHAD (Residual HAD)
    const residualCodes = ['AR', 'CR', 'RHAD'];
    if (residualCodes.includes(typeStr)) {
      return 'residual';
    }

    // Effect types: AE (Absolute Effects), CE (Conditional Effects), EHAD (Effects HAD)
    const effectCodes = ['AE', 'CE', 'EHAD'];
    if (effectCodes.includes(typeStr)) {
      return 'effect';
    }

    // Fallback for legacy string values (in case they still exist)
    const typeLower = typeStr.toLowerCase();
    if (typeLower.includes('proceeds')) {
      return 'proceedsOfSale';
    } else if (typeLower.includes('pecuniary')) {
      return 'pecuniary';
    } else if (typeLower.includes('residual')) {
      return 'residual';
    } else if (typeLower.includes('effect')) {
      return 'effect';
    }

    return null;
  }

  // Check if the bequest type code corresponds to "Proceeds of Sale HAD"
  isProceedsOfSaleHAD(typeCode: string): boolean {
    // Look up the type in bequestTypeOptions to check if it's "Proceeds of Sale HAD"
    const matchedType = this.bequestTypeOptions.find(
      opt => opt.bequestTypeCode.toUpperCase() === typeCode.toUpperCase()
    );
    return matchedType?.bequestType?.toLowerCase().includes('proceeds of sale had') || false;
  }

  // Discretionary Category Lookup - opens popup to select categories
  onDiscretionaryLookup(bequestId: number) {
    const { bequest, isCharitable } = this.findBequestInAllArrays(bequestId);
    if (!bequest) return;

    this.currentDiscretionaryBequestId = bequestId;
    this.currentDiscretionaryCategoryIsCharitable = isCharitable;

    const bequestAny = bequest as any;
    if (bequestAny.selectedDiscretionaryActivities?.length > 0) {
      this.selectedSubActivities = [...bequestAny.selectedDiscretionaryActivities];
    } else {
      this.selectedSubActivities = [];
    }

    // Reset category dropdown and filtered activities
    this.selectedCategory = '';
    this.filteredSubActivities = [];
    
    // Initialize filtered activities list for new single-column view
    this.activitySearchTerm = '';
    this.filteredActivitiesList = [...this.allCharityCategories];

    this.isDiscretionaryCategoryPopupOpen = true;
  }

  closeDiscretionaryCategoryPopup() {
    this.isDiscretionaryCategoryPopupOpen = false;
    this.isCategoryDropdownOpen = false;
    this.currentDiscretionaryBequestId = null;
    this.selectedCategory = '';
    this.selectedSubActivities = [];
    this.filteredSubActivities = [];
    this.activitySearchTerm = '';
    this.filteredActivitiesList = [];
  }

  // Handle category dropdown change
  onCategoryChange(category: string) {
    this.selectedCategory = category;
    this.updateFilteredSubActivities();
  }

  // Update the filtered sub activities list based on selected category
  updateFilteredSubActivities() {
    if (!this.selectedCategory) {
      this.filteredSubActivities = [];
    } else {
      this.filteredSubActivities = this.allCharityCategories.filter(
        c => c.parent === this.selectedCategory
      );
    }
  }

  // Toggle sub activity selection
  toggleSubActivity(charityCategory: CharityCategory) {
    const index = this.selectedSubActivities.findIndex(
      s => s.id === charityCategory.id
    );
    if (index > -1) {
      // Activity is already selected, remove it
      this.selectedSubActivities.splice(index, 1);
    } else {
      // Activity is not selected, add it
      this.selectedSubActivities.push({
        id: charityCategory.id,
        category: charityCategory.parent,
        activity: charityCategory.child
      });
    }
  }

  // Remove selected activity (from chip click)
  removeSelectedActivity(item: SelectedDiscretionaryCategory) {
    const index = this.selectedSubActivities.findIndex(
      s => s.id === item.id
    );
    if (index > -1) {
      this.selectedSubActivities.splice(index, 1);
    }
  }

  // Check if activity is selected
  isSubActivitySelected(charityCategory: CharityCategory): boolean {
    return this.selectedSubActivities.some(
      s => s.id === charityCategory.id
    );
  }

  // New single-list methods
  filterActivities() {
    if (!this.activitySearchTerm.trim()) {
      this.filteredActivitiesList = [...this.allCharityCategories];
    } else {
      const searchLower = this.activitySearchTerm.toLowerCase();
      this.filteredActivitiesList = this.allCharityCategories.filter(
        a => `${a.parent} - ${a.child}`.toLowerCase().includes(searchLower)
      );
    }
  }

  isActivitySelected(activity: CharityCategory): boolean {
    return this.selectedSubActivities.some(s => s.id === activity.id);
  }

  toggleActivity(activity: CharityCategory) {
    const index = this.selectedSubActivities.findIndex(s => s.id === activity.id);
    if (index > -1) {
      this.selectedSubActivities.splice(index, 1);
    } else {
      this.selectedSubActivities.push({
        id: activity.id,
        category: activity.parent,
        activity: activity.child
      });
    }
  }

  applyDiscretionaryCategories() {
    if (this.currentDiscretionaryBequestId === null) {
      this.closeDiscretionaryCategoryPopup();
      return;
    }

    const bequest = this.findBequestById(this.currentDiscretionaryBequestId, this.currentDiscretionaryCategoryIsCharitable);
    if (bequest) {
      // Store the CharityCategory IDs for database persistence
      bequest.charityCategoryId = this.selectedSubActivities.map((s: SelectedDiscretionaryCategory) => s.id);
      // Store full objects for UI state (contains id, category, activity)
      bequest.selectedDiscretionaryActivities = [...this.selectedSubActivities];
      this.markAsChanged();
    }
    this.closeDiscretionaryCategoryPopup();
  }

  removeCategory(bequestId: number, category: string, isCharitable: boolean) {
    const bequest = this.findBequestById(bequestId, isCharitable);
    if (!bequest) return;

    // Find the activity being removed to get its ID
    const activityToRemove = bequest.selectedDiscretionaryActivities?.find(
      (s: SelectedDiscretionaryCategory) => `${s.category} - ${s.activity}` === category
    );

    if (bequest.selectedDiscretionaryActivities) {
      bequest.selectedDiscretionaryActivities = bequest.selectedDiscretionaryActivities.filter(
        (s: SelectedDiscretionaryCategory) => `${s.category} - ${s.activity}` !== category
      );
    }
    // Keep charityCategoryId in sync - remove the ID of the removed activity
    if (bequest.charityCategoryId && activityToRemove) {
      bequest.charityCategoryId = bequest.charityCategoryId.filter((id: number) => id !== activityToRemove.id);
    }
    this.markAsChanged();
  }

  // Named Category Lookup Methods
  onNamedLookup(bequestId: number) {
    const { bequest, isCharitable } = this.findBequestInAllArrays(bequestId);
    if (!bequest) return;

    this.currentNamedCategoryBequestId = bequestId;
    this.currentNamedCategoryIsCharitable = isCharitable;

    // Load existing selection
    this.selectedNamedActivityTemp = bequest.selectedNamedActivity ? { ...bequest.selectedNamedActivity } : null;

    // Initialize filtered list
    this.namedActivitySearchTerm = '';
    this.filteredNamedActivitiesList = [...this.allCharityCategories];

    this.isNamedCategoryPopupOpen = true;
  }

  closeNamedCategoryPopup() {
    this.isNamedCategoryPopupOpen = false;
    this.currentNamedCategoryBequestId = null;
    this.selectedNamedActivityTemp = null;
    this.namedActivitySearchTerm = '';
    this.filteredNamedActivitiesList = [];
  }

  onNamedCategoryOverlayMouseDown(event: MouseEvent) {
    this.handleOverlayMouseDown(event, 'category-popup-overlay', () => this.closeNamedCategoryPopup());
  }

  filterNamedActivities() {
    if (!this.namedActivitySearchTerm.trim()) {
      this.filteredNamedActivitiesList = [...this.allCharityCategories];
    } else {
      const searchLower = this.namedActivitySearchTerm.toLowerCase();
      this.filteredNamedActivitiesList = this.allCharityCategories.filter(
        a => `${a.parent} - ${a.child}`.toLowerCase().includes(searchLower)
      );
    }
  }

  selectNamedActivity(activity: CharityCategory) {
    // Single select - if already selected, deselect; otherwise select this one
    if (this.selectedNamedActivityTemp?.id === activity.id) {
      this.selectedNamedActivityTemp = null;
    } else {
      this.selectedNamedActivityTemp = {
        id: activity.id,
        category: activity.parent,
        activity: activity.child
      };
    }
  }

  isNamedActivitySelected(activity: CharityCategory): boolean {
    return this.selectedNamedActivityTemp?.id === activity.id;
  }

  applyNamedCategory() {
    if (this.currentNamedCategoryBequestId === null) {
      this.closeNamedCategoryPopup();
      return;
    }

    const bequest = this.findBequestById(this.currentNamedCategoryBequestId, this.currentNamedCategoryIsCharitable);
    if (bequest) {
      bequest.selectedNamedActivity = this.selectedNamedActivityTemp ? { ...this.selectedNamedActivityTemp } : null;
      // Set charityCategoryId for Named lookup (single ID in array)
      if (this.selectedNamedActivityTemp) {
        bequest.charityCategoryId = [this.selectedNamedActivityTemp.id];
      } else {
        bequest.charityCategoryId = null;
      }
      this.markAsChanged();
    }
    this.closeNamedCategoryPopup();
  }

  // Trust Category Lookup Methods (same logic as Named)
  onTrustCategoryLookup(bequestId: number, isCharitable: boolean = true) {
    const bequest = this.findBequestById(bequestId, isCharitable);
    if (!bequest) return;

    this.currentTrustCategoryBequestId = bequestId;
    this.currentTrustCategoryIsCharitable = isCharitable;

    // Load existing selection
    this.selectedTrustActivityTemp = bequest.selectedTrustActivity ? { ...bequest.selectedTrustActivity } : null;

    // Initialize filtered list
    this.trustActivitySearchTerm = '';
    this.filteredTrustActivitiesList = [...this.allCharityCategories];

    this.isTrustCategoryPopupOpen = true;
  }

  closeTrustCategoryPopup() {
    this.isTrustCategoryPopupOpen = false;
    this.currentTrustCategoryBequestId = null;
    this.selectedTrustActivityTemp = null;
    this.trustActivitySearchTerm = '';
    this.filteredTrustActivitiesList = [];
  }

  onTrustCategoryOverlayMouseDown(event: MouseEvent) {
    this.handleOverlayMouseDown(event, 'category-popup-overlay', () => this.closeTrustCategoryPopup());
  }

  filterTrustActivities() {
    if (!this.trustActivitySearchTerm.trim()) {
      this.filteredTrustActivitiesList = [...this.allCharityCategories];
    } else {
      const term = this.trustActivitySearchTerm.toLowerCase();
      this.filteredTrustActivitiesList = this.allCharityCategories.filter(
        activity => activity.parent.toLowerCase().includes(term) || activity.child.toLowerCase().includes(term)
      );
    }
  }

  selectTrustActivity(activity: CharityCategory) {
    // Single select - if already selected, deselect; otherwise select this one
    if (this.selectedTrustActivityTemp?.id === activity.id) {
      this.selectedTrustActivityTemp = null;
    } else {
      this.selectedTrustActivityTemp = {
        id: activity.id,
        category: activity.parent,
        activity: activity.child
      };
    }
  }

  isTrustActivitySelected(activity: CharityCategory): boolean {
    return this.selectedTrustActivityTemp?.id === activity.id;
  }

  applyTrustCategory() {
    if (this.currentTrustCategoryBequestId === null) {
      this.closeTrustCategoryPopup();
      return;
    }

    const bequest = this.findBequestById(this.currentTrustCategoryBequestId, this.currentTrustCategoryIsCharitable);
    if (bequest) {
      bequest.selectedTrustActivity = this.selectedTrustActivityTemp ? { ...this.selectedTrustActivityTemp } : null;
      // Set charityCategoryId for Trust lookup (single ID in array)
      if (this.selectedTrustActivityTemp) {
        bequest.charityCategoryId = [this.selectedTrustActivityTemp.id];
      } else {
        bequest.charityCategoryId = null;
      }
      this.markAsChanged();
    }
    this.closeTrustCategoryPopup();
  }

  // Check if a Trust category is selected for a bequest
  hasTrustCategory(bequestId: number): boolean {
    const bequest = this.findBequestById(bequestId, true);
    return !!bequest?.selectedTrustActivity;
  }

  onNotificationTypeChange(bequest: Bequest): void {
    if (bequest.notificationType === 'discretionary' || bequest.notificationType === 'trust') {
      bequest.beneficiaryId = null;
      bequest.organisationName = '';
      bequest.ccnLinked = '';
      bequest.addressLinked = '';
      bequest.linkedClientStatus = '';
      bequest.triggerHads = false;
    }
    this.markAsChanged();
  }

  isNonNamedServiceType(bequestId: number): boolean {
    const bequest = this.findBequestById(bequestId, true);
    return bequest?.notificationType === 'discretionary' || bequest?.notificationType === 'trust';
  }

  isBeneficiaryLinkingDisabled(bequestId: number): boolean {
    if (this.hasNamedCategory(bequestId)) return true;
    const bequest = this.findBequestById(bequestId, true);
    return bequest?.notificationType === 'discretionary' || bequest?.notificationType === 'trust';
  }

  isWillTrustBequestType(bequestSubType: string | BequestType | undefined): boolean {
    if (!bequestSubType) return false;
    const option = this.bequestTypeOptions.find(o => o.bequestTypeCode === bequestSubType);
    return option?.bequestType?.toLowerCase() === 'will trust';
  }

  // Clear Trust category selection for a bequest
  clearTrustCategory(bequestId: number): void {
    const bequest = this.findBequestById(bequestId, true);
    if (bequest) {
      bequest.selectedTrustActivity = null;
      bequest.charityCategoryId = null;
      this.markAsChanged();
    }
  }

  // Footer Popup Methods
  openFooterPopup(target: 'charitable' | 'nonCharitable') {
    this.currentFooterTarget = target;
    this.fetchFooterOptions();
    this.isFooterPopupOpen = true;
  }

  closeFooterPopup() {
    this.isFooterPopupOpen = false;
    // Reset selections
    this.allFooterOptions.forEach(opt => opt.selected = false);
  }

  fetchFooterOptions() {
    this.http.get<BequestsNotesTemplate[]>('/charitycentral/getbequestsnotestemplates')
      .subscribe({
        next: (data) => {
          if (data && data.length > 0) {
            this.allFooterOptions = data.map(item => ({
              id: item.id || item.id || 0,
              footer: item.text || item.text || '',
              selected: false
            }));
          } else {
            this.allFooterOptions = [];
          }
        },
        error: (err) => {
          console.error('Error fetching footer options:', err);
          this.allFooterOptions = [];
        }
      });
  }

  toggleFooterOption(option: { id: number; footer: string; selected: boolean }) {
    option.selected = !option.selected;
  }

  applyFooterSelections() {
    const selectedTexts = this.allFooterOptions
      .filter(opt => opt.selected)
      .map(opt => opt.footer);

    if (selectedTexts.length > 0) {
      const currentValue = this.currentFooterTarget === 'charitable' 
        ? this.charitableBequestNotes 
        : this.nonCharitableBequestNotes;
      
      const newValue = currentValue 
        ? currentValue + ' | ' + selectedTexts.join(' | ')
        : selectedTexts.join(' | ');
      
      if (this.currentFooterTarget === 'charitable') {
        this.charitableBequestNotes = newValue;
      } else {
        this.nonCharitableBequestNotes = newValue;
      }
      this.markAsChanged();
    }
    
    this.closeFooterPopup();
  }

  // Get the Will ID for the currently selected will (for notification PDF filtering)
  private getSelectedWillId(): number | null {
    if (!this.probate?.wills || !this.currentWillFileName) return null;
    const selectedWill = this.probate.wills.find(w => w.fileName === this.currentWillFileName);
    return selectedWill?.id ?? null;
  }

  // Numeric enum values for HAD-type bequests. Matched by value (not name) because the
  // frontend BequestType enum has a stale label for value 12 — the backend calls it
  // ProceedsOfSaleHAD, but the names line up the same on the wire because the values match.
  private static readonly HAD_BEQUEST_TYPE_VALUES = [7, 8, 9, 12];

  // Sentinel ID for the virtual Named_HAD option: no row exists in the ServiceType table.
  private static readonly NAMED_HAD_VIRTUAL_ID = -1;

  // Returns distinct service types present in the current will's charitable bequests.
  // Appends a virtual Named_HAD option when at least one HAD-type bequest is linked
  // to a client beneficiary whose subscription has ReportHads=1 (triggerHads).
  get previewServiceTypes(): { id: number; name: string }[] {
    if (!this.probate?.bequests || !this.availableServices.length) return [];
    const willId = this.getSelectedWillId();
    const namedService = this.availableServices.find(s => s.name.toLowerCase() === 'named');
    const namedId = namedService?.id;

    const relevant = this.probate.bequests.filter((b: any) => {
      if (!b.isCharitable) return false;
      if (willId) return b.will?.id === willId || b.will == null;
      return b.will?.final === true || b.will == null;
    });

    const distinctIds = [...new Set(relevant.map((b: any) => b.serviceTypeId ?? namedId).filter((id: any) => id != null))];
    const realServiceTypes = distinctIds
      .map((id: any) => this.availableServices.find(s => s.id === id))
      .filter((s: any): s is { id: number; name: string } => s != null);

    const hasNamedHadBequest = relevant.some((b: any) =>
      ReportingComponent.HAD_BEQUEST_TYPE_VALUES.includes(b.bequestType) &&
      b.triggerHads === true &&
      b.linkedClientStatus === 'Y');

    if (!hasNamedHadBequest) return realServiceTypes;

    // Order: Named (if present) → Named_HAD → Discretionary → Trust → others.
    const ordered: { id: number; name: string }[] = [];
    const named = realServiceTypes.find(s => s.name.toLowerCase() === 'named');
    if (named) ordered.push(named);
    ordered.push({ id: ReportingComponent.NAMED_HAD_VIRTUAL_ID, name: 'Named_HAD' });
    realServiceTypes
      .filter(s => s.name.toLowerCase() !== 'named')
      .forEach(s => ordered.push(s));
    return ordered;
  }

  selectedPreviewServiceType: { id: number; name: string } | null = null;

  compareServiceTypes(a: { id: number; name: string } | null, b: { id: number; name: string } | null): boolean {
    return a?.id === b?.id;
  }

  // Preview Notification PDF — opens the selected service type's PDF in a new tab
  previewNotification() {
    if (!this.probate?.probateId) {
      alert('No probate selected');
      return;
    }
    const willId = this.getSelectedWillId();
    const svc = this.selectedPreviewServiceType;

    let url = `/charitycentral/getpreviewnotification?ProbateId=${this.probate.probateId}&User=${this.loggedInUserTag}&download=false`;
    if (willId) {
      url += `&WillId=${willId}`;
    }
    if (svc) {
      if (svc.id === ReportingComponent.NAMED_HAD_VIRTUAL_ID) {
        // Named_HAD is virtual — no row in the ServiceType table. Send Named's real id as
        // ServiceTypeId so the backend can include Named-bucket bequests alongside the
        // HAD-qualifying ones; ServiceTypeName is what flags the request as Named_HAD.
        const namedRealId = this.availableServices.find(s => s.name.toLowerCase() === 'named')?.id;
        if (namedRealId != null) {
          url += `&ServiceTypeId=${namedRealId}`;
        }
        url += `&ServiceTypeName=${encodeURIComponent(svc.name)}`;
      } else {
        url += `&ServiceTypeId=${svc.id}&ServiceTypeName=${encodeURIComponent(svc.name)}`;
      }
    }
    window.open(url, '_blank');
    this.notificationPreviewed = true;
  }

  showAddBeneficiaryForm = false;
  alert = { text: '', type: '' };

  newBeneficiary: Partial<Beneficiary> = {
    beneficiarySourceURN: '',
    source: { id: 1, name: 'Manual Entry', code: 'MAN' },
    name: '',
    aliases: [],
    previousNames: [],
    addressLine1: '',
    addressLine2: '',
    addressLine3: '',
    addressLine4: '',
    addressLine5: '',
    postcode: '',
    telephone: '',
    email: '',
    web: '',
    active: true,
    activities: '',
    isClient: false
  };


  toggleAddBeneficiaryForm() {
    this.showAddBeneficiaryForm = !this.showAddBeneficiaryForm;
  }

  saveNewBeneficiary(newBeneficiary: Partial<Beneficiary>, form: NgForm) {
    let parameters = new HttpParams()
      .set('user', this.loggedInUserTag);

    // Normalize helper: ensure value is an array of strings
    const normalizeArray = (val: any): string[] => {
      if (!val) return [];
      if (Array.isArray(val)) return val.map(v => String(v));
      if (typeof val === 'string') return val.split(',').map(s => s.trim()).filter(Boolean);
      return [];
    };

    const beneficiaryPayload: any = {
      // copy fields from the bound model
      ...this.newBeneficiary,
      active: this.newBeneficiary.active ?? true,
      isClient: this.newBeneficiary.isClient ?? false,
      aliases: normalizeArray(this.newBeneficiary.aliases),
    };

    this.http.post('/charitycentral/AddBeneficiary', beneficiaryPayload, { params: parameters })
      .subscribe({
        next: (res: any) => {
          // Optionally close accordion
          this.showAddBeneficiaryForm = false;
          this.alert = { text: 'Beneficiary added successfully!', type: 'success' };
          //setTimeout(() => this.alert = { text: '', type: '' }, 3000); // hides after 3s

          // Refresh search using the name to show the newly added one
          this.beneficiarySearchQuery.beneficiaryName = this.newBeneficiary.name ?? '';
          this.beneficiarySearchQuery.beneficiaryURN = this.newBeneficiary.beneficiarySourceURN ?? '';
          this.onBeneficiarySearch();

          // resetting forms fields after successful save
          form.resetForm();
        },
        error: (err) => {
          this.alert = { text: 'Something went wrong.', type: 'error' };
          this.toggleAddBeneficiaryForm();
        }
      });
  }
}
